import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import ChatWindow from '../Chat/ChatWindow';
import RepairRequestPrintHub from './RepairRequestPrintHub';
import RepairRequestProcessStepper from './RepairRequestProcessStepper';
import WorkshopQrCode from './WorkshopQrCode';
import ReviewForm from '../Reviews/ReviewForm';
import StarRatingDisplay from '../common/StarRatingDisplay';
import { Alert, Badge, Button, Card, Input, Modal, Spinner } from '../common/UIComponents';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { repairRequestService } from '../../services/repairRequestService';
import type {
  ConfirmCashSettlementPayload,
  CancelRepairRequestByServicePayload,
  MarkRepairWorkCompletedPayload,
  SubmitFinalQuotePayload,
  SubmitPreliminaryEstimatePayload,
} from '../../services/repairRequestService';
import {
  PreliminaryEstimate,
  RepairRequest,
  RepairStatus,
  Review,
  type ServiceCancellationReasonCode,
  type FinalRepairQuoteLineItemType,
} from '../../types/firestore';
import {
  getRepairRequestOperationalIndexLabel,
  getRepairRequestOperationalIndexState,
} from '../../shared/repairRequestDisplay';
import {
  buildOrderCardState,
  getOrderCardNoActionStateMessage,
  getOrderCardRoleAwareCurrentStep,
  type OrderCardPanelId,
  type OrderCardRuntimeVariant,
  type OrderCardSignalTone,
} from '../../utils/orderCardRuntime';
import { buildRepairRequestOrderCardReadModel } from '../../utils/repairRequestReadModel';
import {
  formatRepairRequestFulfillmentAddress,
  getFulfillmentPricingHintLabel,
  getRepairRequestFulfillmentPaymentLabel,
  resolveRepairRequestFulfillmentSnapshot,
  type FulfillmentMode,
  type ResolvedRepairRequestFulfillmentSnapshot,
} from '../../shared/fulfillment';
import {
  getRepairStatusMatrix,
  getRepairRequestProcessProgress,
  getRepairRequestProcessSteps,
  type RepairRequestWorkflowAction,
  type RepairRequestWorkflowActor,
} from '../../utils/repairRequestWorkflow';
import {
  getRepairWorkflowV15StageDescription,
  resolveRepairWorkflowReadModel,
} from '../../utils/repairWorkflowV15';
import type { QuickReply } from '../../utils/chatQuickReplies';
import type { ButtonVariant } from '../common/UIComponents';
import { getFriendlyFirebaseRuntimeError } from '../../utils/firebaseRuntimeError';

interface RepairRequestOrderCardProps {
  requestId: string;
  request: RepairRequest;
  actor: RepairRequestWorkflowActor;
  currentUserId: string;
  counterpartLabel: string;
  sectionHint?: string | null;
  focusHint?: string | null;
  reviewRequested?: boolean;
  showReview: boolean;
  existingReview: Review | null;
  reviewLoading?: boolean;
  reviewSubmittedSuccessfully: boolean;
  onReviewAdded: (review: Review) => void;
  onSectionChange?: (section: string | null, mode?: 'push' | 'replace') => void;
  onBack?: () => void;
  backLabel?: string;
  variant?: OrderCardRuntimeVariant;
  loadingCounterpartLabel?: boolean;
  compactListMode?: boolean;
  disableInlineExpand?: boolean;
  isExpanded?: boolean;
  onExpand?: (row: OrderCardPanelId) => void;
  fullViewHref?: string | null;
  workspaceSimpleMode?: boolean;
  serviceDetailMode?: boolean;
  quickReplies?: QuickReply[];
}

type WorkshopSectionId =
  | 'action'
  | 'description'
  | 'messages'
  | 'repair'
  | 'quote'
  | 'photos'
  | 'settlement'
  | 'technical';

type ClientSectionId = 'messages' | 'settlement' | 'description' | 'repair' | 'review';

type WorkflowConfirmationIntent =
  | { kind: 'submit-preliminary-estimate' }
  | { kind: 'preliminary-decision'; decision: 'accept' | 'reject' }
  | { kind: 'confirm-fulfillment'; mode?: FulfillmentMode | null }
  | { kind: 'submit-final-quote' }
  | { kind: 'final-quote-decision'; decision: 'accept' | 'reject' }
  | { kind: 'workflow-action'; action: RepairRequestWorkflowAction };

const SERVICE_CANCELLATION_REASON_LABELS: Record<ServiceCancellationReasonCode, string> = {
  out_of_scope: 'Poza zakresem serwisu',
  no_parts: 'Brak części',
  not_economical: 'Naprawa nieopłacalna',
  capacity: 'Brak mocy przerobowych',
  other: 'Inny powód',
};

const SERVICE_CANCELLATION_REASON_OPTIONS = Object.entries(
  SERVICE_CANCELLATION_REASON_LABELS
) as Array<[ServiceCancellationReasonCode, string]>;

interface WorkflowConfirmationCopy {
  title: string;
  message: string;
  confirmLabel: string;
  variant?: ButtonVariant;
}

const ROW_ORDER: OrderCardPanelId[] = ['description', 'communication', 'settlement'];

const getDetailRowOrder = (actionPanel: OrderCardPanelId): OrderCardPanelId[] => {
  if (actionPanel === 'settlement') {
    return ['settlement', 'description', 'communication'];
  }

  if (actionPanel === 'communication') {
    return ['communication', 'description', 'settlement'];
  }

  return ['description', 'settlement', 'communication'];
};

const getDetailRowPlacementClass = (
  rowId: OrderCardPanelId,
  actionPanel: OrderCardPanelId
): string => {
  if (rowId === 'description') {
    return 'lg:col-start-2 lg:row-span-2 lg:row-start-1';
  }

  if (actionPanel === 'communication') {
    return rowId === 'communication'
      ? 'lg:col-start-1 lg:row-start-1'
      : 'lg:col-start-1 lg:row-start-2';
  }

  return rowId === 'settlement' ? 'lg:col-start-1 lg:row-start-1' : 'lg:col-start-1 lg:row-start-2';
};

const orderToneClasses: Record<OrderCardSignalTone, string> = {
  danger: 'border-status-error-500/35 bg-status-error-500/8',
  warning: 'border-status-warning-500/35 bg-status-warning-500/8',
  success: 'border-status-success-500/35 bg-status-success-500/8',
  neutral: 'border-primary-dark-300/75 bg-primary-dark-200/95',
};

const rowToneClasses: Record<OrderCardSignalTone, string> = {
  danger:
    'border-status-error-500/35 bg-status-error-500/10 text-status-error-500 hover:bg-status-error-500/15',
  warning:
    'border-status-warning-500/35 bg-status-warning-500/10 text-status-warning-500 hover:bg-status-warning-500/15',
  success:
    'border-status-success-500/35 bg-status-success-500/10 text-status-success-500 hover:bg-status-success-500/15',
  neutral:
    'border-primary-dark-300/70 bg-primary-dark-200/70 text-primary-light-300 hover:bg-primary-dark-200',
};

const SETTLEMENT_DETAIL_STATUSES = new Set<RepairStatus>([
  RepairStatus.REPAIR_COMPLETED,
  RepairStatus.AWAITING_CASH_SETTLEMENT,
  RepairStatus.PENDING_PAYMENT,
  RepairStatus.COMPLETED,
]);

type ClientCurrentStepLabel = 'TWOJA DECYZJA' | 'CO TERAZ' | 'AKTUALNY KROK' | 'ZAKOŃCZONE';

const getClientCurrentStepLabel = ({
  isTerminal,
  hasMutation,
  hasRequesterDirectedStep,
}: {
  isTerminal: boolean;
  hasMutation: boolean;
  hasRequesterDirectedStep: boolean;
}): ClientCurrentStepLabel => {
  if (isTerminal) {
    return 'ZAKOŃCZONE';
  }
  if (hasMutation) {
    return 'TWOJA DECYZJA';
  }
  if (hasRequesterDirectedStep) {
    return 'CO TERAZ';
  }
  return 'AKTUALNY KROK';
};

const getDecisionHistoryTitle = (status: RepairStatus, isWorkflowV15: boolean): string => {
  if (SETTLEMENT_DETAIL_STATUSES.has(status)) {
    return 'Rozliczenie i zakończenie';
  }
  return 'Proces i decyzje';
};

const getActionButtonVariant = (action: RepairRequestWorkflowAction): ButtonVariant => {
  if (action.isDestructive) return 'error';
  switch (action.nextStatus) {
    case RepairStatus.COMPLETED:
      return 'success';
    case RepairStatus.PENDING_PAYMENT:
      return 'accent';
    default:
      return 'primary';
  }
};

const getDestructiveConfirmationCopy = (
  action: RepairRequestWorkflowAction,
  actor: RepairRequestWorkflowActor
): {
  title: string;
  message: string;
  confirmLabel: string;
} => {
  if (action.nextStatus === RepairStatus.REJECTED) {
    return {
      title: actor === 'service' ? 'Odrzucić zlecenie?' : 'Odrzucić wycenę?',
      message:
        actor === 'service'
          ? 'Ta operacja zamknie sprawę jako odrzuconą. Właściciel zgłoszenia zobaczy informację o odrzuceniu.'
          : 'Ta operacja odrzuci wycenę i zamknie sprawę.',
      confirmLabel: 'Potwierdź odrzucenie',
    };
  }

  if (
    action.nextStatus === RepairStatus.CANCELLED_BY_SERVICE ||
    action.nextStatus === RepairStatus.CANCELLED_BY_CLIENT
  ) {
    return {
      title: 'Anulować realizację?',
      message:
        'Ta operacja zamknie bieżące zlecenie jako anulowane i nie będzie można jej cofnąć z poziomu tego widoku.',
      confirmLabel: 'Potwierdź anulowanie',
    };
  }

  return {
    title: 'Potwierdź akcję destrukcyjną',
    message: action.description || 'Ta operacja zmieni status sprawy i może być nieodwracalna.',
    confirmLabel: 'Potwierdź operację',
  };
};

const getWorkflowActionConfirmationCopy = (
  action: RepairRequestWorkflowAction,
  actor: RepairRequestWorkflowActor
): WorkflowConfirmationCopy => {
  if (action.id === 'service_start_v2_repair') {
    return {
      title: 'Rozpocząć naprawę?',
      message: 'Status zmieni się na naprawę w toku i klient zobaczy aktualizację sprawy.',
      confirmLabel: 'Rozpocznij naprawę',
    };
  }

  if (action.id === 'service_mark_v2_repair_completed') {
    return {
      title: 'Oznaczyć naprawę jako wykonaną?',
      message: 'Zapiszesz wykonanie prac i przejdziesz do przygotowania rozliczenia.',
      confirmLabel: 'Oznacz naprawę jako wykonaną',
    };
  }

  if (action.id === 'service_prepare_cash_settlement') {
    return {
      title: 'Przygotować rozliczenie?',
      message: 'Kwota zostanie oparta na zaakceptowanej finalnej wycenie.',
      confirmLabel: 'Przygotuj rozliczenie',
    };
  }

  if (action.id === 'service_confirm_cash_settlement') {
    return {
      title: 'Potwierdzić rozliczenie?',
      message: 'Potwierdzenie zamknie zlecenie jako zakończone i rozliczone.',
      confirmLabel: 'Potwierdź rozliczenie i zakończ',
      variant: 'success',
    };
  }

  if (action.id === 'service_cancel_v2_request') {
    return {
      title: 'Zamknąć sprawę bez naprawy?',
      message:
        'Klient zobaczy, że serwis nie podejmie tej naprawy. Podaj powód, żeby wiedział, co dalej.',
      confirmLabel: 'Zamknij sprawę',
      variant: 'error',
    };
  }

  if (action.isDestructive) {
    return {
      ...getDestructiveConfirmationCopy(action, actor),
      variant: 'error',
    };
  }

  return {
    title: 'Potwierdzić zmianę statusu?',
    message: action.description || 'Ta operacja zmieni status sprawy.',
    confirmLabel: action.label,
  };
};

const getFulfillmentConfirmationCopy = (
  mode?: FulfillmentMode | null
): WorkflowConfirmationCopy => {
  if (mode === 'servicePickup') {
    return {
      title: 'Potwierdzić odbiór sprzętu?',
      message: 'Potwierdź tylko wtedy, gdy sprzęt został odebrany od klienta.',
      confirmLabel: 'Potwierdź odbiór sprzętu',
    };
  }

  if (mode === 'onsiteService') {
    return {
      title: 'Potwierdzić rozpoczęcie wizyty?',
      message: 'Potwierdź tylko wtedy, gdy wizyta u klienta została rozpoczęta.',
      confirmLabel: 'Potwierdź rozpoczęcie wizyty',
    };
  }

  return {
    title: 'Potwierdzić przyjęcie sprzętu?',
    message: 'Potwierdź tylko wtedy, gdy sprzęt jest już po stronie serwisu.',
    confirmLabel: 'Potwierdź przyjęcie sprzętu',
  };
};

const getWorkflowConfirmationCopy = (
  intent: WorkflowConfirmationIntent,
  actor: RepairRequestWorkflowActor
): WorkflowConfirmationCopy => {
  switch (intent.kind) {
    case 'submit-preliminary-estimate':
      return {
        title: 'Wysłać wycenę wstępną?',
        message: 'Klient zobaczy wycenę wstępną i będzie musiał ją zaakceptować albo odrzucić.',
        confirmLabel: 'Wyślij wycenę wstępną',
      };
    case 'preliminary-decision':
      return intent.decision === 'accept'
        ? {
            title: 'Zaakceptować wycenę wstępną?',
            message: 'Serwis będzie mógł przejść do przyjęcia sprzętu i diagnozy.',
            confirmLabel: 'Akceptuję rozpoczęcie procesu',
          }
        : {
            title: 'Odrzucić wycenę wstępną?',
            message: 'Odrzucenie zamknie ten proces wyceny wstępnej.',
            confirmLabel: 'Odrzucam wycenę wstępną',
            variant: 'error',
          };
    case 'confirm-fulfillment':
      return getFulfillmentConfirmationCopy(intent.mode);
    case 'submit-final-quote':
      return {
        title: 'Wysłać finalną wycenę?',
        message: 'Klient zobaczy diagnozę i kosztorys finalnej naprawy do decyzji.',
        confirmLabel: 'Wyślij finalną wycenę',
      };
    case 'final-quote-decision':
      return intent.decision === 'accept'
        ? {
            title: 'Zaakceptować finalną wycenę?',
            message: 'Serwis będzie mógł rozpocząć naprawę zgodnie z kosztorysem.',
            confirmLabel: 'Akceptuję finalną wycenę',
          }
        : {
            title: 'Odrzucić finalną wycenę?',
            message: 'Odrzucenie zatrzyma naprawę w tym procesie.',
            confirmLabel: 'Odrzucam finalną wycenę',
            variant: 'error',
          };
    case 'workflow-action':
      return getWorkflowActionConfirmationCopy(intent.action, actor);
    default:
      return {
        title: 'Potwierdzić akcję?',
        message: 'Ta operacja zmieni status sprawy.',
        confirmLabel: 'Potwierdź',
      };
  }
};

const CONFIRMABLE_WORKFLOW_ACTION_IDS = new Set([
  'service_start_v2_repair',
  'service_mark_v2_repair_completed',
  'service_prepare_cash_settlement',
  'service_confirm_cash_settlement',
]);

const requiresWorkflowConfirmation = (action: RepairRequestWorkflowAction): boolean =>
  action.isDestructive === true || CONFIRMABLE_WORKFLOW_ACTION_IDS.has(action.id);

const getSectionForRow = (row: OrderCardPanelId): string => {
  if (row === 'communication') return 'messages';
  if (row === 'description') return 'description';
  return 'settlement';
};

const getRowForSectionHint = (sectionHint?: string | null): OrderCardPanelId | null => {
  if (
    sectionHint === 'messages' ||
    sectionHint === 'communication' ||
    sectionHint === 'contact' ||
    sectionHint === 'komunikacja' ||
    sectionHint === 'kontakt'
  ) {
    return 'communication';
  }
  if (sectionHint === 'description') return 'description';
  if (sectionHint === 'photos') return 'description';
  if (
    sectionHint === 'action' ||
    sectionHint === 'repair' ||
    sectionHint === 'quote' ||
    sectionHint === 'settlement' ||
    sectionHint === 'technical'
  ) {
    return 'settlement';
  }
  return null;
};

const getWorkshopSectionForRouteIntent = (
  sectionHint?: string | null,
  focusHint?: string | null
): WorkshopSectionId | null => {
  const normalizedFocus = String(focusHint || '')
    .trim()
    .toLowerCase();
  if (normalizedFocus === 'action' || normalizedFocus === 'actions' || normalizedFocus === 'cta') {
    return 'action';
  }

  switch (sectionHint) {
    case 'messages':
    case 'communication':
    case 'contact':
    case 'komunikacja':
    case 'kontakt':
      return 'messages';
    case 'description':
      return 'description';
    case 'repair':
      return 'repair';
    case 'quote':
      return 'quote';
    case 'photos':
      return 'photos';
    case 'settlement':
      return 'settlement';
    case 'review':
      return null;
    case 'technical':
      return 'technical';
    case 'action':
      return 'action';
    default:
      return null;
  }
};

const getWorkshopQuerySectionForId = (sectionId: Exclude<WorkshopSectionId, 'action'>): string => {
  return sectionId;
};

const getClientSectionForRouteIntent = (sectionHint?: string | null): ClientSectionId | null => {
  switch (sectionHint) {
    case 'messages':
    case 'communication':
    case 'contact':
    case 'komunikacja':
    case 'kontakt':
      return 'messages';
    case 'description':
    case 'photos':
      return 'description';
    case 'repair':
      return 'repair';
    case 'quote':
    case 'settlement':
      return 'settlement';
    case 'review':
      return 'review';
    default:
      return null;
  }
};

const getClientQuerySectionForId = (sectionId: ClientSectionId): string => {
  return sectionId;
};

const getClientPanelForSection = (sectionId: ClientSectionId): OrderCardPanelId => {
  if (sectionId === 'messages') return 'communication';
  if (sectionId === 'description') return 'description';
  return 'settlement';
};

const formatActionCountLabel = (count: number): string => {
  if (count <= 1) {
    return '1 akcja';
  }
  if (count < 5) {
    return `${count} akcje`;
  }
  return `${count} akcji`;
};

const formatCurrency = (value?: number | null): string =>
  typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
        minimumFractionDigits: 2,
      }).format(value)
    : 'Brak wyceny';

const formatOptionalCurrency = (value?: number | null): string | null =>
  typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
        minimumFractionDigits: 2,
      }).format(value)
    : null;

const formatPreliminaryRange = (estimate?: PreliminaryEstimate | null): string | null => {
  const min = formatOptionalCurrency(estimate?.priceRangeMin);
  const max = formatOptionalCurrency(estimate?.priceRangeMax);
  if (!min || !max) {
    return null;
  }
  return `${min} - ${max}`;
};

const PriorityRequestNotice: React.FC<{
  audience: 'service' | 'client';
  note?: string | null;
  dataTestId?: string;
}> = ({ audience, note, dataTestId }) => {
  const isServiceAudience = audience === 'service';

  return (
    <div
      data-testid={dataTestId}
      className={`rs-detail-note ${
        isServiceAudience ? 'rs-detail-note--warning' : 'rs-detail-note--neutral'
      }`}
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <Badge
          variant={isServiceAudience ? 'warning' : 'neutral'}
          size="sm"
          className="shrink-0 uppercase tracking-[0.05em]"
        >
          PILNE
        </Badge>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm leading-snug text-primary-light-100">
            {isServiceAudience
              ? 'Klient akceptuje możliwość wyższej wyceny.'
              : 'Zgłoszenie oznaczono jako pilne. Serwis może uwzględnić to w wycenie.'}
          </p>
          {note ? (
            <p
              className="min-w-0 break-words text-xs leading-snug text-primary-light-300 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden"
              title={note}
            >
              <span className="text-primary-light-400">
                {isServiceAudience ? 'Notatka klienta:' : 'Twoja notatka:'}
              </span>{' '}
              {note}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const getFinalQuoteLineItemTypeLabel = (type?: string): string => {
  if (type === 'parts') return 'Części';
  if (type === 'labor') return 'Robocizna';
  return 'Inne';
};

const getFinalQuoteLineItemTypeMetaLabel = (type?: string): string => {
  if (type === 'parts') return 'części';
  if (type === 'labor') return 'robocizna';
  return 'inne';
};

type FinalQuoteLineItemDraft = {
  type: FinalRepairQuoteLineItemType;
  label: string;
  amount: string;
  quantity: string;
};

const FINAL_QUOTE_DEFAULT_COMMENT = 'Kosztorys finalnej wyceny.';

const finalQuoteLineItemHasUserFacingQuantity = (type?: string): boolean => type === 'parts';

const createDefaultFinalQuoteLineItem = (): FinalQuoteLineItemDraft => ({
  type: 'labor',
  label: 'Robocizna',
  amount: '',
  quantity: '',
});

const createAdditionalFinalQuoteLineItem = (
  type: FinalRepairQuoteLineItemType = 'other'
): FinalQuoteLineItemDraft => ({
  type,
  label: '',
  amount: '',
  quantity: type === 'parts' ? '1' : '',
});

const normalizeFinalQuoteLineItemDraftForType = (
  item: FinalQuoteLineItemDraft,
  nextType: FinalRepairQuoteLineItemType
): FinalQuoteLineItemDraft => {
  if (nextType === 'labor') {
    return {
      ...item,
      type: nextType,
      label: 'Robocizna',
      quantity: '',
    };
  }

  const label = item.type === 'labor' || item.label.trim() === 'Robocizna' ? '' : item.label;

  return {
    ...item,
    type: nextType,
    label,
    quantity:
      finalQuoteLineItemHasUserFacingQuantity(nextType) && !item.quantity.trim()
        ? '1'
        : finalQuoteLineItemHasUserFacingQuantity(nextType)
          ? item.quantity
          : '',
  };
};

const getFinalQuoteLineItemDraftDisplayLabel = (item: FinalQuoteLineItemDraft): string => {
  if (item.type === 'labor') return 'Robocizna';
  return item.label.trim() || (item.type === 'parts' ? 'Nazwa części' : 'Nazwa kosztu');
};

const getFinalQuoteLineItemSummary = (item: FinalQuoteLineItemDraft): string => {
  const label = getFinalQuoteLineItemDraftDisplayLabel(item);
  if (item.type === 'labor') return label;
  const meta = getFinalQuoteLineItemTypeMetaLabel(item.type);
  const quantity =
    finalQuoteLineItemHasUserFacingQuantity(item.type) && item.quantity.trim()
      ? ` · ilość ${item.quantity.trim()}`
      : '';
  return `${label} · ${meta}${quantity}`;
};

const getFulfillmentModeContext = (mode: string | undefined): string | null => {
  if (mode === 'customerDropoff') {
    return 'Klient dostarcza sprzęt do serwisu.';
  }
  if (mode === 'servicePickup') {
    return 'Serwis odbiera sprzęt od klienta.';
  }
  if (mode === 'onsiteService') {
    return 'Naprawa lub diagnoza odbędzie się pod adresem klienta.';
  }
  return null;
};

const formatDate = (timestamp: any): string => {
  if (!timestamp || typeof timestamp.toDate !== 'function') return 'Brak danych';
  try {
    return timestamp.toDate().toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return 'Brak danych';
  }
};

const formatDateTime = (timestamp: any): string => {
  if (!timestamp || typeof timestamp.toDate !== 'function') return 'Brak danych';
  try {
    return timestamp.toDate().toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Brak danych';
  }
};

const getSettlementStageLabel = (status: RepairStatus): string => {
  switch (status) {
    case RepairStatus.NEW:
    case RepairStatus.PENDING_QUOTE:
      return 'Czeka na wycenę';
    case RepairStatus.QUOTED:
      return 'Czeka na decyzję';
    case RepairStatus.ACCEPTED:
      return 'Zatwierdzone do realizacji';
    case RepairStatus.IN_PROGRESS:
      return 'Naprawa w toku';
    case RepairStatus.PENDING_PAYMENT:
      return 'Oczekuje płatności';
    case RepairStatus.COMPLETED:
      return 'Do zamknięcia';
    case RepairStatus.CANCELLED_BY_CLIENT:
    case RepairStatus.CANCELLED_BY_SERVICE:
    case RepairStatus.REJECTED:
      return 'Sprawa zamknięta';
    case RepairStatus.PRELIMINARY_QUOTED:
      return 'Czeka na decyzję o wycenie wstępnej';
    case RepairStatus.AWAITING_FULFILLMENT:
      return 'Czeka na realizację przekazania';
    case RepairStatus.DIAGNOSIS_IN_PROGRESS:
      return 'Diagnoza w toku';
    case RepairStatus.FINAL_QUOTE_READY:
      return 'Czeka na decyzję o finalnej wycenie';
    case RepairStatus.FINAL_QUOTE_ACCEPTED:
      return 'Naprawa zaakceptowana';
    case RepairStatus.FINAL_QUOTE_REJECTED:
      return 'Finalna wycena odrzucona';
    case RepairStatus.PRELIMINARY_REJECTED:
      return 'Wycena wstępna odrzucona';
    default:
      return 'Etap roboczy';
  }
};

const getCashSettlementMethodLabel = (method?: string | null): string => {
  if (method === 'cash' || method === 'cash_or_direct') {
    return 'Gotówka / bezpośrednio';
  }
  return 'Brak zapisanego rozliczenia';
};

const getCashSettlementStatusLabel = (status?: string | null): string => {
  if (status === 'confirmed') {
    return 'Potwierdzone';
  }
  if (status === 'pending') {
    return 'Do potwierdzenia';
  }
  return 'Nieprzygotowane';
};

const getFinalQuoteStatusLabel = (status?: string | null): string => {
  if (status === 'accepted') {
    return 'Zaakceptowana';
  }
  if (status === 'rejected') {
    return 'Odrzucona';
  }
  return 'Czeka na decyzję';
};

const getWorkshopFinalQuoteLauncherLine = (
  finalQuoteStatusLabel: string,
  finalQuoteAmountLabel?: string | null,
  preliminaryEstimateSummary?: string | null
): string => {
  if (finalQuoteAmountLabel) {
    return `Finalna ${finalQuoteStatusLabel.toLowerCase()} · ${finalQuoteAmountLabel}`;
  }
  return preliminaryEstimateSummary || 'Brak finalnej wyceny';
};

const getWorkshopProblemPreview = (description?: string | null): string =>
  description?.trim() ? 'Problem zgłoszony przez klienta' : 'Brak opisu problemu';

const getHandoverEventLabel = (type?: string): string => {
  switch (type) {
    case 'customerDropoffReceived':
      return 'Przyjęcie sprzętu w serwisie';
    case 'servicePickupCompleted':
      return 'Odbiór od klienta';
    case 'onsiteVisitStarted':
      return 'Start wizyty u klienta';
    case 'onsiteVisitCompleted':
      return 'Wizyta u klienta zakończona';
    case 'releaseConfirmed':
      return 'Wydanie sprzętu potwierdzone';
    default:
      return 'Zdarzenie robocze';
  }
};

const parseOptionalAmountInput = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const normalized = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(normalized)) {
    return NaN;
  }
  return Number(normalized.toFixed(2));
};

type PreliminaryEstimateField =
  | 'diagnosisFee'
  | 'pickupOrTravelFee'
  | 'priceRangeMin'
  | 'priceRangeMax'
  | 'comment';

type PreliminaryEstimateFieldErrors = Partial<Record<PreliminaryEstimateField, string>>;

interface PreliminaryEstimateDraft {
  diagnosisFee: string;
  pickupOrTravelFee: string;
  priceRangeMin: string;
  priceRangeMax: string;
  comment: string;
}

interface PreliminaryEstimateValidationResult {
  errors: PreliminaryEstimateFieldErrors;
  payload: SubmitPreliminaryEstimatePayload | null;
}

const PRELIMINARY_ESTIMATE_FIELD_ORDER: PreliminaryEstimateField[] = [
  'diagnosisFee',
  'pickupOrTravelFee',
  'priceRangeMin',
  'priceRangeMax',
  'comment',
];

const PRELIMINARY_ESTIMATE_AMOUNT_FIELDS: Array<{
  field: Exclude<PreliminaryEstimateField, 'comment'>;
  label: string;
}> = [
  { field: 'diagnosisFee', label: 'Koszt diagnozy' },
  { field: 'pickupOrTravelFee', label: 'Koszt odbioru lub dojazdu' },
  { field: 'priceRangeMin', label: 'Dolna granica widełek' },
  { field: 'priceRangeMax', label: 'Górna granica widełek' },
];

const validatePreliminaryEstimateDraft = (
  draft: PreliminaryEstimateDraft
): PreliminaryEstimateValidationResult => {
  const parsedAmounts = {
    diagnosisFee: parseOptionalAmountInput(draft.diagnosisFee),
    pickupOrTravelFee: parseOptionalAmountInput(draft.pickupOrTravelFee),
    priceRangeMin: parseOptionalAmountInput(draft.priceRangeMin),
    priceRangeMax: parseOptionalAmountInput(draft.priceRangeMax),
  };
  const comment = draft.comment.trim();
  const errors: PreliminaryEstimateFieldErrors = {};

  PRELIMINARY_ESTIMATE_AMOUNT_FIELDS.forEach(({ field, label }) => {
    const value = parsedAmounts[field];
    if (Number.isNaN(value) || (value ?? 0) < 0) {
      errors[field] = `${label} musi być kwotą większą lub równą 0.`;
    }
  });

  const { priceRangeMin, priceRangeMax, diagnosisFee, pickupOrTravelFee } = parsedAmounts;
  const hasValidRangeValues = !errors.priceRangeMin && !errors.priceRangeMax;

  if (hasValidRangeValues && (priceRangeMin === undefined) !== (priceRangeMax === undefined)) {
    errors[priceRangeMin === undefined ? 'priceRangeMin' : 'priceRangeMax'] =
      'Podaj pełne widełki: minimum i maksimum.';
  } else if (
    hasValidRangeValues &&
    priceRangeMin !== undefined &&
    priceRangeMax !== undefined &&
    priceRangeMax < priceRangeMin
  ) {
    errors.priceRangeMax = 'Górna granica widełek musi być większa lub równa dolnej granicy.';
  }

  if (comment.length > 1000) {
    errors.comment = 'Komentarz do wyceny wstępnej może mieć maksymalnie 1000 znaków.';
  }

  const hasBusinessValue =
    diagnosisFee !== undefined ||
    pickupOrTravelFee !== undefined ||
    priceRangeMin !== undefined ||
    Boolean(comment);

  if (!hasBusinessValue && Object.keys(errors).length === 0) {
    errors.diagnosisFee = 'Podaj koszt, widełki ceny albo komentarz wyjaśniający brak kwoty.';
  }

  if (Object.keys(errors).length > 0) {
    return { errors, payload: null };
  }

  return {
    errors,
    payload: {
      ...(diagnosisFee !== undefined ? { diagnosisFee } : {}),
      ...(pickupOrTravelFee !== undefined ? { pickupOrTravelFee } : {}),
      ...(priceRangeMin !== undefined && priceRangeMax !== undefined
        ? { priceRangeMin, priceRangeMax }
        : {}),
      ...(comment ? { comment } : {}),
    },
  };
};

const getFulfillmentConfirmationCtaLabel = (
  mode: ResolvedRepairRequestFulfillmentSnapshot['selectedMode']
): string => {
  if (mode === 'servicePickup') {
    return 'Potwierdź odbiór sprzętu';
  }
  if (mode === 'onsiteService') {
    return 'Potwierdź rozpoczęcie wizyty';
  }
  return 'Potwierdź przyjęcie sprzętu';
};

const getFulfillmentHeaderPillLabel = (
  mode: ResolvedRepairRequestFulfillmentSnapshot['selectedMode']
): string | null => {
  if (mode === 'customerDropoff') return 'W serwisie';
  if (mode === 'servicePickup') return 'Odbiór';
  if (mode === 'onsiteService') return 'U klienta';
  return null;
};

const formatReviewDate = (value: any): string => {
  if (!value) return 'Brak daty';
  if (value instanceof Date) {
    return value.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  if (typeof value.toDate === 'function') {
    return value.toDate().toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  return 'Brak daty';
};

const formatOptionalCalendarDate = (value: any): string | null => {
  if (!value) {
    return null;
  }
  const formatted = formatReviewDate(value);
  return formatted === 'Brak daty' ? null : formatted;
};

const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';
const SECTION_SCROLL_MARGIN_TOP = 8;

type PanelRefMap<T> = Record<OrderCardPanelId, T | null>;

const createPanelRefMap = <T,>(): PanelRefMap<T> => ({
  description: null,
  communication: null,
  settlement: null,
});

interface PanelSyncRafState {
  measure: number;
  snap: number;
  finalize: number;
  ready: number;
}

const createPanelSyncRafState = (): PanelSyncRafState => ({
  measure: 0,
  snap: 0,
  finalize: 0,
  ready: 0,
});

const getScrollContainer = (element: HTMLElement | null): HTMLElement | Window => {
  const appScrollOwner = document.querySelector<HTMLElement>('[data-app-scroll-owner="true"]');
  if (appScrollOwner) {
    return appScrollOwner;
  }

  if (!element) {
    return window;
  }

  let parent: HTMLElement | null = element.parentElement;
  while (parent) {
    const styles = window.getComputedStyle(parent);
    const overflowY = styles.overflowY;
    const isScrollable = /(auto|scroll|overlay)/.test(overflowY);
    if (isScrollable && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return window;
};

const getScrollTop = (container: HTMLElement | Window): number => {
  if ('scrollTop' in container) {
    return container.scrollTop;
  }
  return window.scrollY || document.documentElement.scrollTop || 0;
};

const scrollToTop = (
  container: HTMLElement | Window,
  top: number,
  behavior: ScrollBehavior = 'smooth'
) => {
  if ('scrollTop' in container) {
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top, behavior });
    } else {
      container.scrollTop = top;
    }
    return;
  }
  window.scrollTo({ top, behavior });
};

const getAppChromeBottom = (): number => {
  const chromeElements = document.querySelectorAll<HTMLElement>(
    '[data-app-chrome="topbar"], header.navbar, [data-testid="service-workspace-header"]'
  );
  return Array.from(chromeElements).reduce((bottom, element) => {
    const rect = element.getBoundingClientRect();
    if (rect.height <= 0) {
      return bottom;
    }
    return Math.max(bottom, rect.bottom);
  }, 0);
};

const getNavbarHeight = (): number => {
  return getAppChromeBottom();
};

const getViewportBottom = (): number => {
  if (typeof window === 'undefined') {
    return 0;
  }
  if (window.visualViewport) {
    return window.visualViewport.offsetTop + window.visualViewport.height;
  }
  return window.innerHeight;
};

const isElementWithinVisibleViewport = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const viewportTop = window.visualViewport?.offsetTop || 0;
  const visibleTop = Math.max(viewportTop, getAppChromeBottom()) + SECTION_SCROLL_MARGIN_TOP;
  const visibleBottom = getViewportBottom() - SECTION_SCROLL_MARGIN_TOP;
  return rect.top >= visibleTop && rect.bottom <= visibleBottom;
};

const isAlreadyProcessedQuoteDecisionError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const normalized = error.message.toLowerCase();
  return (
    normalized.includes('decyzja właściciela zgłoszenia dla tej wyceny została już zapisana') ||
    normalized.includes('decyzja klienta dla tej wyceny została już zapisana') ||
    normalized.includes('decyzja klienta dla tej finalnej wyceny została już zapisana') ||
    normalized.includes('niedozwolone przejście statusu')
  );
};

const RepairRequestOrderCard: React.FC<RepairRequestOrderCardProps> = ({
  requestId,
  request,
  actor,
  currentUserId,
  counterpartLabel,
  sectionHint,
  focusHint,
  reviewRequested = false,
  showReview,
  existingReview,
  reviewLoading = false,
  reviewSubmittedSuccessfully,
  onReviewAdded,
  onSectionChange,
  onBack,
  backLabel = 'Wróć',
  variant = 'detail',
  loadingCounterpartLabel = false,
  compactListMode = false,
  disableInlineExpand = false,
  isExpanded = true,
  onExpand,
  fullViewHref,
  workspaceSimpleMode = false,
  serviceDetailMode = false,
  quickReplies = [],
}) => {
  const readModel = useMemo(() => buildRepairRequestOrderCardReadModel(request), [request]);
  const runtime = useMemo(
    () =>
      buildOrderCardState({
        request,
        actor,
        variant,
        sectionHint,
        counterpartLabel,
        showReview,
      }),
    [actor, counterpartLabel, request, sectionHint, showReview, variant]
  );
  const requestIdentity = readModel.requestId || requestId;
  const isWorkshopDetail = variant === 'detail' && serviceDetailMode && actor === 'service';
  const [activePanel, setActivePanel] = useState<OrderCardPanelId | null>(runtime.primaryPanel);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [workflowAwaitingSync, setWorkflowAwaitingSync] = useState(false);
  const [workflowPendingSourceStatus, setWorkflowPendingSourceStatus] =
    useState<RepairStatus | null>(null);
  const [clientQuoteDecisionLock, setClientQuoteDecisionLock] = useState<RepairStatus | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches
      : false
  );
  const [mobilePanelMaxHeight, setMobilePanelMaxHeight] = useState<number | null>(null);
  const [isCommunicationWorkspaceReady, setIsCommunicationWorkspaceReady] = useState(false);
  const [communicationEntryToken, setCommunicationEntryToken] = useState(0);
  const [activeChatWorkspaceHeight, setActiveChatWorkspaceHeight] = useState<number | null>(null);
  const [quotePrice, setQuotePrice] = useState(
    typeof request.quotedPrice === 'number' ? String(request.quotedPrice) : ''
  );
  const [quoteDescription, setQuoteDescription] = useState(request.quoteDescription || '');
  const [preliminaryDiagnosisFee, setPreliminaryDiagnosisFee] = useState('');
  const [preliminaryPickupOrTravelFee, setPreliminaryPickupOrTravelFee] = useState('');
  const [preliminaryPriceRangeMin, setPreliminaryPriceRangeMin] = useState('');
  const [preliminaryPriceRangeMax, setPreliminaryPriceRangeMax] = useState('');
  const [preliminaryComment, setPreliminaryComment] = useState('');
  const [preliminaryError, setPreliminaryError] = useState<string | null>(null);
  const [preliminaryFieldErrors, setPreliminaryFieldErrors] =
    useState<PreliminaryEstimateFieldErrors>({});
  const [preliminaryInvalidSubmitAttempt, setPreliminaryInvalidSubmitAttempt] = useState(0);
  const [preliminarySuccess, setPreliminarySuccess] = useState<string | null>(null);
  const [preliminaryPendingDecision, setPreliminaryPendingDecision] = useState<
    'accept' | 'reject' | null
  >(null);
  const [diagnosisDescription, setDiagnosisDescription] = useState('');
  const [finalQuoteDescription, setFinalQuoteDescription] = useState('');
  const [finalQuoteLineItems, setFinalQuoteLineItems] = useState<FinalQuoteLineItemDraft[]>([
    createDefaultFinalQuoteLineItem(),
  ]);
  const [activeFinalQuoteLineItemIndex, setActiveFinalQuoteLineItemIndex] = useState(0);
  const [finalQuoteRejectionReason, setFinalQuoteRejectionReason] = useState('');
  const [finalQuotePendingDecision, setFinalQuotePendingDecision] = useState<
    'accept' | 'reject' | null
  >(null);
  const [repairWorkSummary, setRepairWorkSummary] = useState('');
  const [cashSettlementNote, setCashSettlementNote] = useState('');
  const [serviceCancellationReasonCode, setServiceCancellationReasonCode] =
    useState<ServiceCancellationReasonCode>('out_of_scope');
  const [serviceCancellationReasonText, setServiceCancellationReasonText] = useState('');
  const [isMessageThreadReady, setIsMessageThreadReady] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [pendingConfirmation, setPendingConfirmation] = useState<WorkflowConfirmationIntent | null>(
    null
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [localWorkshopSection, setLocalWorkshopSection] = useState<WorkshopSectionId | null>(null);
  const [localClientSection, setLocalClientSection] = useState<ClientSectionId | null>(null);
  const normalizedFocusHint = String(focusHint || '')
    .trim()
    .toLowerCase();
  const routeIntentPanel = useMemo(
    () =>
      normalizedFocusHint === 'action' || normalizedFocusHint === 'actions'
        ? runtime.actionPanel
        : getRowForSectionHint(sectionHint),
    [normalizedFocusHint, runtime.actionPanel, sectionHint]
  );
  const requestIdentityKey = useMemo(
    () => `${String(request.id || '')}|${String(request.uid || '')}`,
    [request.id, request.uid]
  );
  const markAsReadKeysRef = useRef<Set<string>>(new Set());
  const pendingOpenRowRef = useRef<OrderCardPanelId | null>(null);
  const pendingSnapPanelRef = useRef<OrderCardPanelId | null>(null);
  const panelSyncRafRef = useRef<PanelSyncRafState>(createPanelSyncRafState());
  const lastInitializedRequestKeyRef = useRef<string | null>(null);
  const rowButtonRefs = useRef<PanelRefMap<HTMLButtonElement>>(createPanelRefMap());
  const workflowActionInFlightRef = useRef(false);
  const confirmationCancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const communicationOpenRef = useRef(false);
  const communicationViewportSettleTimeoutRef = useRef<number | null>(null);
  const activeChatWorkspaceRef = useRef<HTMLDivElement | null>(null);
  const activeChatToolbarRef = useRef<HTMLDivElement | null>(null);
  const sharedChatWasActiveRef = useRef(false);
  const preliminaryFieldRefs = useRef<
    Partial<Record<PreliminaryEstimateField, HTMLInputElement | HTMLTextAreaElement | null>>
  >({});
  const preliminaryFocusRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (preliminaryFocusRafRef.current !== null) {
      window.cancelAnimationFrame(preliminaryFocusRafRef.current);
      preliminaryFocusRafRef.current = null;
    }

    if (preliminaryInvalidSubmitAttempt === 0) {
      return;
    }

    const firstInvalidField = PRELIMINARY_ESTIMATE_FIELD_ORDER.find(
      (field) => preliminaryFieldErrors[field]
    );
    if (!firstInvalidField) {
      return;
    }

    preliminaryFocusRafRef.current = window.requestAnimationFrame(() => {
      preliminaryFocusRafRef.current = null;
      const field = preliminaryFieldRefs.current[firstInvalidField];
      if (!field || !field.isConnected) {
        return;
      }

      field.focus({ preventScroll: true });
      if (!isElementWithinVisibleViewport(field)) {
        field.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    });

    return () => {
      if (preliminaryFocusRafRef.current !== null) {
        window.cancelAnimationFrame(preliminaryFocusRafRef.current);
        preliminaryFocusRafRef.current = null;
      }
    };
  }, [preliminaryFieldErrors, preliminaryInvalidSubmitAttempt]);

  useEffect(() => {
    setQuotePrice(typeof request.quotedPrice === 'number' ? String(request.quotedPrice) : '');
    setQuoteDescription(request.quoteDescription || '');
    setPendingConfirmation(null);
    setActionError(null);
    setActionSuccess(null);
    setPendingActionId(null);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(null);
    setPreliminaryDiagnosisFee('');
    setPreliminaryPickupOrTravelFee('');
    setPreliminaryPriceRangeMin('');
    setPreliminaryPriceRangeMax('');
    setPreliminaryComment('');
    setPreliminaryError(null);
    setPreliminaryFieldErrors({});
    setPreliminaryInvalidSubmitAttempt(0);
    setPreliminarySuccess(null);
    setPreliminaryPendingDecision(null);
    setDiagnosisDescription('');
    setFinalQuoteDescription('');
    setFinalQuoteLineItems([createDefaultFinalQuoteLineItem()]);
    setActiveFinalQuoteLineItemIndex(0);
    setFinalQuoteRejectionReason('');
    setFinalQuotePendingDecision(null);
    setRepairWorkSummary('');
    setCashSettlementNote('');
    setServiceCancellationReasonCode('out_of_scope');
    setServiceCancellationReasonText('');
    workflowActionInFlightRef.current = false;
    setClientQuoteDecisionLock(null);
    // Reset all local workflow form state only when the canonical request identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestIdentityKey]);

  useEffect(() => {
    setQuotePrice(typeof request.quotedPrice === 'number' ? String(request.quotedPrice) : '');
    setQuoteDescription(request.quoteDescription || '');
    setPendingConfirmation(null);
    setActionError(null);
    setActionSuccess(null);
    setPreliminaryError(null);

    if (request.status !== RepairStatus.QUOTED) {
      setClientQuoteDecisionLock(null);
    }
    if (request.status !== RepairStatus.PRELIMINARY_QUOTED) {
      setPreliminaryPendingDecision(null);
    }
    if (request.status !== RepairStatus.FINAL_QUOTE_READY) {
      setFinalQuotePendingDecision(null);
    }
  }, [request.quoteDescription, request.quotedPrice, request.status]);

  useEffect(() => {
    if (!workflowAwaitingSync || workflowPendingSourceStatus === null) {
      return;
    }

    if (request.status === workflowPendingSourceStatus) {
      return;
    }

    workflowActionInFlightRef.current = false;
    setIsUpdating(false);
    setPendingActionId(null);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(null);
  }, [request.status, workflowAwaitingSync, workflowPendingSourceStatus]);

  useEffect(() => {
    if (!isExpanded || !pendingOpenRowRef.current) {
      return;
    }

    const requestedRow = pendingOpenRowRef.current;
    pendingOpenRowRef.current = null;
    setActivePanel(requestedRow);
    pendingSnapPanelRef.current = isMobileViewport ? requestedRow : null;
    lastInitializedRequestKeyRef.current = requestIdentityKey;
  }, [isExpanded, isMobileViewport, requestIdentityKey]);

  useEffect(() => {
    if (!isExpanded) {
      lastInitializedRequestKeyRef.current = null;
    }
  }, [isExpanded]);

  useEffect(() => {
    communicationOpenRef.current = false;
  }, [requestIdentityKey]);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    if (lastInitializedRequestKeyRef.current === requestIdentityKey) {
      return;
    }

    const nextPanel =
      isMobileViewport && routeIntentPanel ? routeIntentPanel : runtime.primaryPanel;
    setActivePanel(nextPanel);
    pendingSnapPanelRef.current = isMobileViewport && nextPanel ? nextPanel : null;
    lastInitializedRequestKeyRef.current = requestIdentityKey;
  }, [isExpanded, isMobileViewport, requestIdentityKey, routeIntentPanel, runtime.primaryPanel]);

  useEffect(() => {
    if (isWorkshopDetail || !isExpanded || !isMobileViewport || !routeIntentPanel) {
      return;
    }

    setActivePanel((currentPanel) =>
      currentPanel === routeIntentPanel ? currentPanel : routeIntentPanel
    );
    pendingSnapPanelRef.current = routeIntentPanel;
  }, [isExpanded, isMobileViewport, isWorkshopDetail, routeIntentPanel]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
      if (!event.matches) {
        setMobilePanelMaxHeight(null);
      }
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  const alignOpenPanelToViewport = useCallback(
    (panel: OrderCardPanelId, behavior: ScrollBehavior = 'auto') => {
      if (!isExpanded || !isMobileViewport) {
        return;
      }
      const rowButton = rowButtonRefs.current[panel];
      if (!rowButton) {
        return;
      }

      const scrollContainer = getScrollContainer(rowButton);
      const topOffset = getNavbarHeight() + SECTION_SCROLL_MARGIN_TOP;
      const deltaToTop = rowButton.getBoundingClientRect().top - topOffset;
      if (Math.abs(deltaToTop) < 6) {
        return;
      }

      const currentTop = getScrollTop(scrollContainer);
      const nextTop = Math.max(0, currentTop + deltaToTop);
      scrollToTop(scrollContainer, nextTop, behavior);
    },
    [isExpanded, isMobileViewport]
  );

  const calculateMobilePanelWorkspaceHeight = useCallback((): number | null => {
    if (!isExpanded || !isMobileViewport || !activePanel) {
      return null;
    }

    const rowButton = rowButtonRefs.current[activePanel];
    if (!rowButton) {
      return null;
    }

    const viewportBottom = getViewportBottom();
    const navbarHeight = getNavbarHeight();
    const rowRect = rowButton.getBoundingClientRect();
    const rowBottom = Math.max(rowRect.bottom, navbarHeight + SECTION_SCROLL_MARGIN_TOP);
    const available = Math.floor(viewportBottom - rowBottom - SECTION_SCROLL_MARGIN_TOP);
    return Math.max(1, available);
  }, [activePanel, isExpanded, isMobileViewport]);

  const recalculateMobilePanelHeight = useCallback(() => {
    const height = calculateMobilePanelWorkspaceHeight();
    setMobilePanelMaxHeight(height);
  }, [calculateMobilePanelWorkspaceHeight]);

  const cancelPanelSync = useCallback(() => {
    const rafState = panelSyncRafRef.current;
    if (rafState.measure) window.cancelAnimationFrame(rafState.measure);
    if (rafState.snap) window.cancelAnimationFrame(rafState.snap);
    if (rafState.finalize) window.cancelAnimationFrame(rafState.finalize);
    if (rafState.ready) window.cancelAnimationFrame(rafState.ready);
    panelSyncRafRef.current = createPanelSyncRafState();
  }, []);

  const schedulePanelActivationSync = useCallback(
    (
      panel: OrderCardPanelId,
      options: { behavior?: ScrollBehavior; resetCommunicationReady?: boolean } = {}
    ) => {
      if (isWorkshopDetail || !isExpanded || !isMobileViewport) {
        return;
      }

      const { behavior = 'auto', resetCommunicationReady = false } = options;
      if (resetCommunicationReady && panel === 'communication') {
        setIsCommunicationWorkspaceReady(false);
      } else if (panel !== 'communication') {
        setIsCommunicationWorkspaceReady(false);
      }

      cancelPanelSync();
      const rafState = panelSyncRafRef.current;

      rafState.measure = window.requestAnimationFrame(() => {
        recalculateMobilePanelHeight();
        rafState.snap = window.requestAnimationFrame(() => {
          if (pendingSnapPanelRef.current && pendingSnapPanelRef.current !== panel) {
            return;
          }

          alignOpenPanelToViewport(panel, behavior);
          pendingSnapPanelRef.current = null;

          rafState.finalize = window.requestAnimationFrame(() => {
            recalculateMobilePanelHeight();
            rafState.ready = window.requestAnimationFrame(() => {
              setIsCommunicationWorkspaceReady(panel === 'communication');
            });
          });
        });
      });
    },
    [
      alignOpenPanelToViewport,
      cancelPanelSync,
      isExpanded,
      isMobileViewport,
      isWorkshopDetail,
      recalculateMobilePanelHeight,
    ]
  );

  const resetExpandedCardViewport = useCallback(() => {
    if (!isExpanded) {
      return;
    }
    const scrollAnchor =
      rowButtonRefs.current.description ||
      rowButtonRefs.current.communication ||
      rowButtonRefs.current.settlement ||
      null;
    const scrollContainer = getScrollContainer(scrollAnchor);
    scrollToTop(scrollContainer, 0, 'auto');
    setMobilePanelMaxHeight(null);
    pendingSnapPanelRef.current = null;
  }, [isExpanded]);

  useEffect(() => {
    if (isWorkshopDetail || !isExpanded || !isMobileViewport || !activePanel) {
      cancelPanelSync();
      setMobilePanelMaxHeight(null);
      setIsCommunicationWorkspaceReady(false);
      return;
    }

    schedulePanelActivationSync(activePanel, {
      behavior: 'auto',
      resetCommunicationReady: true,
    });

    return cancelPanelSync;
  }, [
    activePanel,
    cancelPanelSync,
    isExpanded,
    isMobileViewport,
    isWorkshopDetail,
    schedulePanelActivationSync,
  ]);

  useEffect(() => {
    if (isWorkshopDetail || !isExpanded || !isMobileViewport || activePanel !== 'communication') {
      setIsCommunicationWorkspaceReady(false);
    }
  }, [activePanel, isExpanded, isMobileViewport, isWorkshopDetail]);

  useEffect(() => {
    const isCommunicationOpen = Boolean(
      isExpanded && isMobileViewport && variant === 'detail' && activePanel === 'communication'
    );

    if (isCommunicationOpen && !communicationOpenRef.current) {
      setCommunicationEntryToken((current) => current + 1);
    }

    communicationOpenRef.current = isCommunicationOpen;
  }, [activePanel, isExpanded, isMobileViewport, variant]);

  const syncCommunicationWorkspaceToViewport = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      if (
        !isExpanded ||
        !isMobileViewport ||
        activePanel !== 'communication' ||
        variant !== 'detail'
      ) {
        return;
      }

      schedulePanelActivationSync('communication', {
        behavior,
        resetCommunicationReady: false,
      });
    },
    [activePanel, isExpanded, isMobileViewport, schedulePanelActivationSync, variant]
  );

  useEffect(() => () => cancelPanelSync(), [cancelPanelSync]);

  useEffect(() => {
    if (!isExpanded || !isMobileViewport || !activePanel) {
      return;
    }

    const rowButton = rowButtonRefs.current[activePanel];
    const scrollContainer = getScrollContainer(rowButton || null);
    const visualViewport = window.visualViewport;
    const onResize = () => {
      if (variant === 'detail' && activePanel === 'communication') {
        syncCommunicationWorkspaceToViewport('auto');
        return;
      }
      recalculateMobilePanelHeight();
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    visualViewport?.addEventListener('resize', onResize);
    visualViewport?.addEventListener('scroll', onResize);
    if (scrollContainer !== window) {
      scrollContainer.addEventListener('scroll', onResize, { passive: true });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      visualViewport?.removeEventListener('resize', onResize);
      visualViewport?.removeEventListener('scroll', onResize);
      if (scrollContainer !== window) {
        scrollContainer.removeEventListener('scroll', onResize);
      }
    };
  }, [
    activePanel,
    isExpanded,
    isMobileViewport,
    recalculateMobilePanelHeight,
    syncCommunicationWorkspaceToViewport,
    variant,
  ]);

  const handleCommunicationComposerFocusChange = useCallback(
    (isFocused: boolean) => {
      if (communicationViewportSettleTimeoutRef.current !== null) {
        window.clearTimeout(communicationViewportSettleTimeoutRef.current);
        communicationViewportSettleTimeoutRef.current = null;
      }
      if (isFocused) {
        syncCommunicationWorkspaceToViewport('auto');
        return;
      }
      communicationViewportSettleTimeoutRef.current = window.setTimeout(() => {
        syncCommunicationWorkspaceToViewport('auto');
        communicationViewportSettleTimeoutRef.current = null;
      }, 120);
    },
    [syncCommunicationWorkspaceToViewport]
  );

  useEffect(
    () => () => {
      if (communicationViewportSettleTimeoutRef.current !== null) {
        window.clearTimeout(communicationViewportSettleTimeoutRef.current);
      }
    },
    []
  );

  const receiverId = useMemo(() => {
    if (actor === 'client') {
      return readModel.assignment.assignedServiceUserId;
    }
    return request.clientId || null;
  }, [actor, readModel.assignment.assignedServiceUserId, request.clientId]);

  const workflowV15ReadModel = useMemo(() => resolveRepairWorkflowReadModel(request), [request]);
  const workflowV15StageDescription = useMemo(
    () => getRepairWorkflowV15StageDescription(request),
    [request]
  );
  const preliminaryEstimate = request.preliminaryEstimate;
  const diagnosis = request.diagnosis;
  const finalQuote = request.finalQuote;
  const cashSettlement = request.cashSettlement;
  const isWorkflowV15 = workflowV15ReadModel.isV15;
  const canShowPreliminaryServiceForm =
    isWorkflowV15 &&
    actor === 'service' &&
    request.status === RepairStatus.NEW &&
    workflowV15ReadModel.nextStep.id === 'submit_preliminary_estimate';
  const canShowPreliminaryRequesterDecision =
    isWorkflowV15 &&
    actor === 'client' &&
    currentUserId === request.clientId &&
    request.status === RepairStatus.PRELIMINARY_QUOTED &&
    workflowV15ReadModel.nextStep.id === 'decide_preliminary_estimate';
  const shouldShowPreliminaryStageInfo =
    isWorkflowV15 &&
    ((request.status === RepairStatus.AWAITING_FULFILLMENT && actor !== 'service') ||
      request.status === RepairStatus.PRELIMINARY_REJECTED ||
      workflowV15ReadModel.stage === 'degraded');
  const canShowFinalQuoteServiceForm =
    isWorkflowV15 &&
    actor === 'service' &&
    currentUserId === request.serviceProviderId &&
    request.status === RepairStatus.DIAGNOSIS_IN_PROGRESS &&
    workflowV15ReadModel.nextStep.id === 'submit_final_quote';
  const canShowFinalQuoteRequesterDecision =
    isWorkflowV15 &&
    actor === 'client' &&
    currentUserId === request.clientId &&
    request.status === RepairStatus.FINAL_QUOTE_READY &&
    workflowV15ReadModel.nextStep.id === 'decide_final_quote';
  const canShowStartRepairWork =
    isWorkflowV15 &&
    actor === 'service' &&
    currentUserId === request.serviceProviderId &&
    request.status === RepairStatus.FINAL_QUOTE_ACCEPTED;
  const canShowRepairWorkCompletedForm =
    isWorkflowV15 &&
    actor === 'service' &&
    currentUserId === request.serviceProviderId &&
    request.status === RepairStatus.REPAIR_IN_PROGRESS;
  const canShowPrepareCashSettlement =
    isWorkflowV15 &&
    actor === 'service' &&
    currentUserId === request.serviceProviderId &&
    request.status === RepairStatus.REPAIR_COMPLETED;
  const canShowConfirmCashSettlement =
    isWorkflowV15 &&
    actor === 'service' &&
    currentUserId === request.serviceProviderId &&
    request.status === RepairStatus.AWAITING_CASH_SETTLEMENT;
  const shouldShowFinalQuoteReadOnly =
    isWorkflowV15 &&
    Boolean(finalQuote) &&
    (actor === 'service' ||
      request.status === RepairStatus.FINAL_QUOTE_ACCEPTED ||
      request.status === RepairStatus.FINAL_QUOTE_REJECTED);
  const shouldShowRepairStageInfo =
    isWorkflowV15 &&
    (request.status === RepairStatus.FINAL_QUOTE_ACCEPTED ||
      request.status === RepairStatus.REPAIR_IN_PROGRESS ||
      request.status === RepairStatus.REPAIR_COMPLETED ||
      request.status === RepairStatus.AWAITING_CASH_SETTLEMENT ||
      request.status === RepairStatus.COMPLETED);
  const preliminaryRangeLabel = formatPreliminaryRange(preliminaryEstimate);
  const preliminaryDisplayRows = [
    {
      label: 'Koszt diagnozy',
      value: formatOptionalCurrency(preliminaryEstimate?.diagnosisFee),
    },
    {
      label: 'Odbiór lub dojazd',
      value: formatOptionalCurrency(preliminaryEstimate?.pickupOrTravelFee),
    },
    {
      label: 'Widełki orientacyjne',
      value: preliminaryRangeLabel,
    },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));
  const preliminaryEstimateSummary =
    preliminaryRangeLabel ||
    formatOptionalCurrency(preliminaryEstimate?.diagnosisFee) ||
    formatOptionalCurrency(preliminaryEstimate?.pickupOrTravelFee) ||
    (preliminaryEstimate?.comment?.trim() ? 'Komentarz bez kwoty' : 'Brak wyceny wstępnej');
  const finalQuoteAmountLabel = formatOptionalCurrency(finalQuote?.amount);
  const finalQuoteLineItemsDisplay = Array.isArray(finalQuote?.lineItems)
    ? finalQuote?.lineItems?.filter((item) => item && typeof item.amount === 'number') || []
    : [];
  const diagnosisDescriptionText = diagnosis?.description?.trim() || null;
  const diagnosisCauseText = diagnosis?.cause?.trim() || null;
  const diagnosisDisplayRows = [
    { label: 'Diagnoza dla klienta', value: diagnosisDescriptionText },
    {
      label: 'Przyczyna',
      value:
        diagnosisCauseText && diagnosisCauseText !== diagnosisDescriptionText
          ? diagnosisCauseText
          : null,
    },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));

  const normalizedQuotePrice = Number(quotePrice.replace(',', '.'));
  const hasValidQuotePrice = Number.isFinite(normalizedQuotePrice) && normalizedQuotePrice > 0;
  const requiresQuoteInput =
    runtime.actionPanel === activePanel &&
    runtime.availableActions.all.some((action) => action.nextStatus === RepairStatus.QUOTED);
  const canSendQuote = !isUpdating && (!requiresQuoteInput || hasValidQuotePrice);
  const showCompactChatEmptyState =
    actor === 'service' && messageCount === 0 && runtime.communicationPriority === 'none';
  const reviewEligible = showReview && request.status === RepairStatus.COMPLETED;
  const reviewSubmitted = Boolean(existingReview) || reviewSubmittedSuccessfully;
  const canSubmitReview =
    reviewEligible && !reviewLoading && !existingReview && !reviewSubmittedSuccessfully;
  const isClientQuoteDecisionLocked =
    actor === 'client' &&
    request.status === RepairStatus.QUOTED &&
    (clientQuoteDecisionLock === RepairStatus.ACCEPTED ||
      clientQuoteDecisionLock === RepairStatus.REJECTED);
  const clientQuoteDecisionCopy =
    clientQuoteDecisionLock === RepairStatus.ACCEPTED
      ? {
          pending: 'Zapisywanie akceptacji wyceny...',
          synced: 'Decyzja wysłana. Czekamy na potwierdzenie statusu...',
        }
      : {
          pending: 'Zapisywanie odrzucenia wyceny...',
          synced: 'Decyzja wysłana. Czekamy na potwierdzenie statusu...',
        };
  const roleAwareCurrentStep = getOrderCardRoleAwareCurrentStep(request, actor);
  const noActionStateMessage = getOrderCardNoActionStateMessage(request, actor);
  const pendingReadbackMessage =
    actor === 'client'
      ? 'Decyzja wysłana. Czekamy na potwierdzenie statusu...'
      : 'Zmiana wysłana. Czekamy na potwierdzenie statusu...';
  const detailMode = variant === 'detail';
  const useServiceDetailLayout = detailMode;
  const clientRouteSection = getClientSectionForRouteIntent(sectionHint);
  const activeClientSection: ClientSectionId | 'overview' =
    clientRouteSection || localClientSection || 'overview';
  const isClientOverview = activeClientSection === 'overview';
  const detailDesktopAllSectionsVisible =
    useServiceDetailLayout && !isMobileViewport && actor !== 'client';

  const markSectionAsRead = useCallback(
    (section: 'messages' | 'quote' | 'status') => {
      if (!requestIdentity || !runtime.unreadSections.includes(section)) {
        return;
      }

      const role = actor === 'service' ? 'service' : 'client';
      const unreadKey = `${role}|${requestIdentity}|${section}|${runtime.unreadSections.join(',')}`;
      if (markAsReadKeysRef.current.has(unreadKey)) {
        return;
      }

      markAsReadKeysRef.current.add(unreadKey);
      const promise =
        actor === 'service'
          ? repairRequestService.markServiceActivityAsRead(requestIdentity, section)
          : repairRequestService.markClientActivityAsRead(requestIdentity, section);
      Promise.resolve(promise).catch((error) => {
        markAsReadKeysRef.current.delete(unreadKey);
        console.error('Nie udało się oznaczyć sekcji jako przeczytanej', error);
      });
    },
    [actor, requestIdentity, runtime.unreadSections]
  );

  useEffect(() => {
    const canConsumeSettlementUnread =
      detailDesktopAllSectionsVisible ||
      activePanel === runtime.actionPanel ||
      activePanel === 'settlement' ||
      (detailMode && actor === 'client' && activeClientSection === 'settlement');
    if (!isExpanded || !canConsumeSettlementUnread) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (runtime.unreadSections.includes('quote')) {
        markSectionAsRead('quote');
      }
      if (runtime.unreadSections.includes('status')) {
        markSectionAsRead('status');
      }
    }, 450);

    return () => window.clearTimeout(timer);
  }, [
    activePanel,
    activeClientSection,
    actor,
    detailDesktopAllSectionsVisible,
    detailMode,
    isExpanded,
    markSectionAsRead,
    runtime.actionPanel,
    runtime.unreadSections,
  ]);

  useEffect(() => {
    if (
      !isExpanded ||
      (!detailDesktopAllSectionsVisible &&
        activePanel !== 'communication' &&
        !(detailMode && actor === 'client' && activeClientSection === 'messages')) ||
      !isMessageThreadReady ||
      !requestIdentity
    ) {
      return;
    }

    markSectionAsRead('messages');
  }, [
    activePanel,
    activeClientSection,
    actor,
    detailDesktopAllSectionsVisible,
    detailMode,
    isExpanded,
    isMessageThreadReady,
    markSectionAsRead,
    requestIdentity,
  ]);

  const switchPanel = (panel: OrderCardPanelId) => {
    setPendingConfirmation(null);

    if (!isExpanded) {
      if (!disableInlineExpand) {
        pendingOpenRowRef.current = panel;
      }
      onExpand?.(panel);
      return;
    }

    if (activePanel === panel) {
      setActivePanel(null);
      pendingSnapPanelRef.current = null;
      onSectionChange?.(null);
      resetExpandedCardViewport();
      return;
    }

    setActivePanel(panel);
    pendingSnapPanelRef.current = isMobileViewport ? panel : null;
    onSectionChange?.(getSectionForRow(panel));
  };

  const focusPanel = (panel: OrderCardPanelId) => {
    setPendingConfirmation(null);

    if (!isExpanded) {
      if (!disableInlineExpand) {
        pendingOpenRowRef.current = panel;
      }
      onExpand?.(panel);
      return;
    }

    if (activePanel !== panel) {
      setActivePanel(panel);
    }
    pendingSnapPanelRef.current = isMobileViewport ? panel : null;
    onSectionChange?.(getSectionForRow(panel));
  };

  const runWorkflowAction = async (action: RepairRequestWorkflowAction) => {
    if (workflowActionInFlightRef.current) {
      return;
    }

    if (action.id === 'service_start_v2_repair') {
      await runServiceRepairCommand(action, () => repairRequestService.startRepairWork(requestId));
      return;
    }

    if (action.id === 'service_mark_v2_repair_completed') {
      const payload = buildRepairWorkCompletedPayload();
      if (!payload) {
        return;
      }
      await runServiceRepairCommand(action, () =>
        repairRequestService.markRepairWorkCompleted(requestId, payload)
      );
      return;
    }

    if (action.id === 'service_prepare_cash_settlement') {
      await runServiceRepairCommand(action, () =>
        repairRequestService.prepareCashSettlement(requestId)
      );
      return;
    }

    if (action.id === 'service_confirm_cash_settlement') {
      const payload = buildConfirmCashSettlementPayload();
      if (!payload) {
        return;
      }
      await runServiceRepairCommand(action, () =>
        repairRequestService.confirmCashSettlement(requestId, payload)
      );
      return;
    }

    if (action.id === 'service_cancel_v2_request') {
      const payload = buildServiceCancellationPayload();
      if (!payload) {
        return;
      }
      await runServiceRepairCommand(action, () =>
        repairRequestService.cancelRepairRequestByService(requestId, payload)
      );
      return;
    }

    const isClientQuoteDecisionAction =
      actor === 'client' &&
      request.status === RepairStatus.QUOTED &&
      (action.nextStatus === RepairStatus.ACCEPTED || action.nextStatus === RepairStatus.REJECTED);

    workflowActionInFlightRef.current = true;
    setIsUpdating(true);
    setActionError(null);
    setActionSuccess(null);
    setPendingActionId(action.id);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(request.status);
    if (isClientQuoteDecisionAction) {
      setClientQuoteDecisionLock(action.nextStatus);
    }

    let transitionSucceeded = false;
    try {
      await repairRequestService.transitionRequestStatus(requestId, action.nextStatus, actor, {
        quotedPrice: action.nextStatus === RepairStatus.QUOTED ? normalizedQuotePrice : undefined,
        quoteDescription: action.nextStatus === RepairStatus.QUOTED ? quoteDescription : undefined,
      });
      transitionSucceeded = true;
      setWorkflowAwaitingSync(true);
      if (isClientQuoteDecisionAction) {
        setActionSuccess(null);
      }
    } catch (error) {
      console.error('Nie udało się wykonać akcji workflow', error);
      if (isClientQuoteDecisionAction && isAlreadyProcessedQuoteDecisionError(error)) {
        setActionSuccess('Decyzja dla tej wyceny była już przetworzona. Aktualizujemy dane.');
        setActionError(null);
        try {
          const latestRequest = await repairRequestService.getRequestDetails(requestId);
          if (
            latestRequest.status === RepairStatus.ACCEPTED ||
            latestRequest.status === RepairStatus.REJECTED
          ) {
            setClientQuoteDecisionLock(latestRequest.status);
          }
        } catch (syncError) {
          console.error('Nie udało się dosynchronizować statusu po duplikacie decyzji', syncError);
        }
      } else {
        setActionError(
          getFriendlyFirebaseRuntimeError(
            error,
            error instanceof Error
              ? error.message
              : 'Nie udało się wykonać operacji na statusie zlecenia.'
          )
        );
        if (isClientQuoteDecisionAction) {
          setClientQuoteDecisionLock(null);
        }
        setWorkflowAwaitingSync(false);
        setWorkflowPendingSourceStatus(null);
        setIsUpdating(false);
        setPendingActionId(null);
      }
    } finally {
      workflowActionInFlightRef.current = false;
      if (!transitionSucceeded) {
        setIsUpdating(false);
        setPendingActionId(null);
      }
    }
  };

  const openWorkflowConfirmation = (intent: WorkflowConfirmationIntent) => {
    if (isUpdating || workflowActionInFlightRef.current || isClientQuoteDecisionLocked) {
      return;
    }

    if (intent.kind === 'submit-preliminary-estimate' && !buildPreliminaryEstimatePayload()) {
      return;
    }

    if (intent.kind === 'submit-final-quote') {
      try {
        if (!buildFinalQuotePayload()) {
          return;
        }
      } catch (error) {
        setPreliminaryError(
          error instanceof Error ? error.message : 'Nieprawidłowa finalna wycena.'
        );
        return;
      }
    }

    if (
      intent.kind === 'workflow-action' &&
      intent.action.id === 'service_mark_v2_repair_completed' &&
      !buildRepairWorkCompletedPayload()
    ) {
      return;
    }

    if (
      intent.kind === 'workflow-action' &&
      intent.action.id === 'service_confirm_cash_settlement' &&
      !buildConfirmCashSettlementPayload()
    ) {
      return;
    }

    if (intent.kind === 'workflow-action' && intent.action.id === 'service_cancel_v2_request') {
      setPreliminaryError(null);
    }

    setPendingConfirmation(intent);
  };

  const executeAction = (action: RepairRequestWorkflowAction) => {
    if (isUpdating || workflowActionInFlightRef.current || isClientQuoteDecisionLocked) {
      return;
    }

    if (requiresWorkflowConfirmation(action)) {
      openWorkflowConfirmation({ kind: 'workflow-action', action });
      return;
    }

    void runWorkflowAction(action);
  };

  const confirmPendingWorkflowIntent = () => {
    if (!pendingConfirmation || isUpdating || workflowActionInFlightRef.current) {
      return;
    }

    const intent = pendingConfirmation;
    if (
      intent.kind === 'workflow-action' &&
      intent.action.id === 'service_cancel_v2_request' &&
      !buildServiceCancellationPayload()
    ) {
      return;
    }
    setPendingConfirmation(null);

    if (intent.kind === 'submit-preliminary-estimate') {
      void submitPreliminaryEstimateConfirmed();
      return;
    }

    if (intent.kind === 'preliminary-decision') {
      void respondToPreliminaryEstimateConfirmed(intent.decision);
      return;
    }

    if (intent.kind === 'confirm-fulfillment') {
      void confirmFulfillmentConfirmed();
      return;
    }

    if (intent.kind === 'submit-final-quote') {
      void submitFinalQuoteConfirmed();
      return;
    }

    if (intent.kind === 'final-quote-decision') {
      void respondToFinalQuoteConfirmed(intent.decision);
      return;
    }

    void runWorkflowAction(intent.action);
  };

  const photos = request.photos || [];
  const identification = runtime.header.identification;
  const operationalIndexState = getRepairRequestOperationalIndexState(request);
  const operationalIndexLabel = getRepairRequestOperationalIndexLabel(request);
  const showOperationalTools = detailMode && serviceDetailMode && actor === 'service';
  const focusedAccordionMode = Boolean(
    !isWorkshopDetail && isExpanded && isMobileViewport && activePanel
  );
  const isFocusedCommunicationWorkspace =
    focusedAccordionMode && variant === 'detail' && activePanel === 'communication';
  const shouldLockFocusedCommunicationWorkspace =
    isFocusedCommunicationWorkspace && isCommunicationWorkspaceReady;
  const shouldShowBack = Boolean(onBack) && (detailMode || isExpanded);
  const statusLine = runtime.header.statusLabel;
  const userActionLine = runtime.summaryLine;
  const issueDescription =
    identification.problemDescription ||
    runtime.header.issueSummary ||
    'Brak opisu problemu od klienta.';
  const fulfillment = resolveRepairRequestFulfillmentSnapshot(request);
  const fulfillmentAddress = formatRepairRequestFulfillmentAddress(fulfillment.address);
  const fulfillmentModeContext = getFulfillmentModeContext(fulfillment.selectedMode);
  const fulfillmentHeaderPillLabel = getFulfillmentHeaderPillLabel(fulfillment.selectedMode);
  const canShowFulfillmentConfirmation =
    isWorkflowV15 &&
    actor === 'service' &&
    currentUserId === request.serviceProviderId &&
    request.status === RepairStatus.AWAITING_FULFILLMENT &&
    Boolean(fulfillment.selectedMode);
  const fulfillmentConfirmationCtaLabel = getFulfillmentConfirmationCtaLabel(
    fulfillment.selectedMode
  );
  const rawFulfillmentModeSnapshot = (request.fulfillmentSnapshot as any)
    ?.providerCapabilitiesSnapshot?.mode;
  const fulfillmentPricingHint =
    fulfillment.snapshot?.providerCapabilitiesSnapshot.mode.pricingHint ||
    (rawFulfillmentModeSnapshot?.pricingHint as undefined | string);
  const fulfillmentInstructions =
    typeof rawFulfillmentModeSnapshot?.instructions === 'string' &&
    rawFulfillmentModeSnapshot.instructions.trim()
      ? rawFulfillmentModeSnapshot.instructions.trim()
      : null;
  const fulfillmentPricingHintLabel =
    fulfillmentPricingHint === 'quote' ||
    fulfillmentPricingHint === 'included' ||
    fulfillmentPricingHint === 'not_configured'
      ? getFulfillmentPricingHintLabel(fulfillmentPricingHint)
      : null;
  const counterpartMeta = loadingCounterpartLabel
    ? 'Kontakt: ładowanie...'
    : runtime.header.counterpartLabel;
  const primaryAction = runtime.availableActions.primary;
  const canUseWorkflowActions =
    !isWorkflowV15 ||
    (actor === 'service' && currentUserId === request.serviceProviderId) ||
    (actor === 'client' && currentUserId === request.clientId);
  const primaryActionIsV2Only =
    primaryAction?.id === 'service_start_v2_repair' ||
    primaryAction?.id === 'service_mark_v2_repair_completed' ||
    primaryAction?.id === 'service_prepare_cash_settlement' ||
    primaryAction?.id === 'service_confirm_cash_settlement';
  const actionablePrimaryAction =
    canUseWorkflowActions && (!primaryActionIsV2Only || isWorkflowV15) ? primaryAction : null;
  const secondaryActions = canUseWorkflowActions ? runtime.availableActions.secondary : [];
  const destructiveActions = canUseWorkflowActions ? runtime.availableActions.destructive : [];
  const ownerEarlyCancellationAction =
    isWorkflowV15 && actor === 'client' && request.status === RepairStatus.NEW
      ? destructiveActions.find((action) => action.id === 'client_cancel_new') || null
      : null;
  const serviceCancellationAction =
    actor === 'service'
      ? destructiveActions.find((action) => action.id === 'service_cancel_v2_request') || null
      : null;
  const startRepairWorkAction =
    canShowStartRepairWork && canUseWorkflowActions
      ? runtime.availableActions.all.find((action) => action.id === 'service_start_v2_repair') ||
        null
      : null;
  const prepareCashSettlementAction =
    canShowPrepareCashSettlement && canUseWorkflowActions
      ? runtime.availableActions.all.find(
          (action) => action.id === 'service_prepare_cash_settlement'
        ) || null
      : null;
  const repairWorkCompletedAction =
    canShowRepairWorkCompletedForm && canUseWorkflowActions
      ? runtime.availableActions.all.find(
          (action) => action.id === 'service_mark_v2_repair_completed'
        ) || null
      : null;
  const confirmCashSettlementAction =
    canShowConfirmCashSettlement && canUseWorkflowActions
      ? runtime.availableActions.all.find(
          (action) => action.id === 'service_confirm_cash_settlement'
        ) || null
      : null;
  const compactHeaderBadges = runtime.headerBadges.filter((badge) =>
    workspaceSimpleMode
      ? badge.id === 'attention' || badge.id === 'status'
      : badge.id === 'status' || badge.id === 'attention'
  );
  const primaryHeaderBadge = workspaceSimpleMode ? compactHeaderBadges[0] : null;
  const headerMeta = workspaceSimpleMode
    ? [runtime.header.brandModelLabel, operationalIndexLabel].filter(Boolean).join(' • ')
    : [
        runtime.header.brandModelLabel,
        operationalIndexLabel,
        formatDate(request.createdAt),
        counterpartMeta,
      ]
        .filter(Boolean)
        .join(' • ');
  const compactSecondaryMeta = workspaceSimpleMode
    ? [counterpartMeta, `Utworzono: ${formatDate(request.createdAt)}`].filter(Boolean).join(' • ')
    : null;
  const detailSimpleMode = workspaceSimpleMode || useServiceDetailLayout;
  const cardPaddingClass = detailMode
    ? 'p-0'
    : compactListMode
      ? 'p-2 sm:p-2.5 md:p-3'
      : 'p-2.5 sm:p-3 md:p-3.5';
  const headerBlockClass = useServiceDetailLayout
    ? 'space-y-3'
    : workspaceSimpleMode
      ? compactListMode
        ? 'space-y-1.5'
        : 'space-y-2'
      : compactListMode
        ? 'space-y-0.5 rounded-lg border border-primary-dark-300/34 bg-primary-dark-200/24 p-1.5 sm:p-2'
        : 'space-y-1 rounded-lg border border-primary-dark-300/34 bg-primary-dark-200/24 p-2 sm:p-2.5';
  const rowButtonClass = compactListMode
    ? 'group flex min-h-[50px] w-full items-stretch justify-between gap-1.5 pl-2.5 pr-2 text-left transition-colors sm:min-h-[52px] sm:pl-3'
    : 'group flex min-h-[52px] w-full items-stretch justify-between gap-2 pl-2.5 pr-2 text-left transition-colors sm:pl-3';
  const rowDetailPaddingClass = compactListMode
    ? workspaceSimpleMode
      ? 'border-t border-primary-dark-300/35 bg-primary-dark-100/12 p-2.5 sm:p-3'
      : 'border-t border-primary-dark-300/55 bg-primary-dark-100/28 p-2.5 sm:p-3'
    : workspaceSimpleMode
      ? 'border-t border-primary-dark-300/35 bg-primary-dark-100/14 p-2.5 sm:p-3'
      : 'border-t border-primary-dark-300/70 bg-primary-dark-100/35 p-2.5 sm:p-3';
  const railContainerClass = compactListMode
    ? 'relative flex min-w-[52px] items-center justify-end gap-1 py-1.5 pl-1 pr-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors sm:min-w-[58px]'
    : 'relative flex min-w-[70px] items-center justify-end gap-1.5 py-2 pl-2 pr-1 text-[10px] font-semibold uppercase tracking-wide transition-colors';
  const nextStepToneClass =
    workspaceSimpleMode && runtime.showActionUrgency
      ? 'border-status-warning-500/35 bg-status-warning-500/10 text-status-warning-500'
      : useServiceDetailLayout && runtime.showActionUrgency
        ? 'border-status-warning-500/45 bg-status-warning-500/12 text-status-warning-500'
        : workspaceSimpleMode
          ? 'border-primary-dark-300/35 bg-primary-dark-200/22 text-primary-light-300'
          : 'border-primary-dark-300/55 bg-primary-dark-200/45 text-primary-light-300';
  const calmSectionClass = detailMode
    ? 'rs-detail-info-block'
    : detailSimpleMode
      ? 'border-t border-primary-dark-300/16 bg-transparent px-0 py-3'
      : 'rounded-lg border border-primary-dark-300/30 bg-primary-dark-100/18 p-3';
  const topDecisionNeedsPanel = Boolean(actionablePrimaryAction) && requiresQuoteInput;
  const topDecisionButtonLabel =
    canShowStartRepairWork || canShowRepairWorkCompletedForm || canShowConfirmCashSettlement
      ? null
      : actionablePrimaryAction
        ? topDecisionNeedsPanel
          ? activePanel === runtime.actionPanel
            ? null
            : runtime.actionPanel === 'description'
              ? 'Przejdź do opisu zlecenia'
              : 'Przejdź do wyceny'
          : pendingActionId === actionablePrimaryAction.id
            ? `${actionablePrimaryAction.label}...`
            : actionablePrimaryAction.label
        : runtime.primaryPanel === 'communication'
          ? activePanel === 'communication'
            ? null
            : 'Otwórz komunikację'
          : null;
  const topDecisionButtonVariant: ButtonVariant =
    actionablePrimaryAction && !topDecisionNeedsPanel
      ? getActionButtonVariant(actionablePrimaryAction)
      : 'neutral';
  const hasFreshServiceRequestActivity =
    actor === 'service' && readModel.unread.service.hasFreshRequestActivity;
  const shouldShowServiceChatUnreadCue =
    actor === 'service' && runtime.hasUnreadMessages && !hasFreshServiceRequestActivity;
  const serviceCommunicationLauncherLine = hasFreshServiceRequestActivity
    ? 'Nowe zgłoszenie'
    : runtime.hasUnreadMessages
      ? 'Nowe wiadomości'
      : 'Brak nowych wiadomości';
  const serviceCommunicationModuleHint = hasFreshServiceRequestActivity
    ? 'Nowe zgłoszenie czeka na pierwszą reakcję.'
    : runtime.hasUnreadMessages
      ? 'Są nowe wiadomości w tej sprawie.'
      : 'Robocza rozmowa z właścicielem zgłoszenia.';
  const preferredDateLabel = formatOptionalCalendarDate(request.scheduledDate);
  const topDecisionHelper =
    topDecisionNeedsPanel && actionablePrimaryAction
      ? 'Uzupełnij wymagane pola w aktywnym etapie.'
      : runtime.primaryPanel === 'communication' && runtime.hasUnreadMessages
        ? 'Masz nową wiadomość w tej sprawie.'
        : null;
  const topDecisionButtonDisabled =
    isUpdating ||
    workflowActionInFlightRef.current ||
    isClientQuoteDecisionLocked ||
    (actionablePrimaryAction?.nextStatus === RepairStatus.QUOTED &&
      !topDecisionNeedsPanel &&
      !canSendQuote);
  const serviceDetailMetaRows = [
    runtime.header.brandModelLabel,
    counterpartMeta,
    actor === 'service' ? operationalIndexState.value || 'bez indeksu' : null,
    `Utworzono: ${formatDate(request.createdAt)}`,
  ].filter(Boolean);
  const serviceDetailStatusBadgeVariant =
    runtime.headerBadges.find((badge) => badge.id === 'status')?.variant || 'neutral';
  const currentStatusMatrix = getRepairStatusMatrix(request.status);
  const processProgress = getRepairRequestProcessProgress(request.status);
  const processSteps = getRepairRequestProcessSteps();
  const currentProcessStep = processSteps[processProgress.currentStepIndex];
  const processStepLabel =
    request.status === RepairStatus.AWAITING_FULFILLMENT ||
    request.status === RepairStatus.FINAL_QUOTE_READY
      ? runtime.orientation.stageLabel
      : currentProcessStep?.label || runtime.header.stageLabel;
  const progressStripLabel = `Krok ${processProgress.currentStepIndex + 1} z ${
    processProgress.totalStepCount
  } · ${processStepLabel}`;
  const decisionHistoryTitle = getDecisionHistoryTitle(request.status, isWorkflowV15);
  const servicePlannerShortcutHref = (() => {
    if (
      !isWorkshopDetail ||
      actor !== 'service' ||
      currentStatusMatrix.isTerminal ||
      currentUserId !== readModel.assignment.assignedServiceUserId ||
      !requestIdentity
    ) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('planRequestId', requestIdentity);
    return `/app/service/planner?${params.toString()}`;
  })();
  const currentStepLabel =
    roleAwareCurrentStep?.label ||
    (actor === 'service'
      ? 'Następny krok'
      : runtime.showActionUrgency
        ? 'Twoja decyzja'
        : 'Aktualny etap');
  const currentStepLine =
    roleAwareCurrentStep?.description || workflowV15StageDescription || userActionLine;
  const hasClientRuntimeMutation =
    actor === 'client' &&
    (Boolean(actionablePrimaryAction) ||
      canShowPreliminaryRequesterDecision ||
      canShowFinalQuoteRequesterDecision);
  const hasRequesterDirectedStep =
    actor === 'client' &&
    (runtime.nextActionOwner === 'client' ||
      (isWorkflowV15 &&
        workflowV15ReadModel.nextStep.actor === 'requester' &&
        workflowV15ReadModel.nextStep.kind === 'action'));
  const fallbackClientCurrentStepLabel = getClientCurrentStepLabel({
    isTerminal: currentStatusMatrix.isTerminal,
    hasMutation: hasClientRuntimeMutation,
    hasRequesterDirectedStep,
  });
  const clientCurrentStepLabel =
    actor === 'client' && roleAwareCurrentStep
      ? roleAwareCurrentStep.label
      : fallbackClientCurrentStepLabel;
  const priorityRequest =
    request.priorityRequest?.requested === true ? request.priorityRequest : null;
  const priorityRequestNote = priorityRequest?.note?.trim() || null;
  const shouldRenderClientCurrentStepActionPanel =
    actor === 'client' &&
    (canShowPreliminaryRequesterDecision ||
      canShowFinalQuoteRequesterDecision ||
      topDecisionNeedsPanel);
  const clientContextRows = [
    { label: 'Typ sprzętu', value: identification.device.label, emphasize: true },
    { label: 'Marka i model', value: identification.device.brandModelLabel },
    { label: 'Preferowany termin', value: preferredDateLabel },
    { label: 'Załączniki', value: photos.length > 0 ? `${photos.length} zdjęć` : 'Brak zdjęć' },
  ].filter((row): row is { label: string; value: string; emphasize?: boolean } =>
    Boolean(row.value)
  );
  const clientServiceLabel = counterpartMeta.replace(/^Serwis:\s*/i, '');
  const clientIdentityLine = [runtime.header.brandModelLabel, clientServiceLabel]
    .filter(Boolean)
    .join(' · ');
  const clientDatesLine = [
    `Utworzono ${formatDate(request.createdAt)}`,
    request.updatedAt ? `Aktualizacja ${formatDate(request.updatedAt)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const clientCurrentStepLine =
    actor === 'client' && request.status === RepairStatus.COMPLETED
      ? 'Sprawa jest zamknięta.'
      : currentStepLine;
  const clientSettlementLauncherAmount = cashSettlement?.amount
    ? formatCurrency(cashSettlement.amount)
    : finalQuoteAmountLabel ||
      (isWorkflowV15 && preliminaryEstimate
        ? preliminaryEstimateSummary
        : !isWorkflowV15 && typeof request.quotedPrice === 'number'
          ? formatCurrency(request.quotedPrice)
          : null);
  const clientSettlementLauncherLine = clientSettlementLauncherAmount
    ? `${clientSettlementLauncherAmount} · ${
        cashSettlement?.method
          ? 'rozliczenie bezpośrednie'
          : finalQuote
            ? `wycena ${getFinalQuoteStatusLabel(finalQuote.status).toLowerCase()}`
            : 'rozliczenie i decyzje'
      }`
    : `Status: ${getSettlementStageLabel(request.status)}`;
  const clientContextLauncherLine = `${
    identification.device.brandModelLabel || identification.device.label
  } · problem i załączniki`;
  const clientRepairLauncherLine = `Krok ${processProgress.currentStepIndex + 1}/${
    processProgress.totalStepCount
  } · ${processStepLabel}`;
  const clientCommunicationLauncherLine = runtime.hasUnreadMessages
    ? 'Napisz do serwisu · nowe wiadomości'
    : 'Napisz do serwisu · brak nowych wiadomości';
  const serviceCancellationDisplayReasonLabel = request.serviceCancellation?.reasonCode
    ? SERVICE_CANCELLATION_REASON_LABELS[request.serviceCancellation.reasonCode] ||
      request.serviceCancellation.reasonCode
    : null;
  const serviceCancellationDisplayReasonText =
    request.serviceCancellation?.reasonText?.trim() || null;
  const clientSettlementModuleRows = [
    {
      label: 'Kwota',
      value: clientSettlementLauncherAmount || 'Brak zapisanej kwoty',
      emphasize: Boolean(clientSettlementLauncherAmount),
    },
    {
      label: 'Metoda',
      value: cashSettlement?.method
        ? getCashSettlementMethodLabel(cashSettlement.method)
        : getRepairRequestFulfillmentPaymentLabel(fulfillment.payment),
    },
    {
      label: 'Status',
      value: cashSettlement
        ? getCashSettlementStatusLabel(cashSettlement.status)
        : getSettlementStageLabel(request.status),
    },
    {
      label: 'Powód zamknięcia',
      value:
        request.status === RepairStatus.CANCELLED_BY_SERVICE
          ? [serviceCancellationDisplayReasonLabel, serviceCancellationDisplayReasonText]
              .filter(Boolean)
              .join(' · ')
          : null,
    },
  ];
  const shouldRenderReviewBlock =
    showReview &&
    (request.status === RepairStatus.COMPLETED ||
      Boolean(existingReview) ||
      reviewSubmittedSuccessfully);
  const shouldShowClientReviewCtaCard =
    actor === 'client' && reviewEligible && !reviewLoading && !reviewSubmitted;
  const pendingConfirmationCopy = pendingConfirmation
    ? getWorkflowConfirmationCopy(pendingConfirmation, actor)
    : null;
  const detailRowOrder = useServiceDetailLayout
    ? getDetailRowOrder(runtime.actionPanel)
    : ROW_ORDER;
  const workshopRouteSection = getWorkshopSectionForRouteIntent(sectionHint, normalizedFocusHint);
  const activeWorkshopSection: WorkshopSectionId | 'overview' =
    workshopRouteSection || localWorkshopSection || 'overview';
  const isWorkshopOverview = activeWorkshopSection === 'overview';
  const isWorkshopActionSection = activeWorkshopSection === 'action';
  const isSharedChatWorkspaceActive =
    detailMode &&
    ((actor === 'client' && activeClientSection === 'messages') ||
      (isWorkshopDetail && activeWorkshopSection === 'messages'));
  const shouldLockSharedChatWorkspace = isMobileViewport && isSharedChatWorkspaceActive;

  const alignActiveChatToolbarToViewport = useCallback(
    (mode: 'force' | 'if-obscured' = 'if-obscured') => {
      if (!isMobileViewport || !isSharedChatWorkspaceActive) {
        return false;
      }

      const toolbar = activeChatToolbarRef.current;
      if (!toolbar) {
        return false;
      }

      const scrollContainer = getScrollContainer(toolbar);
      const desiredToolbarTop = getAppChromeBottom() + SECTION_SCROLL_MARGIN_TOP;
      const currentToolbarTop = toolbar.getBoundingClientRect().top;
      const deltaToTop = currentToolbarTop - desiredToolbarTop;
      if (mode === 'if-obscured' && deltaToTop >= -2) {
        return false;
      }
      if (Math.abs(deltaToTop) < 2) {
        return false;
      }

      scrollToTop(scrollContainer, Math.max(0, getScrollTop(scrollContainer) + deltaToTop), 'auto');
      return true;
    },
    [isMobileViewport, isSharedChatWorkspaceActive]
  );

  const recalculateActiveChatWorkspaceHeight = useCallback(() => {
    if (!isMobileViewport || !isSharedChatWorkspaceActive) {
      setActiveChatWorkspaceHeight(null);
      return;
    }

    const workspace = activeChatWorkspaceRef.current;
    if (!workspace) {
      return;
    }

    alignActiveChatToolbarToViewport('if-obscured');
    const viewportBottom = getViewportBottom();
    const workspaceTop = workspace.getBoundingClientRect().top;
    const availableHeight = Math.floor(viewportBottom - workspaceTop - SECTION_SCROLL_MARGIN_TOP);
    if (!Number.isFinite(availableHeight)) {
      setActiveChatWorkspaceHeight(null);
      return;
    }
    setActiveChatWorkspaceHeight(Math.max(1, availableHeight));
  }, [alignActiveChatToolbarToViewport, isMobileViewport, isSharedChatWorkspaceActive]);

  useEffect(() => {
    if (!detailMode || actor !== 'client') {
      return;
    }

    if (clientRouteSection) {
      setLocalClientSection(clientRouteSection);
      return;
    }

    if (!sectionHint) {
      setLocalClientSection(null);
    }
  }, [actor, clientRouteSection, detailMode, sectionHint]);

  useEffect(() => {
    if (!isWorkshopDetail) {
      return;
    }

    if (workshopRouteSection) {
      setLocalWorkshopSection(workshopRouteSection);
      return;
    }

    if (!sectionHint && !normalizedFocusHint) {
      setLocalWorkshopSection(null);
    }
  }, [isWorkshopDetail, normalizedFocusHint, sectionHint, workshopRouteSection]);

  useEffect(() => {
    if (isWorkshopDetail) {
      setLocalWorkshopSection(null);
    }
  }, [isWorkshopDetail, requestIdentityKey]);

  useEffect(() => {
    if (detailMode && actor === 'client') {
      setLocalClientSection(null);
    }
  }, [actor, detailMode, requestIdentityKey]);

  useEffect(() => {
    if (!shouldLockSharedChatWorkspace || !communicationOpenRef.current) {
      if (shouldLockSharedChatWorkspace) {
        setCommunicationEntryToken((current) => current + 1);
      }
      communicationOpenRef.current = shouldLockSharedChatWorkspace;
      return;
    }

    communicationOpenRef.current = shouldLockSharedChatWorkspace;
  }, [shouldLockSharedChatWorkspace]);

  useEffect(() => {
    recalculateActiveChatWorkspaceHeight();
  }, [recalculateActiveChatWorkspaceHeight]);

  useEffect(() => {
    if (!isMobileViewport || !isSharedChatWorkspaceActive) {
      sharedChatWasActiveRef.current = false;
      return;
    }

    const onViewportChange = () => {
      recalculateActiveChatWorkspaceHeight();
    };

    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', onViewportChange);
    visualViewport?.addEventListener('scroll', onViewportChange);

    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('orientationchange', onViewportChange);
      visualViewport?.removeEventListener('resize', onViewportChange);
      visualViewport?.removeEventListener('scroll', onViewportChange);
    };
  }, [isMobileViewport, isSharedChatWorkspaceActive, recalculateActiveChatWorkspaceHeight]);

  useEffect(() => {
    const wasActive = sharedChatWasActiveRef.current;
    sharedChatWasActiveRef.current = isSharedChatWorkspaceActive;
    if (!isMobileViewport || !isSharedChatWorkspaceActive || wasActive) {
      return;
    }

    const firstFrame = window.requestAnimationFrame(() => {
      alignActiveChatToolbarToViewport('force');
      window.requestAnimationFrame(() => {
        recalculateActiveChatWorkspaceHeight();
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
    };
  }, [
    alignActiveChatToolbarToViewport,
    isMobileViewport,
    isSharedChatWorkspaceActive,
    recalculateActiveChatWorkspaceHeight,
  ]);

  const sharedChatWorkspaceStyle = isMobileViewport
    ? {
        height:
          activeChatWorkspaceHeight && activeChatWorkspaceHeight > 0
            ? `${activeChatWorkspaceHeight}px`
            : 'calc(var(--app-vh, 100dvh) - 11rem - env(safe-area-inset-bottom))',
      }
    : { height: 'clamp(420px, 68vh, 760px)' };

  const openWorkshopSection = (sectionId: Exclude<WorkshopSectionId, 'action'>) => {
    const navigationMode = isWorkshopOverview ? 'push' : 'replace';
    setLocalWorkshopSection(sectionId);
    onSectionChange?.(getWorkshopQuerySectionForId(sectionId), navigationMode);

    window.setTimeout(() => {
      const target = document.getElementById(`workshop-section-${sectionId}`);
      if (sectionId !== 'messages' && target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const openClientSection = (sectionId: ClientSectionId) => {
    const panel = getClientPanelForSection(sectionId);
    setLocalClientSection(sectionId);
    setActivePanel(panel);
    pendingSnapPanelRef.current = isMobileViewport ? panel : null;
    onSectionChange?.(getClientQuerySectionForId(sectionId), isClientOverview ? 'push' : 'replace');
  };

  const returnToClientOverview = () => {
    setLocalClientSection(null);
    setActivePanel(null);
    pendingSnapPanelRef.current = null;
    onSectionChange?.(null, 'replace');
  };

  const openWorkshopAction = () => {
    const navigationMode = isWorkshopOverview ? 'push' : 'replace';
    setLocalWorkshopSection('action');
    onSectionChange?.('action', navigationMode);

    window.setTimeout(() => {
      const target = document.getElementById('workshop-section-action');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const returnToWorkshopOverview = () => {
    setLocalWorkshopSection(null);
    onSectionChange?.(null, 'replace');

    window.setTimeout(() => {
      const target = document.getElementById('workshop-section-action');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const getPreliminaryEstimateDraft = (
    override?: Partial<PreliminaryEstimateDraft>
  ): PreliminaryEstimateDraft => ({
    diagnosisFee: preliminaryDiagnosisFee,
    pickupOrTravelFee: preliminaryPickupOrTravelFee,
    priceRangeMin: preliminaryPriceRangeMin,
    priceRangeMax: preliminaryPriceRangeMax,
    comment: preliminaryComment,
    ...override,
  });

  const handlePreliminaryFieldChange = (field: PreliminaryEstimateField, value: string) => {
    switch (field) {
      case 'diagnosisFee':
        setPreliminaryDiagnosisFee(value);
        break;
      case 'pickupOrTravelFee':
        setPreliminaryPickupOrTravelFee(value);
        break;
      case 'priceRangeMin':
        setPreliminaryPriceRangeMin(value);
        break;
      case 'priceRangeMax':
        setPreliminaryPriceRangeMax(value);
        break;
      case 'comment':
        setPreliminaryComment(value);
        break;
    }

    if (Object.keys(preliminaryFieldErrors).length > 0) {
      setPreliminaryFieldErrors(
        validatePreliminaryEstimateDraft(getPreliminaryEstimateDraft({ [field]: value })).errors
      );
    }
  };

  const buildPreliminaryEstimatePayload = (): SubmitPreliminaryEstimatePayload | null => {
    const result = validatePreliminaryEstimateDraft(getPreliminaryEstimateDraft());
    setPreliminaryFieldErrors(result.errors);

    if (!result.payload) {
      setPreliminaryError(null);
      setPreliminaryInvalidSubmitAttempt((attempt) => attempt + 1);
      return null;
    }

    return result.payload;
  };

  const submitPreliminaryEstimateConfirmed = async () => {
    if (isUpdating || workflowActionInFlightRef.current) {
      return;
    }

    const payload = buildPreliminaryEstimatePayload();
    if (!payload) {
      return;
    }

    workflowActionInFlightRef.current = true;
    setIsUpdating(true);
    setPreliminaryError(null);
    setPreliminarySuccess(null);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(request.status);

    try {
      await repairRequestService.submitPreliminaryEstimate(requestId, payload);
      setPreliminarySuccess(null);
      setWorkflowAwaitingSync(true);
    } catch (error) {
      console.error('Nie udało się wysłać wyceny wstępnej', error);
      setPreliminaryError(
        getFriendlyFirebaseRuntimeError(
          error,
          error instanceof Error ? error.message : 'Nie udało się wysłać wyceny wstępnej.'
        )
      );
      setWorkflowAwaitingSync(false);
      setWorkflowPendingSourceStatus(null);
      setIsUpdating(false);
    } finally {
      workflowActionInFlightRef.current = false;
    }
  };

  const respondToPreliminaryEstimateConfirmed = async (decision: 'accept' | 'reject') => {
    if (isUpdating || workflowActionInFlightRef.current || preliminaryPendingDecision) {
      return;
    }

    workflowActionInFlightRef.current = true;
    setIsUpdating(true);
    setPreliminaryPendingDecision(decision);
    setPreliminaryError(null);
    setPreliminarySuccess(null);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(request.status);

    try {
      await repairRequestService.respondToPreliminaryEstimate(requestId, decision);
      setPreliminarySuccess(null);
      setWorkflowAwaitingSync(true);
    } catch (error) {
      console.error('Nie udało się zapisać decyzji o wycenie wstępnej', error);
      setPreliminaryError(
        getFriendlyFirebaseRuntimeError(
          error,
          error instanceof Error
            ? error.message
            : 'Nie udało się zapisać decyzji o wycenie wstępnej.'
        )
      );
      setPreliminaryPendingDecision(null);
      setWorkflowAwaitingSync(false);
      setWorkflowPendingSourceStatus(null);
      setIsUpdating(false);
    } finally {
      workflowActionInFlightRef.current = false;
    }
  };

  const confirmFulfillmentConfirmed = async () => {
    if (isUpdating || workflowActionInFlightRef.current) {
      return;
    }

    workflowActionInFlightRef.current = true;
    setIsUpdating(true);
    setPreliminaryError(null);
    setPreliminarySuccess(null);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(request.status);

    try {
      await repairRequestService.confirmRepairRequestFulfillment(requestId);
      setPreliminarySuccess(null);
      setWorkflowAwaitingSync(true);
    } catch (error) {
      console.error('Nie udało się potwierdzić przekazania', error);
      setPreliminaryError(
        getFriendlyFirebaseRuntimeError(
          error,
          error instanceof Error ? error.message : 'Nie udało się potwierdzić przekazania.'
        )
      );
      setWorkflowAwaitingSync(false);
      setWorkflowPendingSourceStatus(null);
      setIsUpdating(false);
    } finally {
      workflowActionInFlightRef.current = false;
    }
  };

  const updateFinalQuoteLineItem = (
    index: number,
    patch: Partial<(typeof finalQuoteLineItems)[number]>
  ) => {
    setFinalQuoteLineItems((items) =>
      items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
    );
  };

  const updateFinalQuoteLineItemType = (index: number, nextType: FinalRepairQuoteLineItemType) => {
    setFinalQuoteLineItems((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index ? normalizeFinalQuoteLineItemDraftForType(item, nextType) : item
      )
    );
    setActiveFinalQuoteLineItemIndex(index);
  };

  const addFinalQuoteLineItem = (type: FinalRepairQuoteLineItemType = 'other') => {
    setFinalQuoteLineItems((items) => {
      const nextItems = [...items, createAdditionalFinalQuoteLineItem(type)];
      setActiveFinalQuoteLineItemIndex(nextItems.length - 1);
      return nextItems;
    });
  };

  const removeFinalQuoteLineItem = (index: number) => {
    setFinalQuoteLineItems((items) => {
      const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
      setActiveFinalQuoteLineItemIndex((currentIndex) =>
        nextItems.length === 0 ? 0 : Math.min(currentIndex, nextItems.length - 1)
      );
      return nextItems;
    });
  };

  const buildFinalQuotePayload = (): SubmitFinalQuotePayload | null => {
    const diagnosisText = diagnosisDescription.trim();
    const quoteDescription = finalQuoteDescription.trim();

    if (!diagnosisText) {
      setPreliminaryError('Diagnoza dla klienta jest wymagana.');
      return null;
    }

    const parsedLineItems = finalQuoteLineItems.map((item, index) => {
      const label = item.type === 'labor' ? 'Robocizna' : item.label.trim();
      const amount = parseOptionalAmountInput(item.amount);
      const quantity = finalQuoteLineItemHasUserFacingQuantity(item.type)
        ? parseOptionalAmountInput(item.quantity)
        : undefined;

      if (!label) {
        setActiveFinalQuoteLineItemIndex(index);
        throw new Error(`Nazwa pozycji #${index + 1} jest wymagana.`);
      }
      if (amount === undefined || Number.isNaN(amount) || amount <= 0) {
        setActiveFinalQuoteLineItemIndex(index);
        throw new Error(`Kwota pozycji #${index + 1} musi być większa od 0.`);
      }
      if (quantity !== undefined && (Number.isNaN(quantity) || quantity <= 0)) {
        setActiveFinalQuoteLineItemIndex(index);
        throw new Error(`Ilość w pozycji #${index + 1} musi być większa od 0.`);
      }

      return {
        type: item.type,
        label,
        amount,
        ...(quantity !== undefined ? { quantity } : {}),
      };
    });

    if (parsedLineItems.length === 0) {
      setPreliminaryError('Dodaj co najmniej jedną pozycję finalnej wyceny.');
      return null;
    }

    const amount = Number(parsedLineItems.reduce((sum, item) => sum + item.amount, 0).toFixed(2));

    return {
      diagnosis: {
        description: diagnosisText,
        cause: diagnosisText,
      },
      finalQuote: {
        amount,
        description: quoteDescription || FINAL_QUOTE_DEFAULT_COMMENT,
        lineItems: parsedLineItems,
      },
    };
  };

  const submitFinalQuoteConfirmed = async () => {
    if (isUpdating || workflowActionInFlightRef.current) {
      return;
    }

    let payload: SubmitFinalQuotePayload | null = null;
    try {
      payload = buildFinalQuotePayload();
    } catch (error) {
      setPreliminaryError(error instanceof Error ? error.message : 'Nieprawidłowa finalna wycena.');
      return;
    }

    if (!payload) {
      return;
    }

    workflowActionInFlightRef.current = true;
    setIsUpdating(true);
    setPreliminaryError(null);
    setPreliminarySuccess(null);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(request.status);

    try {
      await repairRequestService.submitRepairRequestFinalQuote(requestId, payload);
      setPreliminarySuccess(null);
      setWorkflowAwaitingSync(true);
    } catch (error) {
      console.error('Nie udało się wysłać finalnej wyceny', error);
      setPreliminaryError(
        getFriendlyFirebaseRuntimeError(
          error,
          error instanceof Error ? error.message : 'Nie udało się wysłać finalnej wyceny.'
        )
      );
      setWorkflowAwaitingSync(false);
      setWorkflowPendingSourceStatus(null);
      setIsUpdating(false);
    } finally {
      workflowActionInFlightRef.current = false;
    }
  };

  const respondToFinalQuoteConfirmed = async (decision: 'accept' | 'reject') => {
    if (isUpdating || workflowActionInFlightRef.current || finalQuotePendingDecision) {
      return;
    }

    workflowActionInFlightRef.current = true;
    setIsUpdating(true);
    setFinalQuotePendingDecision(decision);
    setPreliminaryError(null);
    setPreliminarySuccess(null);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(request.status);

    try {
      await repairRequestService.respondToRepairRequestFinalQuote(
        requestId,
        decision,
        finalQuoteRejectionReason
      );
      setPreliminarySuccess(null);
      setWorkflowAwaitingSync(true);
    } catch (error) {
      console.error('Nie udało się zapisać decyzji o finalnej wycenie', error);
      setPreliminaryError(
        getFriendlyFirebaseRuntimeError(
          error,
          error instanceof Error
            ? error.message
            : 'Nie udało się zapisać decyzji o finalnej wycenie.'
        )
      );
      setFinalQuotePendingDecision(null);
      setWorkflowAwaitingSync(false);
      setWorkflowPendingSourceStatus(null);
      setIsUpdating(false);
    } finally {
      workflowActionInFlightRef.current = false;
    }
  };

  const buildRepairWorkCompletedPayload = (): MarkRepairWorkCompletedPayload | null => {
    const workSummary = repairWorkSummary.trim();
    if (!workSummary) {
      setPreliminaryError('Podsumowanie wykonanej naprawy jest wymagane.');
      return null;
    }
    if (workSummary.length > 1600) {
      setPreliminaryError('Podsumowanie wykonanej naprawy może mieć maksymalnie 1600 znaków.');
      return null;
    }
    return {
      workSummary,
    };
  };

  const buildConfirmCashSettlementPayload = (): ConfirmCashSettlementPayload | null => {
    if (cashSettlementNote.trim().length > 1000) {
      setPreliminaryError('Komentarz do rozliczenia może mieć maksymalnie 1000 znaków.');
      return null;
    }

    return {
      ...(cashSettlementNote.trim() ? { settlementNote: cashSettlementNote.trim() } : {}),
    };
  };

  const buildServiceCancellationPayload = (): CancelRepairRequestByServicePayload | null => {
    const reasonText = serviceCancellationReasonText.trim();
    if (serviceCancellationReasonCode === 'other' && !reasonText) {
      setPreliminaryError('Podaj powód zamknięcia sprawy.');
      return null;
    }
    if (reasonText.length > 1000) {
      setPreliminaryError('Powód zamknięcia sprawy może mieć maksymalnie 1000 znaków.');
      return null;
    }
    return {
      reasonCode: serviceCancellationReasonCode,
      ...(reasonText ? { reasonText } : {}),
    };
  };

  const runServiceRepairCommand = async (
    action: RepairRequestWorkflowAction,
    command: () => Promise<void>
  ) => {
    if (isUpdating || workflowActionInFlightRef.current) {
      return;
    }

    workflowActionInFlightRef.current = true;
    setIsUpdating(true);
    setPreliminaryError(null);
    setPreliminarySuccess(null);
    setPendingActionId(action.id);
    setWorkflowAwaitingSync(false);
    setWorkflowPendingSourceStatus(request.status);

    try {
      await command();
      setPreliminarySuccess(null);
      setWorkflowAwaitingSync(true);
    } catch (error) {
      console.error('Nie udało się wykonać komendy naprawy', error);
      setPreliminaryError(
        getFriendlyFirebaseRuntimeError(
          error,
          error instanceof Error ? error.message : 'Nie udało się wykonać akcji naprawy.'
        )
      );
      setWorkflowAwaitingSync(false);
      setWorkflowPendingSourceStatus(null);
      setIsUpdating(false);
      setPendingActionId(null);
    } finally {
      workflowActionInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!shouldLockFocusedCommunicationWorkspace && !shouldLockSharedChatWorkspace) {
      return;
    }

    const appScrollOwner = document.querySelector<HTMLElement>('[data-app-scroll-owner="true"]');
    if (!appScrollOwner) {
      return;
    }

    const previousOverflowY = appScrollOwner.style.overflowY;
    const previousOverscrollBehavior = appScrollOwner.style.overscrollBehavior;

    appScrollOwner.style.overflowY = 'hidden';
    appScrollOwner.style.overscrollBehavior = 'none';

    return () => {
      appScrollOwner.style.overflowY = previousOverflowY;
      appScrollOwner.style.overscrollBehavior = previousOverscrollBehavior;
    };
  }, [shouldLockFocusedCommunicationWorkspace, shouldLockSharedChatWorkspace]);

  useEffect(() => {
    if (!pendingConfirmation || typeof document === 'undefined') {
      return;
    }

    const appScrollOwner = document.querySelector<HTMLElement>('[data-app-scroll-owner="true"]');
    const previousOwnerOverflowY = appScrollOwner?.style.overflowY || '';
    const previousOwnerOverscrollBehavior = appScrollOwner?.style.overscrollBehavior || '';
    const previousOwnerTouchAction = appScrollOwner?.style.touchAction || '';

    if (appScrollOwner) {
      appScrollOwner.style.overflowY = 'hidden';
      appScrollOwner.style.overscrollBehavior = 'none';
      appScrollOwner.style.touchAction = 'none';
    }

    return () => {
      if (appScrollOwner) {
        appScrollOwner.style.overflowY = previousOwnerOverflowY;
        appScrollOwner.style.overscrollBehavior = previousOwnerOverscrollBehavior;
        appScrollOwner.style.touchAction = previousOwnerTouchAction;
      }
    };
  }, [pendingConfirmation]);

  const renderPendingConfirmationSheet = () => {
    if (!pendingConfirmation || !pendingConfirmationCopy) {
      return null;
    }

    const isError = pendingConfirmationCopy.variant === 'error';
    const isServiceCancellationConfirmation =
      pendingConfirmation.kind === 'workflow-action' &&
      pendingConfirmation.action.id === 'service_cancel_v2_request';
    const confirmationError = isServiceCancellationConfirmation ? preliminaryError : null;
    const sheet = (
      <Modal
        isOpen
        onClose={() => setPendingConfirmation(null)}
        ariaLabel={pendingConfirmationCopy.title}
        dialogRole="alertdialog"
        initialFocusRef={confirmationCancelButtonRef}
        placement="bottom-responsive"
        overlayTestId="workflow-confirmation-overlay"
        dialogTestId="workflow-confirmation-dialog"
        overlayClassName="z-[140] bg-primary-dark-900/78 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
        className="!flex !max-h-[min(calc(100dvh-0.75rem-env(safe-area-inset-bottom)),88dvh)] !max-w-md !flex-col !overflow-hidden !rounded-t-2xl !border-primary-dark-300/24 !bg-primary-dark-100 !shadow-xl sm:!max-h-[calc(100dvh-2rem)] sm:!rounded-2xl"
      >
        <div className="overflow-y-auto px-4 pb-4 pt-4 sm:px-5">
          <div className="space-y-2">
            <p
              className={`text-base font-semibold leading-snug ${
                isError ? 'text-status-error-400' : 'text-primary-light-100'
              }`}
            >
              {pendingConfirmationCopy.title}
            </p>
            <p className="text-sm leading-relaxed text-primary-light-300">
              {pendingConfirmationCopy.message}
            </p>
          </div>
          {isServiceCancellationConfirmation && (
            <div className="mt-4 space-y-3">
              <label
                htmlFor={`service-cancellation-reason-${requestIdentity}`}
                className="block text-sm font-medium text-primary-light-200"
              >
                Powód
              </label>
              <select
                id={`service-cancellation-reason-${requestIdentity}`}
                value={serviceCancellationReasonCode}
                onChange={(event) => {
                  setServiceCancellationReasonCode(
                    event.target.value as ServiceCancellationReasonCode
                  );
                  setPreliminaryError(null);
                }}
                disabled={isUpdating}
                className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100/65 px-3 py-2 text-sm text-primary-light-100 focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {SERVICE_CANCELLATION_REASON_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <div>
                <label
                  htmlFor={`service-cancellation-text-${requestIdentity}`}
                  className="mb-1 block text-sm font-medium text-primary-light-200"
                >
                  Krótki opis dla klienta
                </label>
                <textarea
                  id={`service-cancellation-text-${requestIdentity}`}
                  value={serviceCancellationReasonText}
                  onChange={(event) => {
                    setServiceCancellationReasonText(event.target.value);
                    setPreliminaryError(null);
                  }}
                  rows={4}
                  maxLength={1000}
                  disabled={isUpdating}
                  className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100/65 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={
                    serviceCancellationReasonCode === 'other'
                      ? 'Wyjaśnij krótko, dlaczego serwis nie podejmie naprawy'
                      : 'Opcjonalnie doprecyzuj powód'
                  }
                />
              </div>
              {confirmationError && <Alert variant="error">{confirmationError}</Alert>}
            </div>
          )}
        </div>
        <div className="shrink-0 border-t border-primary-dark-300/16 bg-primary-dark-100/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:px-5">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[auto_1fr] sm:justify-end">
            <Button
              ref={confirmationCancelButtonRef}
              variant="neutral"
              size="sm"
              className="min-h-11 sm:order-1"
              onClick={() => setPendingConfirmation(null)}
              disabled={isUpdating}
            >
              Wróć
            </Button>
            <Button
              variant={pendingConfirmationCopy.variant || 'primary'}
              size="sm"
              className="min-h-11 sm:order-2"
              onClick={confirmPendingWorkflowIntent}
              disabled={isUpdating}
              title={
                pendingConfirmation.kind === 'workflow-action'
                  ? pendingConfirmation.action.description
                  : pendingConfirmationCopy.message
              }
            >
              {pendingConfirmationCopy.confirmLabel}
            </Button>
          </div>
        </div>
      </Modal>
    );

    if (typeof document === 'undefined') {
      return sheet;
    }

    return createPortal(sheet, document.body);
  };

  const renderActionsBlock = (row: OrderCardPanelId) => {
    if (runtime.actionPanel !== row) {
      return null;
    }

    const hideClientQuoteActions = isClientQuoteDecisionLocked && !isUpdating;
    const primaryActionOwnedByTopDecision =
      useServiceDetailLayout &&
      !isWorkshopDetail &&
      Boolean(actionablePrimaryAction) &&
      !topDecisionNeedsPanel;
    const primaryActionOwnedByStartRepairPanel =
      canShowStartRepairWork && actionablePrimaryAction?.id === 'service_start_v2_repair';
    const primaryActionOwnedByRepairCompletionPanel =
      canShowRepairWorkCompletedForm &&
      actionablePrimaryAction?.id === 'service_mark_v2_repair_completed';
    const primaryActionOwnedByCashSettlementPanel =
      canShowConfirmCashSettlement &&
      actionablePrimaryAction?.id === 'service_confirm_cash_settlement';
    const visiblePrimaryAction =
      hideClientQuoteActions ||
      primaryActionOwnedByTopDecision ||
      primaryActionOwnedByStartRepairPanel ||
      primaryActionOwnedByRepairCompletionPanel ||
      primaryActionOwnedByCashSettlementPanel
        ? null
        : actionablePrimaryAction;
    const visibleSecondaryActions = hideClientQuoteActions ? [] : secondaryActions;
    const visibleDestructiveActions = hideClientQuoteActions ? [] : destructiveActions;
    const shouldRenderFinalQuoteRuntimeBlock = !isWorkshopDetail && shouldShowFinalQuoteReadOnly;
    const shouldRenderStageRuntimeBlock = !isWorkshopDetail;
    const hasPreliminaryRuntimePanel =
      canShowPreliminaryServiceForm ||
      canShowPreliminaryRequesterDecision ||
      canShowFulfillmentConfirmation ||
      canShowFinalQuoteServiceForm ||
      canShowFinalQuoteRequesterDecision ||
      canShowStartRepairWork ||
      canShowRepairWorkCompletedForm ||
      canShowPrepareCashSettlement ||
      canShowConfirmCashSettlement ||
      shouldRenderFinalQuoteRuntimeBlock ||
      (shouldRenderStageRuntimeBlock &&
        (shouldShowRepairStageInfo || shouldShowPreliminaryStageInfo));
    const shouldShowNoActionInfo =
      !hideClientQuoteActions &&
      !primaryActionOwnedByTopDecision &&
      !primaryActionOwnedByStartRepairPanel &&
      !primaryActionOwnedByRepairCompletionPanel &&
      !primaryActionOwnedByCashSettlementPanel &&
      !hasPreliminaryRuntimePanel;
    const hasVisibleActions =
      Boolean(visiblePrimaryAction) ||
      visibleSecondaryActions.length > 0 ||
      visibleDestructiveActions.length > 0;

    return (
      <div className="space-y-3">
        {actionError && <Alert variant="error">{actionError}</Alert>}
        {workflowAwaitingSync && !isClientQuoteDecisionLocked && (
          <Alert variant="info">{pendingReadbackMessage}</Alert>
        )}
        {actionSuccess && !workflowAwaitingSync && !isClientQuoteDecisionLocked && (
          <Alert variant="success">{actionSuccess}</Alert>
        )}
        {isClientQuoteDecisionLocked && (
          <Alert variant="info">
            {isUpdating ? clientQuoteDecisionCopy.pending : clientQuoteDecisionCopy.synced}
          </Alert>
        )}
        {preliminaryError && (
          <div role="alert" data-testid="preliminary-estimate-backend-error">
            <Alert variant="error">{preliminaryError}</Alert>
          </div>
        )}
        {preliminarySuccess && !workflowAwaitingSync && (
          <Alert variant="success">{preliminarySuccess}</Alert>
        )}

        {canShowPreliminaryServiceForm && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 ring-1 ring-primary-dark-300/10 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">Szybka wycena</h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                Podaj koszt, widełki albo krótki komentarz. To nie jest finalna cena naprawy.
              </p>
            </div>

            {Object.keys(preliminaryFieldErrors).length > 0 && (
              <div
                role="alert"
                data-testid="preliminary-estimate-error-summary"
                className="rounded-lg border border-status-error-500/30 bg-status-error-500/10 px-3 py-2.5 text-sm font-medium leading-snug text-status-error-400"
              >
                Uzupełnij wymagane informacje przed wysłaniem wyceny.
              </div>
            )}

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div>
                <Input
                  id={`preliminary-diagnosis-fee-${requestIdentity}`}
                  ref={(node) => {
                    preliminaryFieldRefs.current.diagnosisFee = node;
                  }}
                  label="Koszt diagnozy"
                  type="number"
                  min={0}
                  step="0.01"
                  value={preliminaryDiagnosisFee}
                  onChange={(event) =>
                    handlePreliminaryFieldChange('diagnosisFee', event.target.value)
                  }
                  aria-invalid={preliminaryFieldErrors.diagnosisFee ? 'true' : undefined}
                  aria-describedby={
                    preliminaryFieldErrors.diagnosisFee
                      ? `preliminary-diagnosis-fee-error-${requestIdentity}`
                      : undefined
                  }
                  className={
                    preliminaryFieldErrors.diagnosisFee
                      ? 'border-status-error-500 focus:ring-status-error-500'
                      : ''
                  }
                  placeholder="np. 80"
                  disabled={isUpdating}
                />
                {preliminaryFieldErrors.diagnosisFee && (
                  <p
                    id={`preliminary-diagnosis-fee-error-${requestIdentity}`}
                    className="mt-1 break-words text-sm text-status-error-400"
                  >
                    {preliminaryFieldErrors.diagnosisFee}
                  </p>
                )}
              </div>
              <div>
                <Input
                  id={`preliminary-pickup-fee-${requestIdentity}`}
                  ref={(node) => {
                    preliminaryFieldRefs.current.pickupOrTravelFee = node;
                  }}
                  label="Odbiór / dojazd"
                  type="number"
                  min={0}
                  step="0.01"
                  value={preliminaryPickupOrTravelFee}
                  onChange={(event) =>
                    handlePreliminaryFieldChange('pickupOrTravelFee', event.target.value)
                  }
                  aria-invalid={preliminaryFieldErrors.pickupOrTravelFee ? 'true' : undefined}
                  aria-describedby={
                    preliminaryFieldErrors.pickupOrTravelFee
                      ? `preliminary-pickup-fee-error-${requestIdentity}`
                      : undefined
                  }
                  className={
                    preliminaryFieldErrors.pickupOrTravelFee
                      ? 'border-status-error-500 focus:ring-status-error-500'
                      : ''
                  }
                  placeholder="np. 40"
                  disabled={isUpdating}
                />
                {preliminaryFieldErrors.pickupOrTravelFee && (
                  <p
                    id={`preliminary-pickup-fee-error-${requestIdentity}`}
                    className="mt-1 break-words text-sm text-status-error-400"
                  >
                    {preliminaryFieldErrors.pickupOrTravelFee}
                  </p>
                )}
              </div>
              <div>
                <Input
                  id={`preliminary-range-min-${requestIdentity}`}
                  ref={(node) => {
                    preliminaryFieldRefs.current.priceRangeMin = node;
                  }}
                  label="Widełki od"
                  type="number"
                  min={0}
                  step="0.01"
                  value={preliminaryPriceRangeMin}
                  onChange={(event) =>
                    handlePreliminaryFieldChange('priceRangeMin', event.target.value)
                  }
                  aria-invalid={preliminaryFieldErrors.priceRangeMin ? 'true' : undefined}
                  aria-describedby={
                    preliminaryFieldErrors.priceRangeMin
                      ? `preliminary-range-min-error-${requestIdentity}`
                      : undefined
                  }
                  className={
                    preliminaryFieldErrors.priceRangeMin
                      ? 'border-status-error-500 focus:ring-status-error-500'
                      : ''
                  }
                  placeholder="np. 150"
                  disabled={isUpdating}
                />
                {preliminaryFieldErrors.priceRangeMin && (
                  <p
                    id={`preliminary-range-min-error-${requestIdentity}`}
                    className="mt-1 break-words text-sm text-status-error-400"
                  >
                    {preliminaryFieldErrors.priceRangeMin}
                  </p>
                )}
              </div>
              <div>
                <Input
                  id={`preliminary-range-max-${requestIdentity}`}
                  ref={(node) => {
                    preliminaryFieldRefs.current.priceRangeMax = node;
                  }}
                  label="Widełki do"
                  type="number"
                  min={0}
                  step="0.01"
                  value={preliminaryPriceRangeMax}
                  onChange={(event) =>
                    handlePreliminaryFieldChange('priceRangeMax', event.target.value)
                  }
                  aria-invalid={preliminaryFieldErrors.priceRangeMax ? 'true' : undefined}
                  aria-describedby={
                    preliminaryFieldErrors.priceRangeMax
                      ? `preliminary-range-max-error-${requestIdentity}`
                      : undefined
                  }
                  className={
                    preliminaryFieldErrors.priceRangeMax
                      ? 'border-status-error-500 focus:ring-status-error-500'
                      : ''
                  }
                  placeholder="np. 350"
                  disabled={isUpdating}
                />
                {preliminaryFieldErrors.priceRangeMax && (
                  <p
                    id={`preliminary-range-max-error-${requestIdentity}`}
                    className="mt-1 break-words text-sm text-status-error-400"
                  >
                    {preliminaryFieldErrors.priceRangeMax}
                  </p>
                )}
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label
                  htmlFor={`preliminary-comment-${requestIdentity}`}
                  className="text-sm font-medium text-primary-light-200"
                >
                  Komentarz / założenia
                </label>
                <span className="text-[11px] text-primary-light-400">
                  {preliminaryComment.trim().length}/1000
                </span>
              </div>
              <textarea
                id={`preliminary-comment-${requestIdentity}`}
                ref={(node) => {
                  preliminaryFieldRefs.current.comment = node;
                }}
                value={preliminaryComment}
                onChange={(event) => handlePreliminaryFieldChange('comment', event.target.value)}
                rows={4}
                maxLength={1100}
                disabled={isUpdating}
                aria-invalid={preliminaryFieldErrors.comment ? 'true' : undefined}
                aria-describedby={
                  preliminaryFieldErrors.comment
                    ? `preliminary-comment-error-${requestIdentity}`
                    : undefined
                }
                className={`w-full rounded-lg border bg-primary-dark-100/65 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                  preliminaryFieldErrors.comment
                    ? 'border-status-error-500 focus:ring-status-error-500'
                    : 'border-primary-dark-300 focus:ring-ratuj-300/70'
                }`}
                placeholder="Krótko opisz założenia wyceny wstępnej"
              />
              {preliminaryFieldErrors.comment && (
                <p
                  id={`preliminary-comment-error-${requestIdentity}`}
                  className="mt-1 break-words text-sm text-status-error-400"
                >
                  {preliminaryFieldErrors.comment}
                </p>
              )}
            </div>

            <Button
              variant="primary"
              size="sm"
              fullWidth
              className="min-h-[42px] text-sm"
              onClick={() => openWorkflowConfirmation({ kind: 'submit-preliminary-estimate' })}
              disabled={isUpdating || workflowActionInFlightRef.current}
            >
              {isUpdating ? 'Wysyłanie...' : 'Wyślij wycenę wstępną'}
            </Button>
          </div>
        )}

        {canShowPreliminaryRequesterDecision && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">Wycena wstępna</h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                Akceptacja oznacza zgodę na rozpoczęcie procesu. Finalna cena naprawy będzie osobnym
                etapem.
              </p>
            </div>

            {preliminaryDisplayRows.length > 0 && <DetailInfoList rows={preliminaryDisplayRows} />}

            <div className="rounded-lg border border-primary-dark-300/24 bg-primary-dark-100/16 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
                Komentarz serwisu
              </p>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-100">
                {preliminaryEstimate?.comment?.trim() || 'Brak komentarza serwisu.'}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
              <Button
                variant="primary"
                size="sm"
                className="min-h-[42px] text-sm"
                onClick={() =>
                  openWorkflowConfirmation({ kind: 'preliminary-decision', decision: 'accept' })
                }
                disabled={isUpdating || Boolean(preliminaryPendingDecision)}
              >
                {preliminaryPendingDecision === 'accept'
                  ? 'Zapisywanie...'
                  : 'Akceptuję rozpoczęcie procesu'}
              </Button>
              <Button
                variant="neutral"
                size="sm"
                className="min-h-[42px] border-status-error-500/34 bg-status-error-500/8 text-sm text-status-error-400 hover:bg-status-error-500/12"
                onClick={() =>
                  openWorkflowConfirmation({ kind: 'preliminary-decision', decision: 'reject' })
                }
                disabled={isUpdating || Boolean(preliminaryPendingDecision)}
              >
                {preliminaryPendingDecision === 'reject' ? 'Zapisywanie...' : 'Odrzucam'}
              </Button>
            </div>
          </div>
        )}

        {canShowFulfillmentConfirmation && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">
                Potwierdzenie przekazania
              </h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                {workflowV15StageDescription}
              </p>
            </div>

            <Button
              variant="primary"
              size="sm"
              fullWidth
              className="min-h-[42px] text-sm"
              onClick={() =>
                openWorkflowConfirmation({
                  kind: 'confirm-fulfillment',
                  mode: fulfillment.selectedMode,
                })
              }
              disabled={isUpdating || workflowActionInFlightRef.current}
            >
              {isUpdating ? 'Potwierdzanie...' : fulfillmentConfirmationCtaLabel}
            </Button>
          </div>
        )}

        {canShowFinalQuoteServiceForm && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">
                Diagnoza i finalna wycena
              </h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                Opisz diagnozę dla klienta i rozpisz kosztorys. Suma pozycji stanie się kwotą
                finalnej wyceny.
              </p>
            </div>

            <div>
              <label
                htmlFor={`diagnosis-description-${requestIdentity}`}
                className="mb-1 block text-sm font-medium text-primary-light-200"
              >
                Diagnoza dla klienta
              </label>
              <textarea
                id={`diagnosis-description-${requestIdentity}`}
                value={diagnosisDescription}
                onChange={(event) => setDiagnosisDescription(event.target.value)}
                rows={3}
                maxLength={1600}
                disabled={isUpdating}
                className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100/65 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Opisz krótko, co jest uszkodzone i co serwis proponuje zrobić."
              />
              <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
                Opisz krótko, co jest uszkodzone i co serwis proponuje zrobić.
              </p>
            </div>

            <div className="space-y-2" data-testid="final-quote-line-items">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-primary-light-100">Kosztorys</h4>
                <p className="text-sm font-semibold text-primary-light-100">
                  Razem:{' '}
                  {formatCurrency(
                    finalQuoteLineItems.reduce((sum, item) => {
                      const amount = parseOptionalAmountInput(item.amount);
                      return sum + (amount && amount > 0 ? amount : 0);
                    }, 0)
                  )}
                </p>
              </div>
              {finalQuoteLineItems.map((item, index) => {
                const isOpen = activeFinalQuoteLineItemIndex === index;
                const summaryAmount = parseOptionalAmountInput(item.amount);

                return (
                  <div
                    key={index}
                    className="rounded-lg border border-primary-dark-300/24 bg-primary-dark-100/14"
                  >
                    <button
                      type="button"
                      className="flex min-h-[46px] w-full items-center justify-between gap-3 px-3 py-2 text-left"
                      onClick={() => setActiveFinalQuoteLineItemIndex(index)}
                      disabled={isUpdating}
                    >
                      <span className="min-w-0 truncate text-sm font-medium text-primary-light-100">
                        {getFinalQuoteLineItemSummary(item)}
                      </span>
                      <span className="shrink-0 text-sm font-semibold text-primary-light-100">
                        {summaryAmount && summaryAmount > 0
                          ? formatCurrency(summaryAmount)
                          : '0,00 zł'}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="grid grid-cols-1 gap-2 border-t border-primary-dark-300/18 px-3 pb-3 pt-2 sm:grid-cols-[128px_1fr_120px_96px_auto]">
                        <select
                          value={item.type}
                          onChange={(event) =>
                            updateFinalQuoteLineItemType(
                              index,
                              event.target.value as FinalRepairQuoteLineItemType
                            )
                          }
                          disabled={isUpdating}
                          className="min-h-[40px] rounded-md border border-primary-dark-300 bg-primary-dark-100 px-2 text-sm text-primary-light-100"
                          aria-label={`Typ pozycji ${index + 1}`}
                        >
                          <option value="labor">Robocizna</option>
                          <option value="parts">Części</option>
                          <option value="other">Inne</option>
                        </select>
                        {item.type !== 'labor' ? (
                          <Input
                            id={`final-quote-line-label-${requestIdentity}-${index}`}
                            label={item.type === 'parts' ? 'Nazwa części' : 'Nazwa kosztu'}
                            value={item.label}
                            placeholder={
                              item.type === 'parts'
                                ? 'np. matryca, bateria, zasilacz'
                                : 'np. transport, dodatkowa usługa'
                            }
                            onChange={(event) =>
                              updateFinalQuoteLineItem(index, { label: event.target.value })
                            }
                            disabled={isUpdating}
                          />
                        ) : (
                          <div className="hidden sm:block" aria-hidden="true" />
                        )}
                        <Input
                          id={`final-quote-line-amount-${requestIdentity}-${index}`}
                          label="Kwota"
                          type="text"
                          inputMode="decimal"
                          value={item.amount}
                          onChange={(event) =>
                            updateFinalQuoteLineItem(index, { amount: event.target.value })
                          }
                          disabled={isUpdating}
                        />
                        {finalQuoteLineItemHasUserFacingQuantity(item.type) ? (
                          <Input
                            id={`final-quote-line-quantity-${requestIdentity}-${index}`}
                            label="Ilość"
                            type="text"
                            inputMode="decimal"
                            value={item.quantity}
                            onChange={(event) =>
                              updateFinalQuoteLineItem(index, { quantity: event.target.value })
                            }
                            disabled={isUpdating}
                          />
                        ) : (
                          <div className="hidden sm:block" aria-hidden="true" />
                        )}
                        {finalQuoteLineItems.length > 1 ? (
                          <Button
                            variant="neutral"
                            size="sm"
                            className="self-end"
                            onClick={() => removeFinalQuoteLineItem(index)}
                            disabled={isUpdating}
                          >
                            Usuń
                          </Button>
                        ) : (
                          <div className="hidden sm:block" aria-hidden="true" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="neutral"
                  size="sm"
                  onClick={() => addFinalQuoteLineItem('parts')}
                  disabled={isUpdating || finalQuoteLineItems.length >= 20}
                >
                  Dodaj część
                </Button>
                <Button
                  variant="neutral"
                  size="sm"
                  onClick={() => addFinalQuoteLineItem('other')}
                  disabled={isUpdating || finalQuoteLineItems.length >= 20}
                >
                  Dodaj inne
                </Button>
              </div>
            </div>

            <Input
              id={`final-quote-description-${requestIdentity}`}
              label="Komentarz do wyceny"
              value={finalQuoteDescription}
              onChange={(event) => setFinalQuoteDescription(event.target.value)}
              disabled={isUpdating}
              placeholder="Opcjonalnie"
            />

            <Button
              variant="primary"
              size="sm"
              fullWidth
              className="min-h-[42px] text-sm"
              onClick={() => openWorkflowConfirmation({ kind: 'submit-final-quote' })}
              disabled={isUpdating || workflowActionInFlightRef.current}
            >
              {isUpdating ? 'Wysyłanie...' : 'Wyślij finalną wycenę'}
            </Button>
          </div>
        )}

        {canShowStartRepairWork && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">Naprawa</h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                Finalna wycena została zaakceptowana. Rozpoczęcie naprawy zapisze start prac i
                poinformuje właściciela zgłoszenia.
              </p>
            </div>
            {startRepairWorkAction && (
              <Button
                variant="primary"
                size="sm"
                fullWidth
                className="min-h-[42px] text-sm"
                onClick={() => executeAction(startRepairWorkAction)}
                disabled={
                  isUpdating ||
                  workflowActionInFlightRef.current ||
                  pendingActionId === startRepairWorkAction.id
                }
                title={startRepairWorkAction.description}
              >
                {pendingActionId === startRepairWorkAction.id
                  ? `${startRepairWorkAction.label}...`
                  : startRepairWorkAction.label}
              </Button>
            )}
          </div>
        )}

        {canShowRepairWorkCompletedForm && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">Wykonanie naprawy</h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                Zapisz krótkie podsumowanie prac. To będzie widoczne jako serwisowy dowód
                zakończenia naprawy.
              </p>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label
                  htmlFor={`repair-work-summary-${requestIdentity}`}
                  className="text-sm font-medium text-primary-light-200"
                >
                  Podsumowanie prac
                </label>
                <span className="text-[11px] text-primary-light-400">
                  {repairWorkSummary.trim().length}/1600
                </span>
              </div>
              <textarea
                id={`repair-work-summary-${requestIdentity}`}
                value={repairWorkSummary}
                onChange={(event) => setRepairWorkSummary(event.target.value)}
                rows={4}
                maxLength={1700}
                disabled={isUpdating}
                className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100/65 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Co zostało naprawione i sprawdzone"
              />
            </div>
            {repairWorkCompletedAction && (
              <Button
                variant="primary"
                size="sm"
                fullWidth
                className="min-h-[42px] text-sm"
                onClick={() => executeAction(repairWorkCompletedAction)}
                disabled={
                  isUpdating ||
                  workflowActionInFlightRef.current ||
                  pendingActionId === repairWorkCompletedAction.id
                }
                title={repairWorkCompletedAction.description}
              >
                {pendingActionId === repairWorkCompletedAction.id
                  ? 'Zapisywanie...'
                  : 'Oznacz naprawę jako wykonaną'}
              </Button>
            )}
          </div>
        )}

        {canShowPrepareCashSettlement && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">Rozliczenie</h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                Rozliczenie będzie gotówką lub bezpośrednio poza płatnością online. Kwota zostanie
                pobrana z zaakceptowanej finalnej wyceny.
              </p>
            </div>
            <DetailInfoList
              rows={[
                {
                  label: 'Kwota rozliczenia',
                  value: finalQuoteAmountLabel || 'Brak kwoty finalnej',
                },
                { label: 'Metoda', value: 'Gotówka / rozliczenie bezpośrednie' },
              ]}
            />
          </div>
        )}

        {canShowConfirmCashSettlement && (
          <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-primary-light-100">
                Potwierdzenie rozliczenia
              </h3>
              <p className="text-xs leading-relaxed text-primary-light-300">
                Potwierdzenie zamyka zlecenie jako zakończone i rozliczone. Nie uruchamia płatności
                online.
              </p>
            </div>
            <div>
              <label
                htmlFor={`cash-settlement-note-${requestIdentity}`}
                className="mb-1 block text-sm font-medium text-primary-light-200"
              >
                Komentarz do rozliczenia / odbioru
              </label>
              <textarea
                id={`cash-settlement-note-${requestIdentity}`}
                value={cashSettlementNote}
                onChange={(event) => setCashSettlementNote(event.target.value)}
                rows={3}
                maxLength={1100}
                disabled={isUpdating}
                className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100/65 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Opcjonalnie"
              />
            </div>
            {confirmCashSettlementAction && (
              <Button
                variant="primary"
                size="sm"
                fullWidth
                className="min-h-[42px] text-sm"
                onClick={() => executeAction(confirmCashSettlementAction)}
                disabled={
                  isUpdating ||
                  workflowActionInFlightRef.current ||
                  pendingActionId === confirmCashSettlementAction.id
                }
                title={confirmCashSettlementAction.description}
              >
                {pendingActionId === confirmCashSettlementAction.id
                  ? 'Zapisywanie...'
                  : 'Potwierdź rozliczenie i zakończ'}
              </Button>
            )}
          </div>
        )}

        {(canShowFinalQuoteRequesterDecision || shouldRenderFinalQuoteRuntimeBlock) &&
          finalQuote && (
            <div className="space-y-3 rounded-lg bg-primary-dark-100/8 p-3 sm:p-3.5">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-primary-light-100">Finalna wycena</h3>
                <p className="text-xs leading-relaxed text-primary-light-300">
                  {request.status === RepairStatus.FINAL_QUOTE_REJECTED
                    ? 'Naprawa nie została zaakceptowana. Zwrot lub rozliczenie diagnozy jest poza tym etapem.'
                    : request.status === RepairStatus.FINAL_QUOTE_ACCEPTED
                      ? 'Naprawa została zaakceptowana do realizacji.'
                      : actor === 'service'
                        ? 'Finalna wycena czeka na decyzję właściciela zgłoszenia.'
                        : 'Sprawdź diagnozę i kosztorys przed decyzją.'}
                </p>
              </div>

              {diagnosisDisplayRows.length > 0 && <DetailInfoList rows={diagnosisDisplayRows} />}

              <div className="rounded-lg border border-primary-dark-300/24 bg-primary-dark-100/16 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
                      Kwota finalna
                    </p>
                    <p className="text-lg font-semibold text-primary-light-100">
                      {finalQuoteAmountLabel || 'Brak kwoty'}
                    </p>
                  </div>
                  <Badge
                    variant={
                      finalQuote.status === 'rejected'
                        ? 'error'
                        : finalQuote.status === 'accepted'
                          ? 'success'
                          : 'warning'
                    }
                  >
                    {finalQuote.status === 'accepted'
                      ? 'Zaakceptowana'
                      : finalQuote.status === 'rejected'
                        ? 'Odrzucona'
                        : 'Czeka na decyzję'}
                  </Badge>
                </div>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-200">
                  {finalQuote.description === FINAL_QUOTE_DEFAULT_COMMENT
                    ? 'Brak dodatkowego komentarza.'
                    : finalQuote.description || 'Brak dodatkowego komentarza.'}
                </p>
              </div>

              {finalQuoteLineItemsDisplay.length > 0 && (
                <div className="space-y-1.5">
                  {finalQuoteLineItemsDisplay.map((item, index) => (
                    <div
                      key={`${item.label}-${index}`}
                      className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-primary-dark-300/22 bg-primary-dark-100/12 px-2.5 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-primary-light-100">{item.label}</p>
                        <p className="text-xs text-primary-light-400">
                          {getFinalQuoteLineItemTypeLabel(item.type)}
                          {finalQuoteLineItemHasUserFacingQuantity(item.type) && item.quantity
                            ? ` • ilość ${item.quantity}`
                            : ''}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-primary-light-100">
                        {formatCurrency(item.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {canShowFinalQuoteRequesterDecision && (
                <div className="space-y-2">
                  <textarea
                    value={finalQuoteRejectionReason}
                    onChange={(event) => setFinalQuoteRejectionReason(event.target.value)}
                    rows={3}
                    maxLength={1000}
                    disabled={isUpdating}
                    className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100/65 px-3 py-2 text-sm text-primary-light-100 placeholder-primary-light-400 focus:outline-none focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Opcjonalny powód odrzucenia"
                  />
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
                    <Button
                      variant="primary"
                      size="sm"
                      className="min-h-[42px] text-sm"
                      onClick={() =>
                        openWorkflowConfirmation({
                          kind: 'final-quote-decision',
                          decision: 'accept',
                        })
                      }
                      disabled={isUpdating || Boolean(finalQuotePendingDecision)}
                    >
                      {finalQuotePendingDecision === 'accept'
                        ? 'Zapisywanie...'
                        : 'Akceptuję finalną wycenę'}
                    </Button>
                    <Button
                      variant="neutral"
                      size="sm"
                      className="min-h-[42px] border-status-error-500/34 bg-status-error-500/8 text-sm text-status-error-400 hover:bg-status-error-500/12"
                      onClick={() =>
                        openWorkflowConfirmation({
                          kind: 'final-quote-decision',
                          decision: 'reject',
                        })
                      }
                      disabled={isUpdating || Boolean(finalQuotePendingDecision)}
                    >
                      {finalQuotePendingDecision === 'reject' ? 'Zapisywanie...' : 'Odrzucam'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

        {shouldRenderStageRuntimeBlock && shouldShowPreliminaryStageInfo && (
          <div className="space-y-2 rounded-lg border border-primary-dark-300/30 bg-primary-dark-200/16 p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
              Aktualny etap
            </p>
            <p className="text-sm font-semibold text-primary-light-100">
              {workflowV15ReadModel.stageLabel}
            </p>
            <p className="text-sm leading-relaxed text-primary-light-300">
              {workflowV15StageDescription}
            </p>
          </div>
        )}

        {shouldRenderStageRuntimeBlock &&
          shouldShowRepairStageInfo &&
          !canShowRepairWorkCompletedForm &&
          !canShowConfirmCashSettlement && (
            <div className="space-y-2 rounded-lg border border-primary-dark-300/30 bg-primary-dark-200/16 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
                Aktualny etap
              </p>
              <p className="text-sm font-semibold text-primary-light-100">
                {workflowV15ReadModel.stageLabel}
              </p>
              <p className="text-sm leading-relaxed text-primary-light-300">
                {workflowV15StageDescription}
              </p>
            </div>
          )}

        {requiresQuoteInput && (
          <div className="space-y-2.5 rounded-lg border border-primary-dark-300/40 bg-primary-dark-200/18 p-3">
            <Input
              id={`order-quote-price-${requestIdentity}`}
              label="Wycena (PLN)"
              type="number"
              min={0}
              step="0.01"
              value={quotePrice}
              onChange={(event) => setQuotePrice(event.target.value)}
              placeholder="np. 249.99"
            />
            <div>
              <p className="mb-1 text-xs uppercase tracking-wide text-primary-light-400">
                Komentarz roboczy (opcjonalnie)
              </p>
              <textarea
                value={quoteDescription}
                onChange={(event) => setQuoteDescription(event.target.value)}
                rows={3}
                className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200/70 px-3 py-2 text-sm text-primary-light-100 focus:outline-none focus:ring-2 focus:ring-ratuj-300"
                placeholder="Zakres wyceny i założenia"
              />
            </div>
            {!hasValidQuotePrice && (
              <p className="text-xs text-status-warning-500">
                Aby wysłać wycenę, podaj kwotę większą od 0.
              </p>
            )}
          </div>
        )}

        {hasVisibleActions ? (
          <div className="space-y-3">
            {visiblePrimaryAction && (
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-wide text-primary-light-400">
                  Główna akcja
                </p>
                <Button
                  variant={getActionButtonVariant(visiblePrimaryAction)}
                  size="sm"
                  fullWidth
                  className="min-h-[42px] text-sm"
                  onClick={() => executeAction(visiblePrimaryAction)}
                  disabled={
                    isUpdating ||
                    workflowActionInFlightRef.current ||
                    isClientQuoteDecisionLocked ||
                    (visiblePrimaryAction.nextStatus === RepairStatus.QUOTED && !canSendQuote)
                  }
                  title={visiblePrimaryAction.description}
                >
                  {pendingActionId === visiblePrimaryAction.id
                    ? `${visiblePrimaryAction.label}...`
                    : visiblePrimaryAction.label}
                </Button>
              </div>
            )}

            {visibleSecondaryActions.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-wide text-primary-light-400">
                  Pozostałe akcje
                </p>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {visibleSecondaryActions.map((action) => {
                    const isPendingThisAction = pendingActionId === action.id;
                    return (
                      <Button
                        key={`action-secondary-${action.id}`}
                        variant="neutral"
                        size="sm"
                        className="min-h-[40px] text-sm"
                        onClick={() => executeAction(action)}
                        disabled={
                          isUpdating ||
                          workflowActionInFlightRef.current ||
                          isClientQuoteDecisionLocked ||
                          (action.nextStatus === RepairStatus.QUOTED && !canSendQuote)
                        }
                        title={action.description}
                      >
                        {isPendingThisAction ? `${action.label}...` : action.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {visibleDestructiveActions.length > 0 && (
              <div className="space-y-1.5 border-t border-primary-dark-300/26 pt-2.5">
                <p className="text-[10px] uppercase tracking-wide text-primary-light-400">
                  Akcje kończące sprawę
                </p>
                <div className="grid grid-cols-1 gap-1.5">
                  {visibleDestructiveActions.map((action) => {
                    const isPendingThisAction = pendingActionId === action.id;
                    return (
                      <Button
                        key={`action-destructive-${action.id}`}
                        variant="neutral"
                        size="sm"
                        className="min-h-11 border border-status-error-500/34 bg-status-error-500/8 text-sm text-status-error-400 hover:bg-status-error-500/12"
                        onClick={() => executeAction(action)}
                        disabled={
                          isUpdating ||
                          workflowActionInFlightRef.current ||
                          isClientQuoteDecisionLocked
                        }
                        title={action.description}
                        data-action-semantic="destructive"
                        data-testid={
                          action.id === 'client_cancel_new'
                            ? 'owner-cancel-request-secondary'
                            : undefined
                        }
                      >
                        {isPendingThisAction ? `${action.label}...` : action.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : shouldShowNoActionInfo ? (
          <Alert variant="info">{noActionStateMessage}</Alert>
        ) : null}

        {isUpdating && <Spinner size="sm" />}
      </div>
    );
  };

  const renderSharedChatWorkspace = ({
    compactEmptyState,
    hint,
  }: {
    compactEmptyState: boolean;
    hint: string;
  }) => (
    <div
      ref={activeChatWorkspaceRef}
      className="flex min-h-0 min-w-0 flex-none flex-col overflow-hidden rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/12 [touch-action:pan-y]"
      style={sharedChatWorkspaceStyle}
      data-testid="shared-chat-workspace"
      data-chat-workspace="shared"
    >
      <div className="shrink-0 px-3 pb-2 pt-2.5">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <p className="min-w-0 flex-1 text-sm leading-snug text-primary-light-300">{hint}</p>
          {shouldShowServiceChatUnreadCue ? (
            <span
              className="shrink-0 rounded-full border border-ratuj-300/20 bg-ratuj-300/8 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-ratuj-100"
              data-testid="service-chat-unread-cue"
            >
              Nowa wiadomość
            </span>
          ) : null}
        </div>
      </div>
      {receiverId ? (
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden [touch-action:pan-y]"
          data-testid="order-communication-chat-shell"
        >
          <ChatWindow
            repairRequestId={requestId}
            receiverId={receiverId}
            onThreadReadyChange={setIsMessageThreadReady}
            onMessageCountChange={setMessageCount}
            onComposerFocusChange={handleCommunicationComposerFocusChange}
            compactEmptyState={compactEmptyState}
            quickReplies={quickReplies}
            initialBottomAnchorToken={isMobileViewport ? communicationEntryToken : undefined}
            initialBottomAnchorReady={isMobileViewport ? isCommunicationWorkspaceReady : true}
          />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 [touch-action:pan-y]">
          <Alert variant="info">
            Brak danych drugiej strony konwersacji. Komunikacja będzie dostępna po przypisaniu
            uczestnika.
          </Alert>
        </div>
      )}
    </div>
  );

  if (isWorkshopDetail) {
    const workshopRailClass =
      runtime.orderTone === 'danger'
        ? 'border-l-status-error-500'
        : runtime.orderTone === 'warning'
          ? 'border-l-status-warning-500'
          : runtime.orderTone === 'success'
            ? 'border-l-status-success-500'
            : 'border-l-ratuj-300/70';
    const workshopIndexLabel = operationalIndexState.value || 'bez indeksu (legacy)';
    const workshopDeviceMeta = [runtime.header.title, runtime.header.brandModelLabel]
      .filter(Boolean)
      .join(' / ');
    const workshopActionTitle =
      roleAwareCurrentStep?.label ||
      workflowV15ReadModel.nextStep.label ||
      runtime.summaryLine ||
      runtime.header.stageLabel;
    const handoverEvents = Array.isArray(request.handoverEvents) ? request.handoverEvents : [];
    const repairWork = request.repairWork;
    const settlementAmountLabel =
      formatOptionalCurrency(cashSettlement?.amount) ||
      finalQuoteAmountLabel ||
      'Brak zaakceptowanej kwoty';
    const cleanBackLabel = backLabel.replace(/^[\s←<-]+/, '').trim() || 'WYJDŹ';
    const workshopRouteTarget = `/app/request/${requestId}`;
    const processSteps = getRepairRequestProcessSteps();
    const processProgress = getRepairRequestProcessProgress(request.status);
    const currentProcessStep =
      processSteps[Math.min(processProgress.currentStepIndex, processSteps.length - 1)];
    const workshopProcessStepLabel =
      request.status === RepairStatus.AWAITING_FULFILLMENT ||
      request.status === RepairStatus.FINAL_QUOTE_READY
        ? runtime.orientation.stageLabel
        : currentProcessStep?.label || 'Etap';
    const workshopStepLine = `Krok ${processProgress.currentStepIndex + 1}/${
      processProgress.totalStepCount
    } · ${workshopProcessStepLabel}`;
    const quoteLauncherLine = getWorkshopFinalQuoteLauncherLine(
      getFinalQuoteStatusLabel(finalQuote?.status),
      finalQuoteAmountLabel,
      preliminaryEstimateSummary
    );
    const moduleTiles: Array<{
      id: Exclude<WorkshopSectionId, 'action'>;
      title: string;
      line: string;
    }> = [
      {
        id: 'description',
        title: 'Szczegóły zlecenia',
        line: getWorkshopProblemPreview(issueDescription),
      },
      {
        id: 'messages',
        title: 'Czat z klientem',
        line: serviceCommunicationLauncherLine,
      },
      {
        id: 'repair',
        title: 'Etap naprawy',
        line: workshopStepLine,
      },
      {
        id: 'quote',
        title: 'Wycena',
        line: quoteLauncherLine,
      },
      {
        id: 'settlement',
        title: 'Rozliczenie',
        line: `Gotówka / bezpośrednio · ${getCashSettlementStatusLabel(cashSettlement?.status)}`,
      },
      {
        id: 'technical',
        title: 'Druk i identyfikacja',
        line: 'Etykieta, QR, dane techniczne',
      },
    ];
    const workshopDirectConfirmationIntent: WorkflowConfirmationIntent | null =
      canShowFulfillmentConfirmation
        ? { kind: 'confirm-fulfillment', mode: fulfillment.selectedMode }
        : canShowStartRepairWork && startRepairWorkAction
          ? { kind: 'workflow-action', action: startRepairWorkAction }
          : canShowPrepareCashSettlement && prepareCashSettlementAction
            ? { kind: 'workflow-action', action: prepareCashSettlementAction }
            : canShowConfirmCashSettlement && confirmCashSettlementAction
              ? { kind: 'workflow-action', action: confirmCashSettlementAction }
              : null;
    const workshopHasActionPayload =
      canShowPreliminaryServiceForm ||
      canShowFinalQuoteServiceForm ||
      canShowRepairWorkCompletedForm ||
      Boolean(workshopDirectConfirmationIntent) ||
      Boolean(actionablePrimaryAction) ||
      secondaryActions.length > 0 ||
      destructiveActions.length > 0 ||
      requiresQuoteInput;
    const workshopActionCtaLabel = canShowRepairWorkCompletedForm
      ? 'Uzupełnij wykonanie naprawy'
      : canShowFinalQuoteServiceForm
        ? 'Przygotuj finalną wycenę'
        : canShowPreliminaryServiceForm
          ? 'Otwórz szybką wycenę'
          : canShowPrepareCashSettlement
            ? 'Przygotuj rozliczenie'
            : canShowConfirmCashSettlement
              ? 'Potwierdź rozliczenie i zakończ'
              : canShowFulfillmentConfirmation
                ? fulfillmentConfirmationCtaLabel
                : canShowStartRepairWork
                  ? startRepairWorkAction?.label || 'Rozpocznij naprawę'
                  : actionablePrimaryAction?.label || 'Otwórz akcję';
    const activeWorkshopTitles: Record<WorkshopSectionId, string> = {
      action: 'Następny krok',
      description: 'Szczegóły zlecenia',
      messages: 'Czat z klientem',
      repair: 'Etap naprawy',
      quote: 'Wycena',
      photos: 'Zdjęcia',
      settlement: 'Rozliczenie',
      technical: 'Druk i identyfikacja',
    };
    const activeWorkshopTitle =
      activeWorkshopSection === 'overview' ? 'PANEL' : activeWorkshopTitles[activeWorkshopSection];
    const servicePlannerShortcutLink = servicePlannerShortcutHref ? (
      <Link
        to={servicePlannerShortcutHref}
        className="rs-detail-utility-link focus-outline"
        data-testid="service-planner-shortcut"
      >
        PLANER
      </Link>
    ) : null;

    return (
      <Card className="border-transparent bg-transparent p-0 shadow-none">
        <div className="space-y-3 md:space-y-4" data-testid="service-workshop-detail">
          {isWorkshopOverview ? (
            <section
              className={`rounded-lg bg-primary-dark-100/10 px-2.5 py-2.5 shadow-none ring-1 ring-primary-dark-300/10 border-l-4 ${workshopRailClass}`}
              data-testid="service-workshop-header"
            >
              <div className="flex min-w-0 items-center gap-2">
                {shouldShowBack && onBack && (
                  <Button
                    variant="neutral"
                    size="sm"
                    icon={<ArrowLeftIcon className="h-4 w-4" />}
                    onClick={onBack}
                    className="min-h-[30px] shrink-0 border-transparent bg-primary-dark-200/12 px-2 text-[11px] uppercase tracking-[0.04em] text-primary-light-200 hover:bg-primary-dark-200/24"
                  >
                    {cleanBackLabel}
                  </Button>
                )}
                <div className="ml-auto flex min-w-0 shrink-0 items-center justify-end gap-1.5">
                  {servicePlannerShortcutLink}
                  {fulfillmentHeaderPillLabel && (
                    <span
                      className="max-w-[24vw] truncate rounded-full border border-primary-dark-300/26 bg-primary-dark-200/16 px-2 py-0.5 text-[10px] font-semibold text-primary-light-300"
                      data-testid="request-fulfillment-header-pill"
                    >
                      {fulfillmentHeaderPillLabel}
                    </span>
                  )}
                  <Badge
                    variant={serviceDetailStatusBadgeVariant}
                    size="sm"
                    className="max-w-[42vw] truncate whitespace-nowrap"
                  >
                    {statusLine}
                  </Badge>
                </div>
              </div>

              <div className="mt-2 min-w-0">
                <p className="truncate text-[13px] font-semibold leading-tight text-primary-light-100">
                  {workshopDeviceMeta || 'Sprzęt bez nazwy'}
                </p>
                <h2
                  className="mt-0.5 truncate text-2xl font-semibold leading-none text-primary-light-100"
                  data-testid="service-workshop-operational-index"
                >
                  {workshopIndexLabel}
                </h2>
                <p className="mt-1 truncate text-[11px] leading-snug text-primary-light-400">
                  {runtime.stageLabel}
                </p>
              </div>
            </section>
          ) : (
            <div
              ref={activeWorkshopSection === 'messages' ? activeChatToolbarRef : undefined}
              className="shrink-0"
              data-chat-mini-toolbar={activeWorkshopSection === 'messages' ? 'true' : undefined}
            >
              <WorkshopSubscreenToolbar
                title={activeWorkshopTitle}
                meta={`${workshopIndexLabel} · ${workshopDeviceMeta || 'Sprzęt'}`}
                exitLabel={cleanBackLabel}
                onPanel={returnToWorkshopOverview}
                onExit={shouldShowBack ? onBack : undefined}
                rightAction={servicePlannerShortcutLink}
              />
            </div>
          )}

          {isWorkshopOverview ? (
            <>
              <div className="grid grid-cols-3 gap-1.5" data-testid="service-workshop-quick-tools">
                <WorkshopQuickTool
                  label={`Zdjęcia ${photos.length}`}
                  disabled={photos.length === 0}
                  onClick={() => openWorkshopSection('photos')}
                />
                <WorkshopQuickTool
                  label="QR / ID"
                  onClick={() => openWorkshopSection('technical')}
                />
                <WorkshopQuickTool
                  label="Kontakt"
                  onClick={() => openWorkshopSection('messages')}
                />
              </div>

              <WorkshopSection
                id="action"
                title="NASTĘPNY KROK"
                subtitle={workshopActionTitle}
                active
                emphasis
              >
                <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="min-w-0 text-sm leading-snug text-primary-light-300">
                    {currentStepLine}
                  </p>
                  {workshopHasActionPayload ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        if (workshopDirectConfirmationIntent) {
                          openWorkflowConfirmation(workshopDirectConfirmationIntent);
                          return;
                        }
                        openWorkshopAction();
                      }}
                      disabled={isUpdating || workflowActionInFlightRef.current}
                      className="min-h-[38px] shrink-0 text-sm sm:min-w-[12rem]"
                      data-testid="workshop-open-action"
                    >
                      {isUpdating && workshopDirectConfirmationIntent
                        ? `${workshopActionCtaLabel}...`
                        : workshopActionCtaLabel}
                    </Button>
                  ) : (
                    <span className="shrink-0 rounded-md bg-primary-dark-100/16 px-2.5 py-1.5 text-xs font-semibold text-primary-light-300 ring-1 ring-primary-dark-300/12">
                      {noActionStateMessage}
                    </span>
                  )}
                </div>
                {workflowAwaitingSync && (
                  <div className="mt-2">
                    <Alert variant="info">{pendingReadbackMessage}</Alert>
                  </div>
                )}
                {serviceCancellationAction && (
                  <div className="mt-3 border-t border-primary-dark-300/18 pt-2.5">
                    <Button
                      variant="neutral"
                      size="sm"
                      onClick={() =>
                        openWorkflowConfirmation({
                          kind: 'workflow-action',
                          action: serviceCancellationAction,
                        })
                      }
                      disabled={isUpdating || workflowActionInFlightRef.current}
                      className="min-h-[36px] border border-status-error-500/26 bg-transparent px-3 text-xs text-status-error-400 hover:bg-status-error-500/8"
                      data-testid="service-cancel-repair-secondary"
                    >
                      {pendingActionId === serviceCancellationAction.id
                        ? 'Zamykanie...'
                        : serviceCancellationAction.label}
                    </Button>
                  </div>
                )}
              </WorkshopSection>

              {priorityRequest && (
                <PriorityRequestNotice
                  audience="service"
                  note={priorityRequestNote}
                  dataTestId="service-priority-request-overview"
                />
              )}

              <nav
                aria-label="Moduły sprawy"
                className="space-y-1.5 md:grid md:grid-cols-3 md:gap-2 md:space-y-0"
                data-testid="service-workshop-module-list"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-light-500 md:col-span-3">
                  MODUŁY SPRAWY
                </p>
                {moduleTiles.map((tile) => (
                  <WorkshopModuleLauncher
                    key={tile.id}
                    title={tile.title}
                    line={tile.line}
                    active={false}
                    onClick={() => openWorkshopSection(tile.id)}
                  />
                ))}
              </nav>
            </>
          ) : isWorkshopActionSection ? (
            <>
              <WorkshopSection
                id="action"
                title="NASTĘPNY KROK"
                subtitle={workshopActionTitle}
                active
                emphasis
              >
                {renderActionsBlock(runtime.actionPanel)}
              </WorkshopSection>
            </>
          ) : (
            <>
              <div className="space-y-3" data-testid="workshop-active-section">
                <div className="space-y-3">
                  {activeWorkshopSection === 'description' && (
                    <WorkshopSection
                      id="description"
                      title="SZCZEGÓŁY ZLECENIA"
                      subtitle="Problem, realizacja i najważniejsze instrukcje."
                      active
                    >
                      {priorityRequest && (
                        <PriorityRequestNotice
                          audience="service"
                          note={priorityRequestNote}
                          dataTestId="service-priority-request-description"
                        />
                      )}
                      <div
                        className="rs-detail-info-block"
                        data-testid="workshop-description-problem"
                      >
                        <p className="text-[10px] uppercase tracking-wide text-primary-light-400">
                          Opis problemu
                        </p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-100">
                          {issueDescription}
                        </p>
                      </div>
                      {preferredDateLabel && (
                        <div className="rs-detail-info-block" data-testid="workshop-preferred-date">
                          <p className="text-[10px] uppercase tracking-wide text-primary-light-400">
                            Preferowany termin
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-primary-light-100">
                            {preferredDateLabel}
                          </p>
                        </div>
                      )}
                      <FulfillmentDetailBlock
                        fulfillment={fulfillment}
                        address={fulfillmentAddress}
                        context={fulfillmentModeContext}
                        paymentLabel={getRepairRequestFulfillmentPaymentLabel(fulfillment.payment)}
                        pricingHintLabel={fulfillmentPricingHintLabel}
                        instructions={fulfillmentInstructions}
                        className="rs-detail-info-block"
                      />
                    </WorkshopSection>
                  )}

                  {activeWorkshopSection === 'repair' && (
                    <WorkshopSection
                      id="repair"
                      title="ETAP NAPRAWY"
                      subtitle={workflowV15ReadModel.stageLabel || progressStripLabel}
                      active
                    >
                      <WorkshopRepairTimeline
                        status={request.status}
                        nextStepLabel={currentStepLine}
                        stageLabel={
                          request.status === RepairStatus.AWAITING_FULFILLMENT ||
                          request.status === RepairStatus.FINAL_QUOTE_READY
                            ? runtime.orientation.stageLabel
                            : undefined
                        }
                      />
                      <DetailInfoList
                        rows={[
                          {
                            label: 'Ostatnia aktualizacja',
                            value: formatDateTime(request.updatedAt || request.createdAt),
                          },
                          {
                            label: 'Start prac',
                            value: repairWork?.startedAt
                              ? formatDateTime(repairWork.startedAt)
                              : null,
                          },
                          {
                            label: 'Zakończenie prac',
                            value: repairWork?.completedAt
                              ? formatDateTime(repairWork.completedAt)
                              : null,
                          },
                          { label: 'Podsumowanie prac', value: repairWork?.workSummary },
                        ]}
                      />
                      <div className="rs-detail-info-block">
                        <p className="text-[10px] uppercase tracking-wide text-primary-light-400">
                          Zdarzenia przekazania
                        </p>
                        {handoverEvents.length > 0 ? (
                          <div className="mt-2 space-y-2">
                            {handoverEvents.map((event, index) => (
                              <div
                                key={`${event.type || 'event'}-${index}`}
                                className="rs-detail-handover-event"
                              >
                                <p className="text-sm font-medium text-primary-light-100">
                                  {getHandoverEventLabel(event.type)}
                                </p>
                                <p className="mt-0.5 text-xs text-primary-light-400">
                                  {formatDateTime(event.occurredAt || event.createdAt)}
                                </p>
                                {event.note && (
                                  <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-primary-light-300">
                                    {event.note}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-1.5 text-sm text-primary-light-400">
                            Brak zapisanych zdarzeń przekazania.
                          </p>
                        )}
                      </div>
                    </WorkshopSection>
                  )}

                  {activeWorkshopSection === 'photos' && (
                    <WorkshopSection
                      id="photos"
                      title="ZDJĘCIA ZGŁOSZENIA"
                      subtitle="Podgląd zdjęć zapisanych przy sprawie."
                      active
                    >
                      {photos.length > 0 ? (
                        <div className="space-y-2.5">
                          <button
                            type="button"
                            onClick={() => {
                              setLightboxIndex(0);
                              setLightboxOpen(true);
                            }}
                            className="group relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-primary-dark-200/18 focus:outline-none focus:ring-2 focus:ring-ratuj-300 focus:ring-offset-2 focus:ring-offset-primary-dark-100"
                            aria-label="Pokaż zdjęcie 1"
                          >
                            <img
                              src={photos[0]}
                              alt="Zdjęcie zgłoszenia 1"
                              className="h-full w-full object-contain transition-transform group-hover:scale-[1.01]"
                            />
                            <span className="absolute right-2 top-2 rounded-md bg-primary-dark-100/75 px-2 py-1 text-xs font-semibold text-primary-light-100 ring-1 ring-primary-dark-300/25">
                              1/{photos.length}
                            </span>
                          </button>
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {photos.slice(0, 5).map((photoUrl, photoIndex) => (
                              <button
                                key={`workshop-photo-${photoIndex}`}
                                type="button"
                                onClick={() => {
                                  setLightboxIndex(photoIndex);
                                  setLightboxOpen(true);
                                }}
                                className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-primary-dark-200/18 focus:outline-none focus:ring-2 focus:ring-ratuj-300 focus:ring-offset-2 focus:ring-offset-primary-dark-100"
                                aria-label={`Pokaż zdjęcie ${photoIndex + 1}`}
                              >
                                <img
                                  src={photoUrl}
                                  alt={`Zdjęcie zgłoszenia ${photoIndex + 1}`}
                                  className="h-full w-full object-cover"
                                />
                                {photoIndex === 4 && photos.length > 5 && (
                                  <span className="absolute inset-0 grid place-items-center bg-primary-dark-100/70 text-sm font-semibold text-primary-light-100">
                                    +{photos.length - 5}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-primary-light-400">
                          Brak zdjęć w zapisanym modelu zgłoszenia. Ten widok nie dodaje nowego
                          uploadu.
                        </p>
                      )}
                    </WorkshopSection>
                  )}
                </div>

                <div className="space-y-3">
                  {activeWorkshopSection === 'messages' && (
                    <section
                      id="messages"
                      data-testid="workshop-section-messages"
                      className="flex min-h-0 min-w-0 flex-col overflow-hidden"
                    >
                      {renderSharedChatWorkspace({
                        compactEmptyState: true,
                        hint: serviceCommunicationModuleHint,
                      })}
                    </section>
                  )}

                  {activeWorkshopSection === 'quote' && (
                    <WorkshopSection
                      id="quote"
                      title="WYCENA"
                      subtitle="Wstępna wycena, diagnoza i finalny kosztorys."
                      active
                    >
                      <WorkshopInfoGroup title="Wstępna">
                        <DetailInfoList
                          rows={[
                            {
                              label: 'Zakres',
                              value: preliminaryEstimateSummary,
                              emphasize: Boolean(preliminaryEstimate),
                            },
                            { label: 'Komentarz', value: preliminaryEstimate?.comment },
                          ]}
                        />
                      </WorkshopInfoGroup>
                      {diagnosisDisplayRows.length > 0 && (
                        <WorkshopInfoGroup title="Diagnoza">
                          <DetailInfoList rows={diagnosisDisplayRows} />
                        </WorkshopInfoGroup>
                      )}
                      <WorkshopInfoGroup title="Finalna">
                        <DetailInfoList
                          rows={[
                            {
                              label: 'Kwota',
                              value: finalQuoteAmountLabel || null,
                              emphasize: Boolean(finalQuoteAmountLabel),
                            },
                            {
                              label: 'Status',
                              value: getFinalQuoteStatusLabel(finalQuote?.status),
                            },
                            {
                              label: 'Komentarz',
                              value:
                                finalQuote?.description === FINAL_QUOTE_DEFAULT_COMMENT
                                  ? null
                                  : finalQuote?.description,
                            },
                          ]}
                        />
                      </WorkshopInfoGroup>
                      {finalQuoteLineItemsDisplay.length > 0 ? (
                        <WorkshopInfoGroup title="Pozycje">
                          <div className="space-y-1.5">
                            {finalQuoteLineItemsDisplay.map((item, index) => (
                              <div
                                key={`workshop-quote-line-${item.label}-${index}`}
                                className="rs-detail-line-item"
                                data-testid="workshop-quote-line-item"
                              >
                                <div className="min-w-0">
                                  <p className="break-words text-sm font-medium text-primary-light-100">
                                    {item.label}
                                  </p>
                                  <p className="text-xs text-primary-light-400">
                                    {getFinalQuoteLineItemTypeLabel(item.type)}
                                    {finalQuoteLineItemHasUserFacingQuantity(item.type) &&
                                    item.quantity
                                      ? ` · ilość ${item.quantity}`
                                      : ''}
                                  </p>
                                </div>
                                <p className="shrink-0 text-sm font-semibold text-primary-light-100">
                                  {formatCurrency(item.amount)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </WorkshopInfoGroup>
                      ) : (
                        <p className="text-sm text-primary-light-400">
                          Brak rozpisanych pozycji finalnej wyceny.
                        </p>
                      )}
                    </WorkshopSection>
                  )}

                  {activeWorkshopSection === 'settlement' && (
                    <WorkshopSection
                      id="settlement"
                      title="ROZLICZENIE"
                      subtitle="Końcowa kwota i realnie obsługiwana metoda."
                      active
                    >
                      <div className="grid gap-1.5">
                        <SettlementSummaryItem label="KWOTA" value={settlementAmountLabel} strong />
                        <SettlementSummaryItem
                          label="METODA"
                          value={getCashSettlementMethodLabel(cashSettlement?.method)}
                        />
                        <SettlementSummaryItem
                          label="STATUS"
                          value={getCashSettlementStatusLabel(cashSettlement?.status)}
                        />
                      </div>
                      <DetailInfoList
                        rows={[
                          {
                            label: 'Zakończono',
                            value: request.completedDate
                              ? formatDateTime(request.completedDate)
                              : null,
                          },
                          {
                            label: 'Komentarz do rozliczenia / odbioru',
                            value: cashSettlement?.settlementNote || cashSettlement?.note,
                          },
                        ]}
                      />
                      <p className="rounded-md bg-primary-dark-200/8 px-3 py-2 text-xs leading-relaxed text-primary-light-400 ring-1 ring-primary-dark-300/10">
                        Inne metody płatności są niedostępne w MVP.
                      </p>
                    </WorkshopSection>
                  )}
                </div>
              </div>

              {activeWorkshopSection === 'technical' && (
                <WorkshopSection
                  id="technical"
                  title="DRUK I IDENTYFIKACJA"
                  subtitle="Etykieta, QR i dane pomocnicze."
                  active
                >
                  <div
                    className="grid gap-3 rounded-lg bg-primary-dark-200/10 p-3 ring-1 ring-ratuj-300/16 sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-center"
                    data-testid="workshop-qr-focus"
                  >
                    <div className="mx-auto w-full max-w-[10rem] rounded-lg bg-primary-light-100 p-2.5">
                      <WorkshopQrCode value={workshopRouteTarget} className="h-auto w-full" />
                    </div>
                    <div className="min-w-0 text-center sm:text-left">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ratuj-200">
                        QR / ID sprawy
                      </p>
                      <p className="mt-1 break-words font-mono text-2xl font-semibold leading-none text-primary-light-100">
                        {operationalIndexLabel}
                      </p>
                      <p className="mt-1.5 break-all text-xs leading-relaxed text-primary-light-400">
                        {workshopRouteTarget}
                      </p>
                    </div>
                  </div>
                  <WorkshopInfoGroup title="Identyfikacja">
                    <DetailInfoList
                      rows={[
                        {
                          label: 'Indeks operacyjny',
                          value: operationalIndexLabel,
                          emphasize: true,
                        },
                        {
                          label: 'RequestIndex / intake code',
                          value: request.requestIndex || null,
                        },
                      ]}
                    />
                  </WorkshopInfoGroup>
                  <details className="rounded-md bg-primary-dark-200/8 px-3 py-2 ring-1 ring-primary-dark-300/10">
                    <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-primary-light-400 focus-outline">
                      Dane techniczne
                    </summary>
                    <DetailInfoList
                      rows={[
                        { label: 'Raw requestId', value: requestIdentity },
                        {
                          label: 'Typ flow',
                          value: request.requestFlowType || 'legacy / brak pola',
                        },
                      ]}
                    />
                  </details>
                  {operationalIndexState.isAssigned ? (
                    <RepairRequestPrintHub request={request} />
                  ) : (
                    <Alert variant="info">
                      Wydruk identyfikatora jest dostępny po nadaniu zapisanego indeksu
                      operacyjnego.
                    </Alert>
                  )}
                </WorkshopSection>
              )}
            </>
          )}
        </div>

        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={photos.map((url) => ({ src: url }))}
          index={lightboxIndex}
        />
        {renderPendingConfirmationSheet()}
      </Card>
    );
  }

  if (detailMode && actor === 'client') {
    const clientActiveModuleTitle =
      activeClientSection === 'messages'
        ? 'Komunikacja'
        : activeClientSection === 'settlement'
          ? 'Rozliczenie'
          : activeClientSection === 'description'
            ? 'Szczegóły zlecenia'
            : activeClientSection === 'review'
              ? 'Opinia'
              : 'Etap sprawy';
    const clientActiveModuleMeta = [runtime.header.title, runtime.header.brandModelLabel]
      .filter(Boolean)
      .join(' · ');
    const renderClientReviewPreview = () =>
      existingReview ? (
        <div className="space-y-3" data-testid="client-review-preview">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <StarRatingDisplay rating={existingReview.rating} />
              <span className="font-medium text-primary-light-100">
                {existingReview.rating.toFixed(1)}/5
              </span>
            </div>
            <Badge variant="success" size="sm">
              Opinia wysłana
            </Badge>
          </div>
          {existingReview.comment && (
            <p
              className="rs-detail-note rs-detail-note--success whitespace-pre-wrap text-sm leading-relaxed text-primary-light-100"
              data-testid="client-review-preview-comment"
            >
              {existingReview.comment}
            </p>
          )}
          <p className="text-xs text-primary-light-400">
            Dodano: {formatReviewDate(existingReview.createdAt)}
          </p>
        </div>
      ) : reviewSubmittedSuccessfully ? (
        <Alert variant="success">Opinia wysłana.</Alert>
      ) : null;
    const renderClientReviewContent = () => {
      if (reviewLoading) {
        return (
          <div
            className="rs-detail-note flex min-h-[10rem] items-center gap-3 text-sm text-primary-light-300"
            data-testid="client-review-loading"
          >
            <Spinner size="sm" />
            <span>Ładowanie opinii...</span>
          </div>
        );
      }

      if (existingReview || reviewSubmittedSuccessfully) {
        return renderClientReviewPreview();
      }

      if (canSubmitReview) {
        return (
          <ReviewForm
            repairRequestId={requestId}
            serviceProviderId={readModel.assignment.assignedServiceUserId || ''}
            onReviewAdded={onReviewAdded}
          />
        );
      }

      return (
        <div className="space-y-3">
          <Alert variant="info">Opinia nie jest dostępna dla tego zlecenia.</Alert>
          <div>
            <Button variant="neutral" size="sm" onClick={returnToClientOverview}>
              Wróć do panelu
            </Button>
          </div>
        </div>
      );
    };
    const renderClientReviewModule = () => (
      <ClientModulePanel
        dataTestId="client-active-module-review"
        title="Opinia"
        subtitle="Ocena realizacji zakończonego zlecenia."
      >
        {renderClientReviewContent()}
      </ClientModulePanel>
    );
    const renderClientCommunicationModule = () => (
      <section
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        data-testid="client-active-module-messages"
      >
        {renderSharedChatWorkspace({
          compactEmptyState: showCompactChatEmptyState,
          hint: runtime.rows.communication.hint,
        })}
      </section>
    );
    const renderClientSettlementModule = () => (
      <ClientModulePanel
        dataTestId="client-active-module-settlement"
        title="Rozliczenie"
        subtitle="Kwota, metoda, status i bieżący następny krok."
      >
        <DetailInfoList rows={clientSettlementModuleRows} />
        <ClientMutedNote label="Co dalej" value={clientCurrentStepLine} />
        {renderActionsBlock('settlement')}
        {!hasClientRuntimeMutation && !getRepairStatusMatrix(request.status).isTerminal && (
          <div>
            <Button
              variant="neutral"
              size="sm"
              onClick={() => openClientSection('messages')}
              className="min-h-[38px]"
            >
              Napisz do serwisu
            </Button>
          </div>
        )}
        {preliminaryEstimate?.comment?.trim() && (
          <ClientMutedNote
            label="Komentarz do wyceny wstępnej"
            value={preliminaryEstimate.comment}
          />
        )}
        {finalQuote?.description?.trim() &&
          finalQuote.description !== FINAL_QUOTE_DEFAULT_COMMENT && (
            <ClientMutedNote label="Komentarz do wyceny" value={finalQuote.description} />
          )}
        {(cashSettlement?.settlementNote || cashSettlement?.note) && (
          <ClientMutedNote
            label="Komentarz do rozliczenia / odbioru"
            value={cashSettlement.settlementNote || cashSettlement.note || ''}
          />
        )}
      </ClientModulePanel>
    );
    const renderClientContextModule = () => (
      <ClientModulePanel
        dataTestId="client-active-module-description"
        title="Szczegóły zlecenia"
        subtitle="Sprzęt, problem, realizacja i załączniki."
      >
        {priorityRequest && (
          <PriorityRequestNotice
            audience="client"
            note={priorityRequestNote}
            dataTestId="client-priority-request-notice"
          />
        )}
        <DetailInfoList rows={clientContextRows} />
        <FulfillmentDetailBlock
          fulfillment={fulfillment}
          address={fulfillmentAddress}
          context={fulfillmentModeContext}
          paymentLabel={getRepairRequestFulfillmentPaymentLabel(fulfillment.payment)}
          pricingHintLabel={fulfillmentPricingHintLabel}
          instructions={fulfillmentInstructions}
          className="rs-detail-info-block"
        />
        <ClientMutedNote label="Problem" value={issueDescription} />
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2.5">
            {photos.map((photoUrl, photoIndex) => (
              <button
                key={`client-photo-${photoIndex}`}
                type="button"
                onClick={() => {
                  setLightboxIndex(photoIndex);
                  setLightboxOpen(true);
                }}
                className="overflow-hidden rounded focus:outline-none focus:ring-2 focus:ring-ratuj-300 focus:ring-offset-2 focus:ring-offset-primary-dark-100"
                aria-label={`Pokaż zdjęcie ${photoIndex + 1}`}
              >
                <img
                  src={photoUrl}
                  alt={`Załącznik ${photoIndex + 1}`}
                  className="h-16 w-16 rounded object-cover transition-opacity hover:opacity-80 sm:h-20 sm:w-20"
                />
              </button>
            ))}
          </div>
        )}
      </ClientModulePanel>
    );
    const renderClientRepairModule = () => (
      <ClientModulePanel
        dataTestId="client-active-module-repair"
        title="Etap sprawy"
        subtitle="Jeden wspólny widok postępu i historii."
      >
        <WorkshopRepairTimeline
          status={request.status}
          nextStepLabel={clientCurrentStepLine}
          stageLabel={
            request.status === RepairStatus.AWAITING_FULFILLMENT ||
            request.status === RepairStatus.FINAL_QUOTE_READY
              ? runtime.orientation.stageLabel
              : undefined
          }
        />
      </ClientModulePanel>
    );
    const renderClientActiveModule = () => {
      if (activeClientSection === 'messages') {
        return renderClientCommunicationModule();
      }
      if (activeClientSection === 'settlement') {
        return renderClientSettlementModule();
      }
      if (activeClientSection === 'description') {
        return renderClientContextModule();
      }
      if (activeClientSection === 'review') {
        return renderClientReviewModule();
      }
      return renderClientRepairModule();
    };

    return (
      <Card className="border-transparent bg-transparent p-0 shadow-none">
        <div className="space-y-3 md:space-y-4" data-testid="client-request-detail">
          {isClientOverview ? (
            <>
              {shouldShowBack && onBack && (
                <div>
                  <Button
                    variant="neutral"
                    size="sm"
                    icon={<ArrowLeftIcon className="h-4 w-4" />}
                    onClick={onBack}
                    className="rounded-full"
                  >
                    {backLabel}
                  </Button>
                </div>
              )}

              <section
                className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3 sm:p-4"
                data-testid="client-case-header"
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <h2 className="line-clamp-2 break-words text-lg font-semibold leading-tight text-primary-light-100 sm:text-[20px]">
                      {runtime.header.title}
                    </h2>
                    <p className="truncate text-xs leading-relaxed text-primary-light-300">
                      {clientIdentityLine}
                    </p>
                    <p className="truncate text-xs leading-relaxed text-primary-light-400">
                      {clientDatesLine}
                    </p>
                  </div>
                  <Badge
                    variant={serviceDetailStatusBadgeVariant}
                    size="sm"
                    className="max-w-[10rem] shrink-0 whitespace-normal text-left sm:max-w-[14rem]"
                  >
                    {statusLine}
                  </Badge>
                </div>
              </section>

              <section
                className="rounded-lg border border-ratuj-300/20 bg-ratuj-300/7 p-3 sm:p-4"
                data-testid="client-current-step-panel"
              >
                <div className="space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
                    {clientCurrentStepLabel}
                  </p>
                  <p className="line-clamp-2 text-sm leading-relaxed text-primary-light-100">
                    {clientCurrentStepLine}
                  </p>
                </div>

                <div className="mt-3 space-y-3">
                  {request.status === RepairStatus.CANCELLED_BY_SERVICE &&
                    (serviceCancellationDisplayReasonLabel ||
                      serviceCancellationDisplayReasonText) && (
                      <div className="rounded-md border border-primary-dark-300/18 bg-primary-dark-100/12 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
                          Powód
                        </p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-100">
                          {[
                            serviceCancellationDisplayReasonLabel,
                            serviceCancellationDisplayReasonText,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                    )}
                  {shouldRenderClientCurrentStepActionPanel ? (
                    renderActionsBlock('settlement')
                  ) : actionablePrimaryAction ? (
                    <div className="space-y-2">
                      <Button
                        variant={topDecisionButtonVariant}
                        size="sm"
                        className="min-h-[42px] text-sm"
                        onClick={() => executeAction(actionablePrimaryAction)}
                        disabled={topDecisionButtonDisabled}
                      >
                        {pendingActionId === actionablePrimaryAction.id
                          ? `${actionablePrimaryAction.label}...`
                          : actionablePrimaryAction.label}
                      </Button>
                      {(secondaryActions.length > 0 || destructiveActions.length > 0) &&
                        renderActionsBlock(runtime.actionPanel)}
                    </div>
                  ) : !currentStatusMatrix.isTerminal ? (
                    <div className="space-y-2">
                      <Button
                        variant={
                          request.status === RepairStatus.AWAITING_FULFILLMENT
                            ? 'neutral'
                            : 'primary'
                        }
                        size="sm"
                        onClick={() => openClientSection('messages')}
                        className={
                          request.status === RepairStatus.AWAITING_FULFILLMENT
                            ? 'min-h-[38px] text-sm'
                            : 'min-h-[42px] text-sm'
                        }
                        data-testid={
                          request.status === RepairStatus.AWAITING_FULFILLMENT
                            ? 'client-fulfillment-communication-secondary'
                            : undefined
                        }
                      >
                        Napisz do serwisu
                      </Button>
                      {ownerEarlyCancellationAction && renderActionsBlock(runtime.actionPanel)}
                    </div>
                  ) : null}
                </div>
              </section>

              {priorityRequest && (
                <PriorityRequestNotice
                  audience="client"
                  note={priorityRequestNote}
                  dataTestId="client-priority-request-overview"
                />
              )}

              {shouldShowClientReviewCtaCard && (
                <section
                  className="rounded-lg border border-ratuj-300/20 bg-primary-dark-100/12 p-3 ring-1 ring-ratuj-300/10 sm:p-4"
                  data-testid="client-review-cta-card"
                >
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                    <div className="min-w-0 space-y-1">
                      <h3 className="text-base font-semibold leading-snug text-primary-light-100">
                        Oceń serwis
                      </h3>
                      <p className="text-sm leading-relaxed text-primary-light-300">
                        Twoja opinia pomoże innym klientom wybrać sprawdzony serwis.
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => openClientSection('review')}
                      className="min-h-[42px] w-full text-sm sm:w-auto"
                    >
                      Wystaw opinię
                    </Button>
                  </div>
                </section>
              )}

              <section className="space-y-2" data-testid="client-module-list">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
                  MODUŁY SPRAWY
                </p>
                <div className="space-y-2">
                  <ClientModuleLauncher
                    title="Komunikacja z serwisem"
                    line={clientCommunicationLauncherLine}
                    onClick={() => openClientSection('messages')}
                  />
                  <ClientModuleLauncher
                    title="Rozliczenie"
                    line={clientSettlementLauncherLine}
                    onClick={() => openClientSection('settlement')}
                  />
                  <ClientModuleLauncher
                    title="Szczegóły zlecenia"
                    line={clientContextLauncherLine}
                    onClick={() => openClientSection('description')}
                  />
                  <ClientModuleLauncher
                    title="Etap sprawy"
                    line={clientRepairLauncherLine}
                    onClick={() => openClientSection('repair')}
                  />
                  {reviewSubmitted && (
                    <ClientModuleLauncher
                      title="Opinia"
                      line="Opinia wysłana"
                      onClick={() => openClientSection('review')}
                    />
                  )}
                </div>
              </section>
            </>
          ) : (
            <>
              <div
                ref={activeClientSection === 'messages' ? activeChatToolbarRef : undefined}
                className="shrink-0"
                data-chat-mini-toolbar={activeClientSection === 'messages' ? 'true' : undefined}
              >
                <WorkshopSubscreenToolbar
                  title={clientActiveModuleTitle}
                  meta={clientActiveModuleMeta}
                  exitLabel="WYJDŹ"
                  onPanel={returnToClientOverview}
                  onExit={shouldShowBack ? onBack : undefined}
                />
              </div>
              {renderClientActiveModule()}
            </>
          )}
        </div>
        {renderPendingConfirmationSheet()}
      </Card>
    );
  }

  return (
    <Card
      className={`${cardPaddingClass} ${
        useServiceDetailLayout
          ? 'border-transparent bg-transparent shadow-none'
          : workspaceSimpleMode
            ? 'border-primary-dark-300/42 bg-primary-dark-100/24 shadow-none'
            : orderToneClasses[runtime.orderTone]
      }`}
    >
      <div className={useServiceDetailLayout ? 'space-y-3' : 'space-y-1.5'}>
        {useServiceDetailLayout ? (
          <div className="space-y-3">
            {shouldShowBack && onBack && (
              <div>
                <Button
                  variant="neutral"
                  size="sm"
                  icon={<ArrowLeftIcon className="h-4 w-4" />}
                  onClick={onBack}
                >
                  {backLabel}
                </Button>
              </div>
            )}

            <div
              className="grid gap-3 rounded-lg border border-ratuj-300/22 bg-ratuj-300/7 p-3 sm:p-4 lg:grid-cols-[minmax(0,1fr)_minmax(14rem,auto)] lg:items-center"
              data-testid="service-detail-top-identification"
            >
              <div className="min-w-0 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2 lg:block">
                  <div className="min-w-0">
                    <h2 className="line-clamp-2 text-lg font-semibold leading-tight text-primary-light-100 sm:text-[20px]">
                      {runtime.header.title}
                    </h2>
                    <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs leading-relaxed text-primary-light-300">
                      {serviceDetailMetaRows.map((value, index) => (
                        <span key={value} className="min-w-0 break-words">
                          {index > 0 && <span className="mr-2 text-primary-light-500">·</span>}
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Badge variant={serviceDetailStatusBadgeVariant} size="sm" className="lg:hidden">
                    {statusLine}
                  </Badge>
                </div>

                <div className="space-y-1.5">
                  <p
                    className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400"
                    data-testid="service-detail-next-step-label"
                  >
                    {currentStepLabel}
                  </p>
                  <p
                    className={`text-sm leading-relaxed ${
                      runtime.showActionUrgency
                        ? 'text-primary-light-100'
                        : 'text-primary-light-300'
                    }`}
                  >
                    {currentStepLine}
                  </p>
                  {topDecisionHelper && (
                    <p className="text-xs leading-relaxed text-primary-light-400">
                      {topDecisionHelper}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 lg:items-end">
                <Badge
                  variant={serviceDetailStatusBadgeVariant}
                  size="sm"
                  className="hidden lg:inline-flex"
                >
                  {statusLine}
                </Badge>
                {topDecisionButtonLabel && (
                  <Button
                    variant={topDecisionButtonVariant}
                    size="sm"
                    fullWidth
                    className="min-h-[42px] text-sm lg:w-auto lg:min-w-[13rem]"
                    onClick={() => {
                      if (actionablePrimaryAction && !topDecisionNeedsPanel) {
                        executeAction(actionablePrimaryAction);
                        return;
                      }
                      focusPanel(
                        actionablePrimaryAction
                          ? runtime.actionPanel
                          : runtime.primaryPanel || 'communication'
                      );
                    }}
                    disabled={topDecisionButtonDisabled}
                  >
                    {topDecisionButtonLabel}
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => focusPanel('settlement')}
                  className="text-left text-xs font-medium text-primary-light-300 transition hover:text-ratuj-100 focus-outline lg:text-right"
                >
                  {progressStripLabel}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={headerBlockClass}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 space-y-0.5">
                <h2 className="truncate text-base font-semibold leading-tight text-primary-light-100 sm:text-[17px]">
                  {runtime.header.title}
                </h2>
                <p className="truncate text-xs text-primary-light-400">{headerMeta}</p>
                {workspaceSimpleMode && compactSecondaryMeta && (
                  <p className="truncate text-xs text-primary-light-300">{compactSecondaryMeta}</p>
                )}
              </div>
              <div className="flex max-w-full flex-wrap items-center justify-end gap-1">
                {useServiceDetailLayout && fulfillmentHeaderPillLabel && (
                  <span
                    className="max-w-[40vw] truncate rounded-full border border-primary-dark-300/26 bg-primary-dark-200/16 px-2 py-0.5 text-[10px] font-semibold text-primary-light-300"
                    data-testid="request-fulfillment-header-pill"
                  >
                    {fulfillmentHeaderPillLabel}
                  </span>
                )}
                {(workspaceSimpleMode
                  ? primaryHeaderBadge
                    ? [primaryHeaderBadge]
                    : []
                  : compactHeaderBadges
                ).map((badge) => (
                  <Badge
                    key={badge.id}
                    variant={badge.variant}
                    size="sm"
                    className="max-w-[48vw] truncate sm:max-w-[52vw]"
                  >
                    {badge.label}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
                  Status sprawy: <span className="text-primary-light-200">{statusLine}</span>
                </p>
                <p
                  className={`min-w-0 rounded-lg border px-2.5 py-1.5 text-xs sm:text-[13px] ${nextStepToneClass}`}
                >
                  {runtime.showActionUrgency ? 'Twoja akcja' : currentStepLabel}: {currentStepLine}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {fullViewHref && (
                  <Link
                    to={fullViewHref}
                    className={
                      workspaceSimpleMode
                        ? 'inline-flex min-h-[36px] items-center rounded-lg border border-primary-dark-300/40 bg-primary-dark-200/30 px-2.5 py-1 text-[11px] font-medium text-primary-light-300 transition hover:border-ratuj-300/30 hover:text-ratuj-200 focus-outline [touch-action:manipulation]'
                        : 'inline-flex items-center rounded-lg px-1 py-0.5 text-[11px] font-medium text-primary-light-400 underline decoration-primary-light-400/40 underline-offset-2 transition hover:text-ratuj-300 hover:decoration-ratuj-300/60 focus-outline [touch-action:manipulation]'
                    }
                  >
                    Pełny widok
                  </Link>
                )}
                {shouldShowBack && onBack && (
                  <Button
                    variant="neutral"
                    size="sm"
                    icon={<ArrowLeftIcon className="h-4 w-4" />}
                    onClick={onBack}
                  >
                    {backLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div
          data-accordion-focused={focusedAccordionMode ? 'true' : 'false'}
          className={`${
            useServiceDetailLayout
              ? detailDesktopAllSectionsVisible
                ? 'grid gap-3 lg:grid-cols-[minmax(0,1.08fr)_minmax(20rem,0.92fr)] lg:items-start'
                : 'rs-detail-row-stack'
              : `overflow-hidden rounded-xl ${
                  workspaceSimpleMode
                    ? 'bg-primary-dark-100/10 ring-1 ring-primary-dark-300/25'
                    : 'bg-primary-dark-100/10 ring-1 ring-primary-dark-300/30'
                }`
          }`}
        >
          {detailRowOrder.map((rowId, index) => {
            const row = runtime.rows[rowId];
            const isOpen = detailDesktopAllSectionsVisible || (isExpanded && activePanel === rowId);
            const displayRowTitle =
              useServiceDetailLayout && rowId === 'description'
                ? 'Szczegóły zlecenia'
                : useServiceDetailLayout && rowId === 'settlement'
                  ? decisionHistoryTitle
                  : row.title;
            const displayRowSubtitle =
              useServiceDetailLayout && rowId === 'description'
                ? 'Sprzęt, problem, realizacja i załączniki.'
                : useServiceDetailLayout && rowId === 'communication'
                  ? actor === 'service'
                    ? serviceCommunicationModuleHint
                    : runtime.hasUnreadMessages
                      ? 'Są nowe wiadomości w tej sprawie.'
                      : 'Kontakt z serwisem.'
                  : useServiceDetailLayout && rowId === 'settlement'
                    ? 'Decyzje, wyceny i zmiany statusu.'
                    : row.subtitle;
            const baseRowClasses = rowToneClasses[row.tone];
            const rowActionCount = 'actionCount' in row ? row.actionCount : 0;
            const railLabel = isOpen
              ? 'Otwarte'
              : row.hasAttention
                ? rowActionCount > 0
                  ? formatActionCountLabel(rowActionCount)
                  : rowId === 'communication'
                    ? 'Nowe'
                    : 'Do sprawdzenia'
                : workspaceSimpleMode || useServiceDetailLayout
                  ? 'Szczegóły'
                  : 'Wejdź';
            const shouldShowRailBadge = detailDesktopAllSectionsVisible
              ? row.hasAttention
              : isOpen || row.hasAttention || (!workspaceSimpleMode && !useServiceDetailLayout);
            const isCommunicationWorkspaceRow = isOpen && rowId === 'communication';
            const isMobileCommunicationWorkspaceRow =
              isCommunicationWorkspaceRow && isMobileViewport;
            const isDesktopCommunicationWorkspaceRow =
              isCommunicationWorkspaceRow && !isMobileViewport && detailMode;
            const rowDetailStyle = isMobileCommunicationWorkspaceRow
              ? {
                  height: mobilePanelMaxHeight
                    ? `${mobilePanelMaxHeight}px`
                    : 'calc(var(--app-vh, 100dvh) - 13rem - env(safe-area-inset-bottom))',
                }
              : isDesktopCommunicationWorkspaceRow
                ? {
                    height: 'clamp(360px, 56vh, 560px)',
                  }
                : undefined;

            return (
              <div
                key={rowId}
                className={
                  useServiceDetailLayout
                    ? detailDesktopAllSectionsVisible
                      ? `rs-detail-row-shell rs-detail-row-shell--card ${getDetailRowPlacementClass(
                          rowId,
                          runtime.actionPanel
                        )}`
                      : 'rs-detail-row-shell'
                    : index === detailRowOrder.length - 1
                      ? ''
                      : 'border-b border-primary-dark-300/30'
                }
              >
                <button
                  ref={(node) => {
                    rowButtonRefs.current[rowId] = node;
                  }}
                  type="button"
                  onClick={() => {
                    if (!detailDesktopAllSectionsVisible) {
                      switchPanel(rowId);
                    }
                  }}
                  className={`${
                    useServiceDetailLayout
                      ? `group rs-detail-row-trigger ${
                          detailDesktopAllSectionsVisible ? 'rs-detail-row-trigger--static' : ''
                        }`
                      : rowButtonClass
                  } ${
                    useServiceDetailLayout
                      ? isOpen
                        ? detailDesktopAllSectionsVisible
                          ? 'rs-detail-row-trigger--open-static'
                          : 'rs-detail-row-trigger--open'
                        : 'rs-detail-row-trigger--idle'
                      : workspaceSimpleMode
                        ? isOpen
                          ? 'bg-ratuj-300/7 text-primary-light-100'
                          : 'bg-primary-dark-200/14 text-primary-light-300 hover:bg-primary-dark-200/30'
                        : baseRowClasses
                  }`}
                  aria-expanded={isOpen}
                  aria-controls={`order-row-${requestIdentity}-${rowId}`}
                  tabIndex={detailDesktopAllSectionsVisible ? -1 : undefined}
                >
                  <div className="min-w-0 py-2.5">
                    <p className="text-sm font-semibold text-primary-light-100 sm:text-[15px]">
                      {displayRowTitle}
                    </p>
                    <p className="text-xs text-primary-light-300 [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden sm:text-[13px]">
                      {displayRowSubtitle}
                    </p>
                  </div>
                  {useServiceDetailLayout ? (
                    <div className="flex items-center gap-2 py-2">
                      {shouldShowRailBadge && (
                        <span
                          className={`rs-detail-row-badge ${
                            isOpen
                              ? 'rs-detail-row-badge--open'
                              : row.hasAttention
                                ? 'rs-detail-row-badge--attention'
                                : 'rs-detail-row-badge--idle'
                          }`}
                        >
                          {railLabel}
                        </span>
                      )}
                      {!detailDesktopAllSectionsVisible && (
                        <ChevronRightIcon
                          className={`h-5 w-5 flex-shrink-0 transition-transform ${
                            isOpen
                              ? 'rotate-90 text-ratuj-300'
                              : 'text-primary-light-300 group-hover:text-ratuj-300'
                          }`}
                        />
                      )}
                    </div>
                  ) : (
                    <div
                      className={`${railContainerClass} ${
                        isOpen
                          ? 'text-ratuj-200'
                          : 'text-primary-light-400 group-hover:text-ratuj-200'
                      }`}
                    >
                      <span
                        className={`rounded-full border px-1.5 py-0.5 text-[10px] normal-case tracking-normal ${
                          shouldShowRailBadge
                            ? 'border-ratuj-300/30 bg-ratuj-300/8 text-ratuj-200'
                            : 'hidden border-primary-dark-300/80 text-primary-light-400 sm:inline-flex'
                        }`}
                      >
                        {railLabel}
                      </span>
                      <ChevronRightIcon
                        className={`h-5 w-5 flex-shrink-0 transition-transform ${
                          isOpen
                            ? 'rotate-90 text-ratuj-300'
                            : 'text-primary-light-300 group-hover:text-ratuj-300'
                        }`}
                      />
                    </div>
                  )}
                </button>

                {isOpen && (
                  <div
                    id={`order-row-${requestIdentity}-${rowId}`}
                    data-testid={`order-row-detail-${rowId}`}
                    data-order-panel-scroll-owner="true"
                    className={`${
                      useServiceDetailLayout
                        ? `${
                            detailDesktopAllSectionsVisible
                              ? 'rs-detail-row-panel rs-detail-row-panel--static'
                              : 'rs-detail-row-panel'
                          } ${
                            isCommunicationWorkspaceRow
                              ? 'flex min-h-0 flex-col overflow-hidden [touch-action:pan-y]'
                              : ''
                          }`
                        : `${rowDetailPaddingClass} ${
                            isCommunicationWorkspaceRow
                              ? 'flex min-h-0 flex-col overflow-hidden [touch-action:pan-y]'
                              : ''
                          }`
                    }`}
                    style={rowDetailStyle}
                  >
                    {rowId === 'description' && (
                      <div className="space-y-2.5 sm:space-y-3">
                        {renderActionsBlock('description')}

                        {useServiceDetailLayout ? (
                          <DetailInfoList
                            rows={[
                              {
                                label: 'Typ sprzętu',
                                value: identification.device.label,
                              },
                              {
                                label: 'Marka i model',
                                value: identification.device.brandModelLabel,
                              },
                              {
                                label: 'Utworzono',
                                value: formatDate(request.createdAt),
                              },
                              {
                                label: 'Preferowany termin',
                                value: preferredDateLabel,
                              },
                            ]}
                          />
                        ) : (
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <WorkDetailBlock
                              label="Typ sprzętu"
                              value={identification.device.label}
                              simpleMode={detailSimpleMode}
                            />
                            <WorkDetailBlock
                              label="Marka i model"
                              value={identification.device.brandModelLabel}
                              simpleMode={detailSimpleMode}
                            />
                            <div className="sm:col-span-2">
                              <WorkDetailBlock
                                label="Twoja akcja"
                                value={currentStepLine}
                                simpleMode={detailSimpleMode}
                              />
                            </div>
                          </div>
                        )}

                        <div className={calmSectionClass}>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
                            Problem od klienta
                          </p>
                          <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-primary-light-100">
                            {issueDescription}
                          </p>
                        </div>

                        <FulfillmentDetailBlock
                          fulfillment={fulfillment}
                          address={fulfillmentAddress}
                          context={fulfillmentModeContext}
                          paymentLabel={getRepairRequestFulfillmentPaymentLabel(
                            fulfillment.payment
                          )}
                          pricingHintLabel={fulfillmentPricingHintLabel}
                          instructions={fulfillmentInstructions}
                          className={calmSectionClass}
                        />

                        <div className={calmSectionClass}>
                          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
                            Załączniki ({photos.length})
                          </p>
                          {photos.length > 0 ? (
                            <div className="flex flex-wrap gap-2.5">
                              {photos.map((photoUrl, photoIndex) => (
                                <button
                                  key={`photo-${photoIndex}`}
                                  type="button"
                                  onClick={() => {
                                    setLightboxIndex(photoIndex);
                                    setLightboxOpen(true);
                                  }}
                                  className="overflow-hidden rounded focus:outline-none focus:ring-2 focus:ring-ratuj-300 focus:ring-offset-2 focus:ring-offset-primary-dark-100"
                                  aria-label={`Pokaż zdjęcie ${photoIndex + 1}`}
                                >
                                  <img
                                    src={photoUrl}
                                    alt={`Załącznik ${photoIndex + 1}`}
                                    className="h-16 w-16 rounded object-cover transition-opacity hover:opacity-80 sm:h-20 sm:w-20"
                                  />
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-primary-light-400">Brak zdjęć od klienta.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {rowId === 'communication' && (
                      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/12">
                        <div className="shrink-0 px-3 pb-2 pt-2.5">
                          <p className="text-sm leading-snug text-primary-light-300">
                            {runtime.rows.communication.hint}
                          </p>
                        </div>
                        {receiverId ? (
                          <div
                            className="flex min-h-0 flex-1 flex-col overflow-hidden [touch-action:pan-y]"
                            data-testid="order-communication-chat-shell"
                          >
                            <ChatWindow
                              repairRequestId={requestId}
                              receiverId={receiverId}
                              onThreadReadyChange={setIsMessageThreadReady}
                              onMessageCountChange={setMessageCount}
                              onComposerFocusChange={handleCommunicationComposerFocusChange}
                              compactEmptyState={showCompactChatEmptyState}
                              quickReplies={quickReplies}
                              initialBottomAnchorToken={
                                variant === 'detail' && isMobileViewport
                                  ? communicationEntryToken
                                  : undefined
                              }
                              initialBottomAnchorReady={
                                variant === 'detail' && isMobileViewport
                                  ? isCommunicationWorkspaceReady
                                  : true
                              }
                            />
                          </div>
                        ) : (
                          <div className="px-3 pb-3">
                            <Alert variant="info">
                              Brak danych drugiej strony konwersacji. Komunikacja będzie dostępna po
                              przypisaniu uczestnika.
                            </Alert>
                          </div>
                        )}
                      </div>
                    )}

                    {rowId === 'settlement' && (
                      <div className="space-y-2.5 sm:space-y-3">
                        {renderActionsBlock('settlement')}

                        {useServiceDetailLayout ? (
                          <DetailInfoList
                            rows={[
                              {
                                label: isWorkflowV15 ? 'Wycena wstępna' : 'Wycena',
                                value: isWorkflowV15
                                  ? preliminaryEstimateSummary
                                  : formatCurrency(request.quotedPrice),
                                emphasize: true,
                              },
                              {
                                label: 'Etap',
                                value: getSettlementStageLabel(request.status),
                              },
                              {
                                label: 'Aktualizacja',
                                value: formatDateTime(request.updatedAt || request.createdAt),
                              },
                            ]}
                          />
                        ) : (
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <WorkDetailBlock
                              label={isWorkflowV15 ? 'Wycena wstępna' : 'Wycena'}
                              value={
                                isWorkflowV15
                                  ? preliminaryEstimateSummary
                                  : formatCurrency(request.quotedPrice)
                              }
                              emphasize
                              simpleMode={detailSimpleMode}
                            />
                            <WorkDetailBlock
                              label="Etap zamknięcia"
                              value={getSettlementStageLabel(request.status)}
                              simpleMode={detailSimpleMode}
                            />
                            <WorkDetailBlock
                              label="Status sprawy"
                              value={runtime.header.statusLabel}
                              simpleMode={detailSimpleMode}
                            />
                            <WorkDetailBlock
                              label="Aktualizacja"
                              value={formatDateTime(request.updatedAt || request.createdAt)}
                              simpleMode={detailSimpleMode}
                            />
                          </div>
                        )}

                        {detailMode && (
                          <div className={`${calmSectionClass} space-y-2`}>
                            <p className="text-[11px] uppercase tracking-wide text-primary-light-400">
                              Przebieg sprawy
                            </p>
                            <RepairRequestProcessStepper
                              status={request.status}
                              compactOnMobile
                              defaultExpanded={false}
                            />
                          </div>
                        )}

                        <div className={calmSectionClass}>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
                            {isWorkflowV15 ? 'Komentarz do wyceny wstępnej' : 'Komentarz'}
                          </p>
                          <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-primary-light-100">
                            {isWorkflowV15
                              ? preliminaryEstimate?.comment || 'Brak komentarza roboczego.'
                              : request.quoteDescription || 'Brak komentarza roboczego.'}
                          </p>
                        </div>

                        {shouldRenderReviewBlock && (
                          <div className="space-y-3 border-t border-primary-dark-300/70 pt-3">
                            <h3 className="text-base font-semibold text-primary-light-100">
                              Opinia po realizacji
                            </h3>

                            {reviewSubmittedSuccessfully && (
                              <Alert variant="success">
                                Dziękujemy, Twoja opinia została zapisana.
                              </Alert>
                            )}

                            {existingReview ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="flex items-center gap-2">
                                    <StarRatingDisplay rating={existingReview.rating} size={20} />
                                    <span className="font-medium text-primary-light-100">
                                      {existingReview.rating.toFixed(1)}/5
                                    </span>
                                  </div>
                                  <Badge variant="success" size="sm">
                                    Zweryfikowane zlecenie
                                  </Badge>
                                </div>
                                <p
                                  className="rs-detail-note rs-detail-note--success text-primary-light-300"
                                  data-testid="client-review-comment"
                                >
                                  {existingReview.comment}
                                </p>
                                <p className="text-xs text-primary-light-400">
                                  Dodano: {formatReviewDate(existingReview.createdAt)}
                                </p>
                              </div>
                            ) : canSubmitReview ? (
                              <ReviewForm
                                repairRequestId={requestId}
                                serviceProviderId={readModel.assignment.assignedServiceUserId || ''}
                                onReviewAdded={onReviewAdded}
                              />
                            ) : (
                              <Alert variant="info">
                                Opinia dla tego zlecenia już została dodana.
                              </Alert>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {showOperationalTools &&
          (operationalIndexState.isAssigned ? (
            <RepairRequestPrintHub request={request} />
          ) : (
            <section
              data-testid="service-detail-identification"
              className="rs-detail-info-block text-sm text-primary-light-300"
            >
              <p className="font-medium text-primary-light-100">Wydruki i identyfikacja</p>
              <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
                Dostępne po rozpoczęciu realizacji.
              </p>
            </section>
          ))}
      </div>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={photos.map((url) => ({ src: url }))}
        index={lightboxIndex}
      />
      {renderPendingConfirmationSheet()}
    </Card>
  );
};

const DetailInfoList: React.FC<{
  rows: Array<{
    label: string;
    value?: string | React.ReactNode;
    emphasize?: boolean;
  }>;
}> = ({ rows }) => {
  const visibleRows = rows.filter(
    (row) => row.value !== null && row.value !== undefined && row.value !== ''
  );
  if (visibleRows.length === 0) {
    return null;
  }

  return (
    <div className="rs-detail-info-list">
      {visibleRows.map((row, index) => (
        <div key={`${row.label}-${index}`} className="rs-detail-info-row">
          <p className="text-[10px] uppercase tracking-wide text-primary-light-400">{row.label}</p>
          <div
            className={`mt-1 text-sm leading-snug ${
              row.emphasize ? 'font-semibold text-ratuj-200' : 'text-primary-light-100'
            }`}
            style={{ wordBreak: 'break-word' }}
          >
            {row.value}
          </div>
        </div>
      ))}
    </div>
  );
};

const WorkshopSection: React.FC<{
  id: WorkshopSectionId;
  title: string;
  subtitle?: string | null;
  active?: boolean;
  emphasis?: boolean;
  children: React.ReactNode;
}> = ({ id, title, subtitle, active = false, emphasis = false, children }) => (
  <section
    id={`workshop-section-${id}`}
    data-testid={`workshop-section-${id}`}
    className={`rs-detail-section space-y-2.5 ${
      emphasis ? 'rs-detail-section--emphasis' : active ? 'rs-detail-section--active' : ''
    }`}
  >
    <div className="min-w-0 space-y-0.5">
      <h3 className="break-words text-sm font-semibold uppercase tracking-wide text-primary-light-100">
        {title}
      </h3>
      {subtitle && (
        <p className="truncate text-xs leading-snug text-primary-light-400">{subtitle}</p>
      )}
    </div>
    <div className="space-y-2.5">{children}</div>
  </section>
);

const WorkshopSubscreenToolbar: React.FC<{
  title: string;
  meta: string;
  exitLabel: string;
  onPanel: () => void;
  onExit?: () => void;
  rightAction?: React.ReactNode;
}> = ({ title, meta, exitLabel, onPanel, onExit, rightAction }) => (
  <div className="rs-detail-mini-toolbar" data-testid="workshop-mini-toolbar">
    <button
      type="button"
      onClick={onPanel}
      className="rs-detail-toolbar-action rs-detail-toolbar-action--panel focus-outline"
      data-testid="workshop-back-to-overview"
    >
      <ArrowLeftIcon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">PANEL</span>
    </button>
    <div className="min-w-0 text-center">
      <p
        className="truncate text-xs font-semibold uppercase tracking-[0.06em] text-primary-light-100"
        data-testid="workshop-active-title"
      >
        {title}
      </p>
      <p className="mt-0.5 truncate text-[11px] leading-snug text-primary-light-400">{meta}</p>
    </div>
    {rightAction || onExit ? (
      <div className="flex min-w-0 items-center justify-end gap-1.5">
        {rightAction}
        {onExit ? (
          <button
            type="button"
            onClick={onExit}
            className="rs-detail-toolbar-action rs-detail-toolbar-action--exit focus-outline"
            data-testid="workshop-exit-detail"
          >
            <span className="block truncate">{exitLabel}</span>
          </button>
        ) : null}
      </div>
    ) : (
      <span aria-hidden="true" />
    )}
  </div>
);

const WorkshopQuickTool: React.FC<{
  label: string;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, disabled = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rs-detail-quick-tool focus-outline"
  >
    <span className="block truncate">{label}</span>
  </button>
);

const WorkshopModuleLauncher: React.FC<{
  title: string;
  line: string;
  active?: boolean;
  onClick: () => void;
}> = ({ title, line, active = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rs-detail-module-launcher focus-outline ${
      active ? 'rs-detail-module-launcher--active' : ''
    }`}
    aria-pressed={active}
  >
    <span className="min-w-0 flex-1 md:w-full">
      <span className="block truncate text-sm font-semibold text-primary-light-100">{title}</span>
      <span className="mt-0.5 block overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-snug text-primary-light-400">
        {line}
      </span>
    </span>
    <ChevronRightIcon className="rs-detail-module-launcher-icon" />
  </button>
);

const WorkshopRepairTimeline: React.FC<{
  status: RepairStatus;
  nextStepLabel?: string | null;
  stageLabel?: string | null;
}> = ({ status, nextStepLabel, stageLabel }) => {
  const steps = getRepairRequestProcessSteps();
  const progress = getRepairRequestProcessProgress(status);
  const currentStep = steps[Math.min(progress.currentStepIndex, steps.length - 1)];
  const currentStepLabel = stageLabel || currentStep?.label || 'Etap';

  return (
    <div className="rs-detail-timeline" data-testid="workshop-repair-timeline-card">
      <p className="text-sm font-semibold text-primary-light-100">
        {`Krok ${progress.currentStepIndex + 1}/${progress.totalStepCount} · ${currentStepLabel}`}
      </p>
      {nextStepLabel && (
        <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
          Następny krok: {nextStepLabel}
        </p>
      )}
      <div className="mt-3 space-y-2" data-testid="workshop-repair-timeline">
        {steps.map((step, index) => {
          const isCompleted = index < progress.completedStepCount;
          const isCurrent = !isCompleted && index === progress.currentStepIndex;
          const marker = isCompleted ? '✓' : isCurrent ? '●' : '○';
          return (
            <div key={step.id} className="grid grid-cols-[1.5rem_1fr] items-start gap-2">
              <span
                className={`mt-0.5 text-sm font-semibold ${
                  isCompleted
                    ? 'text-ratuj-200'
                    : isCurrent
                      ? 'text-primary-light-100'
                      : 'text-primary-light-500'
                }`}
              >
                {marker}
              </span>
              <div className="min-w-0">
                <p
                  className={`text-sm leading-snug ${
                    isCurrent || isCompleted
                      ? 'font-semibold text-primary-light-100'
                      : 'text-primary-light-400'
                  }`}
                >
                  {isCurrent ? currentStepLabel : step.label}
                </p>
                {isCurrent && (
                  <p className="mt-0.5 text-xs leading-relaxed text-primary-light-400">
                    {step.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WorkshopInfoGroup: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className="space-y-1.5">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
      {title}
    </p>
    {children}
  </section>
);

const SettlementSummaryItem: React.FC<{
  label: string;
  value: string;
  strong?: boolean;
}> = ({ label, value, strong = false }) => (
  <div className="rs-detail-summary-item">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
      {label}
    </p>
    <p
      className={`mt-1 break-words leading-snug ${
        strong
          ? 'text-xl font-semibold text-ratuj-100'
          : 'text-sm font-semibold text-primary-light-100'
      }`}
    >
      {value}
    </p>
  </div>
);

const ClientModulePanel: React.FC<{
  title: string;
  subtitle: string;
  dataTestId: string;
  children: React.ReactNode;
}> = ({ title, subtitle, dataTestId, children }) => (
  <section className="rs-detail-client-panel space-y-3" data-testid={dataTestId}>
    <div className="space-y-1">
      <h3 className="text-sm font-semibold text-primary-light-100 sm:text-[15px]">{title}</h3>
      <p className="text-xs leading-relaxed text-primary-light-300">{subtitle}</p>
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

const ClientModuleLauncher: React.FC<{
  title: string;
  line: string;
  onClick: () => void;
}> = ({ title, line, onClick }) => (
  <button type="button" onClick={onClick} className="rs-detail-module-launcher focus-outline">
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-semibold text-primary-light-100">{title}</span>
      <span className="mt-0.5 block overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-snug text-primary-light-400">
        {line}
      </span>
    </span>
    <ChevronRightIcon className="rs-detail-module-launcher-icon" />
  </button>
);

const ClientMutedNote: React.FC<{
  label: string;
  value: string;
}> = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-light-400">
      {label}
    </p>
    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-100">
      {value}
    </p>
  </div>
);

const WorkDetailBlock: React.FC<{
  label: string;
  value?: string | React.ReactNode;
  emphasize?: boolean;
  simpleMode?: boolean;
}> = ({ label, value, emphasize = false, simpleMode = false }) => {
  const shouldRenderValue = value !== null && value !== undefined && value !== '';
  if (!shouldRenderValue) return null;

  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${
        emphasize
          ? 'border-ratuj-300/35 bg-ratuj-300/10'
          : simpleMode
            ? 'border-primary-dark-300/30 bg-primary-dark-200/18'
            : 'border-primary-dark-300/65 bg-primary-dark-200/35'
      }`}
    >
      <p className="text-[10px] uppercase tracking-wide text-primary-light-400">{label}</p>
      <p
        className={`mt-1 text-sm leading-snug ${
          emphasize ? 'font-semibold text-ratuj-200' : 'text-primary-light-100'
        }`}
        style={{ wordBreak: 'break-word' }}
      >
        {value}
      </p>
    </div>
  );
};

const FulfillmentDetailBlock: React.FC<{
  fulfillment: ResolvedRepairRequestFulfillmentSnapshot;
  address: string | null;
  context: string | null;
  paymentLabel: string;
  pricingHintLabel: string | null;
  instructions: string | null;
  className: string;
}> = ({
  fulfillment,
  address,
  context,
  paymentLabel,
  pricingHintLabel,
  instructions,
  className,
}) => {
  if (fulfillment.state === 'missing') {
    return (
      <div className={className} data-testid="request-fulfillment-detail">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
          REALIZACJA
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-primary-light-300">
          Brak danych o sposobie realizacji dla tego starszego zgłoszenia.
        </p>
      </div>
    );
  }

  if (!fulfillment.selectedMode || !fulfillment.selectedModeLabel) {
    return (
      <div className={className} data-testid="request-fulfillment-detail">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
          REALIZACJA
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-primary-light-300">
          Dane sposobu realizacji są niepełne. Nie pokazujemy domyślnej metody bez zapisanego
          wyboru.
        </p>
      </div>
    );
  }

  const addressLabel =
    fulfillment.selectedMode === 'servicePickup' ? 'Adres odbioru' : 'Adres wizyty';
  const noteLabel =
    fulfillment.selectedMode === 'servicePickup'
      ? 'Notatka do odbioru'
      : fulfillment.selectedMode === 'onsiteService'
        ? 'Notatka do dojazdu'
        : 'Notatka';
  const rows = [
    { label: 'Metoda', value: fulfillment.selectedModeLabel, emphasize: true },
    { label: 'Ustalenia', value: context || undefined },
    {
      label: addressLabel,
      value: fulfillment.requiresAddress ? address || 'Brak adresu w zgłoszeniu' : undefined,
    },
    { label: noteLabel, value: fulfillment.note },
    { label: 'Płatność', value: paymentLabel },
    { label: 'Koszt', value: pricingHintLabel || undefined },
    { label: 'Instrukcje serwisu', value: instructions || undefined },
    {
      label: 'Zgodność',
      value:
        fulfillment.source === 'legacyFallback'
          ? 'Metoda z domyślnej zgodności starszego profilu'
          : undefined,
    },
  ];

  return (
    <div className={className} data-testid="request-fulfillment-detail">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-light-400">
        REALIZACJA
      </p>
      <div className="mt-2">
        <DetailInfoList rows={rows} />
      </div>
    </div>
  );
};

export default RepairRequestOrderCard;
